import React, { useState, useEffect, useReducer, useCallback, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import './global.scss';
import { AppState, AppAction, Sheet, Statistics, Pouch, Group, POUCH_COLORS, GROUP_COLORS, HistoryActionType, SheetHistoryEntry } from './types';
import { initializeData, SheetStorage, SettingsStorage } from './utils/StorageUtils';
import ActiveSheetComponent, { ActiveSheetComponentRef } from './components/ActiveSheetComponent';
import { selectSheet, updateSheetTitle, deleteSheet } from './logic/SheetLogic';
import { calculateStatistics, getPouchActualValue, snapBalancingTotalDiff } from './logic/StatisticsLogic';
import { duplicatePouchRecord, updatePouches } from './logic/PouchLogic';
import { repairGroupPaths, syncGroupMembershipFromPouchPositions } from './logic/GroupLogic';
import { applyBridgePlacement } from './services/sheet-api/applyBridgePlacement';
import { applyHistoryNavigation } from './services/sheet-api/applyHistoryNavigation';
import { tidySheetLayout } from './services/sheet-api/tidySheetLayout';
import { getInitialSheets, createAndSaveNewSheet, createAndSaveApiSheet, createAndSaveInitializationSheet } from './utils/DefaultSheetUtils';
import { LayerAwareCounter } from './components/LayerAwareCounter';
import { LAYER_SLIDE_MS } from './constants/layerSlideAnimation';
import {
  buildSheetTotalLayerTooltipLines,
  buildStatsTotalLayerTooltipLines,
  computeSheetTotalForSidebar,
  resolveLayerNumericTooltipHtml,
  sheetHasMultipleLayers,
} from './utils/layerValueTooltip';
import HistoryComponent from './components/HistoryComponent';
import { HistoryLogic } from './utils/HistoryLogic';
import { UNDO_REDO_TRANSITION_MS } from './utils/documentAutoScroll';
import TableComponent from './components/TableComponent';
import { SheetLayerCalcTitleList } from './components/SheetLayerCalcTitleList';
import { shareGroupContent, isInMuteGroup, clearGroupLogicCache, clearGTPRelatedCache, clearGroupResizeCache, changeGroupColor, sheetContributingPouchesAreAllTime } from './logic/GroupLogic';
import { applyBalancingPouch, calculateSheetTotal, getPouchContributionForTotals, getTimePouchEffectiveForTotal } from './logic/BalancingPouchLogic';
import { syncBalancingPouchTimeMode } from './logic/balancingPouchTimeSync';
import { applySheetFromJson } from './services/sheet-api/applySheetFromJson';
import { readSheetForApi } from './services/sheet-api/readSheetForApi';
import { getBridgeClientInfo } from './services/sheet-api/bridgeClientInfo';
import { SheetApiHost } from './services/sheet-api/SheetApiHost';
import { startBridgeClient } from './services/sheet-api/BridgeClient';
import type {
  ActivateSheetResult,
  CreateSheetResult,
  CreateSnapshotResult,
  DeleteSheetResult,
  SheetApiError,
  WriteSheetResult,
} from './services/sheet-api/types';
import { LegendsComponent } from './components/LegendsComponent';
import { calculateColorTotal, calculateColorCount } from './logic/LegendsLogic';
import { useModifierKeyClasses } from './hooks/useModifierKeyClasses';
import { useAppDisplayVersion } from './hooks/useAppDisplayVersion';
import { setLayerSlideChromeLock, startGridOperationActiveSync } from './utils/gridOperationActive';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { formatTooltip, formatKeyDescLine } from './utils/tooltipFormatter';
import { GuideContext } from './contexts/GuideContext';
import { LayerSwitchProvider, LayerSwitchDirection } from './context/LayerSwitchContext';
import { PouchValueKeyboardProvider } from './context/PouchValueKeyboardContext';
import {
  setSheetActiveLayer,
  addSheetLayer,
  updateSheetLayerName,
  removeSheetLayer,
  resolveActiveLayerId,
  getSortedLayers,
  compareLayerOrder,
  pouchWithLayerValue,
  mirrorPouchesForLayer,
  setPouchLayerValue,
  applyPouchScalarForSheet,
  getLayerValue,
  getBalancingTargetForLayer,
  syncBalancingPouchToActiveLayer,
  syncBalancingTargetTotalToActiveLayer,
  syncIncomingPouchScalarsToActiveLayer,
  syncSheetPouchesToActiveLayer,
  withBalancingTargetForLayer,
  calculateSheetTotalForLayer,
  ensureBalancingTargetForLayer,
  migrateSheetLayers,
  getPouchLayerRoundUnit,
  getPouchLayerRoundUnitWhenLastExpression,
  getPouchLayerIsTime,
  getPouchLayerMultiplier,
  pouchUsesLayerValues,
  syncPouchValueMirror,
} from './logic/LayerLogic';
import { isEditableElement } from './utils/pouchColorHotkeys';
import { parseImportLaunchUrl, applyImportPageTitle, buildServerImportUrl, clearImportLaunchUrl, clearImportLaunchDeclined, clearImportPromptPending, clearPendingImportLaunchUrl, isImportLaunchDeclined, isImportPromptPending, isStoreHandoffBlockingWebImport, markImportLaunchDeclined, markImportPromptPending, rememberPendingImportLaunchUrl } from './utils/importLaunchUrl';
import { fetchImportFile } from './utils/fetchImportFile';
import { getDeleteTempFileUrl, getPouchesApiUrl } from './utils/pouchesApiUrl';
import { detectTouchDevice, isWindowsPwa } from './utils/detectTouchDevice';
import { pickPouchesFile } from './utils/pouchesFilePicker';
import { shareOrDownloadBlob } from './utils/shareDownloadFile';
import { clearLayerRemoveConfirming, handleKeyboardLayerRemove } from './utils/layerRemoveButton';
import { clearConfirmingDeleteButtons } from './utils/confirmingDeleteButton';
import {
  isLayerAddKey,
  isLayerPrevNextKey,
  isLayerRemoveKey,
  isLayerShortcutChord,
  isLayerSwitchNumberKey,
} from './utils/layerKeyboardShortcuts';
import { logSheetPouchValues } from './utils/pouchLayerDebugLog';
import { clampRoundUnitToScaledCap } from './utils/pouchScaleAndRound';
import { canAddSheet, freeSheetsRemaining, isProEntitlement } from './logic/SheetLimitLogic';
import {
  canIssueWebRedeemCode,
  isStoreProEntitlement,
} from './services/entitlement/entitlementHelpers';
import { authorizeNextAddSheet, consumeAddSheetAuthorization } from './logic/sheetAddGate';
import { mergeSheetsForState } from './utils/mergeSheetsForState';
import { entitlementService } from './services/entitlement/EntitlementService';
import type { EntitlementStatus } from './services/entitlement/types';
import { PaywallModal } from './components/PaywallModal';
import { WebRedeemIssueModal } from './components/WebRedeemIssueModal';
import {
  ENGLISH_UI_LABELS,
  getBillingCopy,
  getHistoryStorageCopy,
  historyActionLabel,
  HISTORY_STORAGE_LOCALE,
  isPurchaseCancelledMessage,
  localizeEntitlementError,
} from './i18n';
import { useLocale } from './contexts/LocaleContext';
import { isBillingPaywallEnabled } from './services/entitlement/billingApi';
import {
  isNativeStorePurchasePlatform,
  PurchaseCancelledError,
} from './services/entitlement/platform/appStoreRouter';
import { isWindowsStorePurchasePlatform } from './services/entitlement/platform/windowsAppStore';
import {
  detectBillingPlatform,
  isIosInAppPurchasePlatform,
  isNativeBillingPaywallPlatform,
} from './services/entitlement/platform/detect';
import { prefetchIosStoreCatalog } from './services/entitlement/platform/appStore';
import { maybeRequestReviewOnLaunch, requestReviewFromMenu } from './services/appReview';

function logImportLaunch(message: string, detail?: unknown): void {
  if (process.env.NODE_ENV === 'development') {
    if (detail !== undefined) {
      console.log('[ImportLaunch]', message, detail);
    } else {
      console.log('[ImportLaunch]', message);
    }
  }
  const electron = (window as unknown as { electron?: { logImportLaunch?: (m: string, d?: unknown) => void } }).electron;
  electron?.logImportLaunch?.(message, detail);
}

// OS別修飾キー表記の取得関数
export const getModifierKeys = () => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  
  return {
    ctrlOption: isMac ? '⌥' : 'Ctrl',      // Mac: Option, Windows/Linux: Ctrl
    shift: isMac ? '⇧' : 'Shift',          // Mac: ⇧, Windows/Linux: Shift
    altControl: isMac ? '⌃' : 'Alt'        // Mac: Control, Windows/Linux: Alt
  };
};

const openExternal = (url: string) => {
  try {
    const cap = (window as any).Capacitor;
    if (cap?.isNativePlatform?.()) {
      const plugins = cap.Plugins;
      if (plugins?.Browser?.open) {
        plugins.Browser.open({ url });
        return;
      }
    }
  } catch {}
  window.open(url, '_blank');
};

const formatDetailedNumber = (value: number): string => {
  const r8 = Math.round(value * 1e8) / 1e8;
  const r9 = Math.round(value * 1e9) / 1e9;
  const hasMore = Math.abs(r9 - r8) > 0;
  const intPart = Math.floor(Math.abs(r8));
  const decPart = Math.abs(r8) - intPart;
  const sign = r8 < 0 ? '-' : '';
  let result = decPart > 0
    ? `${sign}${intPart.toLocaleString()}.${decPart.toFixed(8).replace(/0+$/, '').substring(2)}`
    : `${sign}${intPart.toLocaleString()}`;
  if (hasMore) result += '...';
  return result;
};

/** 統計・帳尻の「分」値を TOTAL/AVERAGE と同じ H:MM 表記に */
const formatSheetMinutesAsTime = (signedMinutes: number): string => {
  const totalMinutes = Math.round(Math.abs(signedMinutes));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  // -0.39 分のような値は丸めると 0:00 になるため、負号は出さない
  const sign = totalMinutes === 0 ? '' : (signedMinutes < 0 ? '-' : '');
  return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
};

/** 帳尻ツールチップ: 目標 ± 差分 = 実合計（時刻シートは H:MM） */
const formatBalancingEquationTooltip = (
  target: number,
  diff: number,
  actualTotal: number,
  allTime: boolean
): string => {
  if (Math.abs(diff) <= 1e-9) {
    if (allTime) {
      return `<b>${formatDetailedNumber(target)} (${formatSheetMinutesAsTime(target)})</b>`;
    }
    return `<b>${formatDetailedNumber(target)}</b>`;
  }
  if (allTime) {
    const sign = diff >= 0 ? '+ ' : '- ';
    // 時刻モード: 数値式と時刻式を並記
    return `<b>${formatDetailedNumber(target)} ${sign}${formatDetailedNumber(Math.abs(diff))} = ${formatDetailedNumber(actualTotal)} (${formatSheetMinutesAsTime(target)} ${sign}${formatSheetMinutesAsTime(Math.abs(diff))} = ${formatSheetMinutesAsTime(actualTotal)})</b>`;
  }
  const sign = diff >= 0 ? '+ ' : '- ';
  return `<b>${formatDetailedNumber(target)} ${sign}${formatDetailedNumber(Math.abs(diff))} = ${formatDetailedNumber(actualTotal)}</b>`;
};

const formatDetailedWithTimeTooltipHtml = (value: number): string => {
  // 本体が小数なし（イタリックなし）相当のときはツールチップも簡潔にする
  const isSimpleIntegerLike = Math.abs(value - Math.round(value)) <= 1e-6;
  const numeric = isSimpleIntegerLike
    ? `${value < 0 ? '-' : ''}${Math.abs(Math.round(value)).toLocaleString()}`
    : formatDetailedNumber(value);
  return `<b>${numeric} (${formatSheetMinutesAsTime(value)})</b>`;
};

// 本体の数値表示（誤差ラベルなど）は小数点以下2桁で統一
const formatBodyNumber2dp = (value: number): string => {
  const rounded = Math.round(value * 100) / 100;
  return rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

/** 帳尻パウチ更新後: 計算結果と stateRef を突き合わせ、削除で ref が古い ID のままになる問題と BP トグルの競合に対応 */
function reconcileBalancingAfterSheetUpdate(
  computedPouchId: string | null,
  computedTarget: number | undefined,
  computedPreferredSign: 1 | -1 | undefined,
  updatedPouches: Pouch[],
  refSheet: Sheet | undefined
): { balancingPouchId: string | null; balancingTargetTotal: number | undefined; balancingPreferredSign: 1 | -1 | undefined } {
  const pouchOk = (id: string | null | undefined) =>
    !!id && updatedPouches.some(p => p.id === id && !(p as any)._isDeleting);

  let id: string | null = computedPouchId ?? null;
  let target = computedTarget;
  let preferredSign = computedPreferredSign;

  const refId = refSheet?.balancingPouchId ?? null;
  if (pouchOk(refId)) {
    if (
      refId !== id ||
      refSheet!.balancingTargetTotal !== target ||
      refSheet!.balancingPreferredSign !== preferredSign
    ) {
      id = refId;
      target = refSheet!.balancingTargetTotal;
      preferredSign = refSheet!.balancingPreferredSign;
    }
  }

  if (id && !pouchOk(id)) {
    return { balancingPouchId: null, balancingTargetTotal: undefined, balancingPreferredSign: undefined };
  }
  return { balancingPouchId: id, balancingTargetTotal: target, balancingPreferredSign: preferredSign };
}

function sanitizeBalancingPouchAgainstPouches(
  sheet: { balancingPouchId?: string | null; balancingTargetTotal?: number; balancingPreferredSign?: 1 | -1 } | undefined,
  pouches: Pouch[]
): { balancingPouchId: string | null; balancingTargetTotal: number | undefined; balancingPreferredSign: 1 | -1 | undefined } {
  if (!sheet) {
    return { balancingPouchId: null, balancingTargetTotal: undefined, balancingPreferredSign: undefined };
  }
  const id = sheet.balancingPouchId ?? null;
  if (!id) {
    return { balancingPouchId: null, balancingTargetTotal: undefined, balancingPreferredSign: undefined };
  }
  const ok = pouches.some(p => p.id === id && !(p as any)._isDeleting);
  if (!ok) {
    return { balancingPouchId: null, balancingTargetTotal: undefined, balancingPreferredSign: undefined };
  }
  return {
    balancingPouchId: id,
    balancingTargetTotal: sheet.balancingTargetTotal,
    balancingPreferredSign: sheet.balancingPreferredSign,
  };
}

/** 非同期後の latest とビルド時の BP を突き合わせ、stale な ID は捨てつつ新しいトグルは維持 */
function pickBalancingForPersist(
  latestSheet: Sheet | undefined,
  buildBalancing: {
    balancingPouchId: string | null;
    balancingTargetTotal: number | undefined;
    balancingPreferredSign: 1 | -1 | undefined;
  },
  pouches: Pouch[]
): { balancingPouchId: string | null; balancingTargetTotal: number | undefined; balancingPreferredSign: 1 | -1 | undefined } {
  const fromLatest = sanitizeBalancingPouchAgainstPouches(latestSheet, pouches);
  if (fromLatest.balancingPouchId) {
    return fromLatest;
  }
  return sanitizeBalancingPouchAgainstPouches(buildBalancing, pouches);
}

// PouchWithDrag型を定義（ActiveSheetComponentと同じ定義）
interface PouchWithDrag extends Pouch {
  _isDragging?: boolean;
  _isMoving?: boolean; // パウチの移動フラグを追加
}

// 初期状態
export const initialState: AppState = {
  sheets: [],
  activeSheetId: '',
  settings: {
    buttonsVisible: true,
    posinegaVisible: true,
    defaultValue: 0,
    autoHideMenu: false,
    isLeftSidebarOpen: window.innerWidth > 768,
    isRightSidebarOpen: window.innerWidth > 768
  },
  isMobile: window.innerWidth <= 768,
  isLeftSidebarOpen: true,
  isRightSidebarOpen: false,
  isRightSidebarFullyClosed: true, // 初期状態では完全に閉じている
  isTouchDevice: detectTouchDevice()

};

// Reducer関数
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'INIT_APP':
      return {
        ...state,
        sheets: action.payload.sheets,
        settings: action.payload.settings
      };
      
    case 'SET_ACTIVE_SHEET':
      const newActiveSheet = state.sheets.find(s => s.id === action.payload);
      return {
        ...state,
        activeSheetId: action.payload
      };
      
    case 'ADD_SHEET':
      if (!consumeAddSheetAuthorization()) {
        return state;
      }
      return {
        ...state,
        sheets: [action.payload, ...state.sheets]
      };
      
      case 'UPDATE_SHEET':
        const updatedSheets = state.sheets.map(sheet => 
          sheet.id === action.payload.id ? action.payload : sheet
        );
        
        return {
          ...state,
          sheets: updatedSheets
        };
      
    case 'DELETE_SHEET':
      return {
        ...state,
        sheets: state.sheets.filter(sheet => sheet.id !== action.payload),
        activeSheetId: state.activeSheetId === action.payload 
          ? (state.sheets.length > 1 
            ? state.sheets.find(s => s.id !== action.payload)?.id || '' 
            : '')
          : state.activeSheetId
      };
      
    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: action.payload
      };
      
    case 'TOGGLE_MENU':
      return {
        ...state,
        isLeftSidebarOpen: action.payload !== undefined ? action.payload : !state.isLeftSidebarOpen,
        isRightSidebarOpen: state.isMobile ? false : state.isRightSidebarOpen,
        settings: {
          ...state.settings,
          isLeftSidebarOpen: action.payload !== undefined ? action.payload : !state.isLeftSidebarOpen
        }
      };
      
    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        isRightSidebarOpen: action.payload !== undefined ? action.payload : !state.isRightSidebarOpen,
        isLeftSidebarOpen: state.isMobile ? false : state.isLeftSidebarOpen,
        isRightSidebarFullyClosed: false, // サイドバーが開かれた場合は即座にfalse
        settings: {
          ...state.settings,
          isRightSidebarOpen: action.payload !== undefined ? action.payload : !state.isRightSidebarOpen
        }
      };
      
    case 'SET_RIGHT_SIDEBAR_FULLY_CLOSED':
      return {
        ...state,
        isRightSidebarFullyClosed: action.payload
      };
    
    case 'SET_MOBILE_STATE':
      return {
        ...state,
        isMobile: action.payload
      };
      
    case 'SET_ACTIVE_SHEET_POUCHES':
      return {
        ...state,
        sheets: state.sheets.map(sheet => 
          sheet.id === state.activeSheetId ? { ...sheet, pouches: action.payload } : sheet
        )
      };
      
    case 'SET_ACTIVE_SHEET_GROUPS':
      return {
        ...state,
        sheets: state.sheets.map(sheet => 
          sheet.id === state.activeSheetId ? { ...sheet, groups: action.payload } : sheet
        )
      };
      
    default:
      return state;
  }
};

// デバウンス用のカスタムフック
const useDebounce = (callback: Function, delay: number) => {
  const timeoutRef = useRef<number | null>(null);

  return useCallback((...args: any[]) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      callback(...args);
      timeoutRef.current = null;
    }, delay);
  }, [callback, delay]);
};

/** Bridge write/tidy: トランジションは非同期。HTTP 応答はアニメ完了を待たない。 */
function scheduleBridgeActiveSheetVisualUpdate(
  activeSheet: ActiveSheetComponentRef | null | undefined,
  existingPouches: Pouch[],
  existingGroups: Group[],
  mergedPouches: Pouch[],
  mergedGroups: Group[],
  finalPouches: Pouch[],
  finalGroups: Group[],
): boolean {
  if (!activeSheet) return false;
  document.documentElement.classList.add('undoredo');
  const { pouches: animatedPouches, groups: animatedGroups } =
    HistoryLogic.applyBridgeWriteAnimationFlags(
      existingPouches,
      mergedPouches,
      existingGroups,
      mergedGroups,
    );
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      activeSheet.setPouches(animatedPouches);
      activeSheet.setGroups(animatedGroups);
    });
  });
  window.setTimeout(() => {
    document.documentElement.classList.remove('undoredo');
    activeSheet.setPouches(finalPouches);
    activeSheet.setGroups(finalGroups);
  }, UNDO_REDO_TRANSITION_MS);
  return true;
}

function postSheetMutatedBroadcast(
  sheetId: string,
  revision: number | undefined,
  skipMutationId: string,
): void {
  try {
    const channel = new BroadcastChannel('pouches-refresh-data');
    channel.postMessage({
      type: 'SHEET_MUTATED',
      sheetId,
      revision,
      skipMutationId,
    });
    channel.close();
  } catch {
    // BroadcastChannel unsupported
  }
}

function App() {
  const { appCopy: t, tooltipCopy: tt, locale } = useLocale();
  const appDisplayVersion = useAppDisplayVersion();

  // 修飾キーの状態を追跡してHTMLにクラスを付ける
  useModifierKeyClasses();
  
  // アプリのメインReducer
  const [state, dispatch] = useReducer(appReducer, initialState);
  
  // 合計表示の展開状態を管理
  const [isTotalExpanded, setIsTotalExpanded] = useState(false);
  
  // グリッドスナップの有効/無効を管理（デフォルトはオン、localStorage永続化）
  const [isSnapEnabled, setIsSnapEnabled] = useState(() => {
    const saved = localStorage.getItem('pouches-grid-enabled');
    return saved !== null ? saved === 'true' : true;
  });

  // ツールチップガイドの有効/無効を管理（デフォルトはオフ、localStorage永続化）
  const [isGuideEnabled, setIsGuideEnabled] = useState(() => {
    const saved = localStorage.getItem('pouches-guide-enabled');
    const value = saved !== null ? saved === 'true' : false;
    (window as any).__isGuideEnabled = value;
    return value;
  });
  
  // スナップ状態をグローバルに同期し、グリッド表示も制御
  useEffect(() => {
    window.__isSnapEnabled = isSnapEnabled;
    localStorage.setItem('pouches-grid-enabled', String(isSnapEnabled));
    if (isSnapEnabled) {
      document.body.classList.remove('grid-disabled');
    } else {
      document.body.classList.add('grid-disabled');
    }
  }, [isSnapEnabled]);

  // 操作中表示: global.scss の --sheet-chrome-* / --grid-operation-active（gridOperationActive.ts）
  useEffect(() => startGridOperationActiveSync(), []);

  // ガイド状態をlocalStorageに永続化
  useEffect(() => {
    localStorage.setItem('pouches-guide-enabled', String(isGuideEnabled));
  }, [isGuideEnabled]);
  
  // シート切替フラグをstateとrefの両方で管理
  const [isSheetSwitching, setIsSheetSwitching] = useState(false);
  const isSheetSwitchingRef = useRef(false);
  const prevActiveSheetIdForSwitchRef = useRef<string | null>(null);

  const beginSheetSwitch = useCallback((nextSheetId: string) => {
    if (nextSheetId !== state.activeSheetId && state.activeSheetId) {
      isSheetSwitchingRef.current = true;
      setIsSheetSwitching(true);
    }
  }, [state.activeSheetId]);
  const initialPouchLogDoneRef = useRef(false);
  const switchTimerRef = useRef<number | null>(null);
  
  // グループ更新用のデバウンスref
  const groupUpdateTimeoutRef = useRef<number | null>(null);
  const pendingGroupUpdateRef = useRef<Group[] | null>(null);
  
  // パウチ更新用のデバウンスref
  const pouchUpdateTimeoutRef = useRef<number | null>(null);
  const pendingPouchUpdateRef = useRef<Pouch[] | null>(null);
  /** デバウンス保存待ちのシート（シート切替・インポートでタイマー破棄時に永続化する） */
  const pendingDebouncedSheetRef = useRef<Sheet | null>(null);
  /** 遅延保存の世代。古い save が新しいデータを上書きしない */
  const pouchSaveGenerationRef = useRef(0);
  const pouchSaveInFlightRef = useRef<Promise<void> | null>(null);
  
  // handleUpdateActiveSheetの並行実行を防ぐためのフラグ
  const isUpdatingActiveSheetRef = useRef<boolean>(false);
  
  // 最新のstateを常に保持するref（クロージャ問題を回避）
  const stateRef = useRef(state);
  const activeSheetFilePathRef = useRef<Map<string, string>>(new Map());
  
  // 履歴作成処理中のフラグとタイマー
  const isInHistoryActionRef = useRef<boolean>(false);
  const historyActionTimerRef = useRef<number | null>(null);
  
  // リロード時アニメーションフラグを追加
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [entitlementStatus, setEntitlementStatus] = useState<EntitlementStatus>(() =>
    entitlementService.getStatus()
  );
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallBusy, setPaywallBusy] = useState(false);
  const [paywallError, setPaywallError] = useState<string | undefined>();
  const [paywallRedeemCodeInput, setPaywallRedeemCodeInput] = useState('');
  const [webRedeemIssueOpen, setWebRedeemIssueOpen] = useState(false);
  const [billingPaywallEnabled] = useState(true);

  // initApp 完了前にディープリンクインポートすると INIT_APP で上書きされるため待機用
  const appInitReadyRef = useRef<{ promise: Promise<void>; resolve: () => void } | null>(null);
  if (!appInitReadyRef.current) {
    let resolveReady: () => void = () => {};
    const promise = new Promise<void>((resolve) => {
      resolveReady = resolve;
    });
    appInitReadyRef.current = { promise, resolve: resolveReady };
  }
  const waitForAppInit = useCallback(async () => {
    await (appInitReadyRef.current?.promise ?? Promise.resolve());
  }, []);

  useEffect(() => {
    void waitForAppInit().then(() => maybeRequestReviewOnLaunch());
  }, [waitForAppInit]);

  /** initApp と URL 監視の二重起動・1秒ポーリングの連打を防ぐ */
  const urlParamImportRef = useRef({ search: '', inFlight: false });
  
  // ローディング状態を追跡してHTMLにクラスを付ける
  useEffect(() => {
    const htmlElement = document.documentElement;
    
    if (isInitialLoading || isSheetSwitching) {
      htmlElement.classList.add('loading');
    } else {
      htmlElement.classList.remove('loading');
    }
  }, [isInitialLoading, isSheetSwitching]);
  
  // シート間ドラッグ移動用の状態管理
  const [isDraggingToSheet, setIsDraggingToSheet] = useState(false);
  const [hoveredSheetId, setHoveredSheetId] = useState<string | null>(null);
  const sheetHoverTimerRef = useRef<number | null>(null);
  
  // シート間ドラッグ用のref
  const draggedGroupDataRef = useRef<{
    group: Group;
    pouches: Pouch[];
    containedGroups: Group[];
    finalPosition: { x: number, y: number };
    sourceSheetId: string;
  } | null>(null);
  const sourceSheetGroupRef = useRef<Group | null>(null);
  const draggedPouchDataRef = useRef<{
    pouch: Pouch;
    finalPosition: { x: number, y: number };
    sourceSheetId: string;
  } | null>(null);
  const sourceSheetPouchRef = useRef<Pouch | null>(null);

  // シート間ドラッグ状態をリセットする関数
  const resetSheetDragState = useCallback(() => {
    setIsDraggingToSheet(false);
    draggedGroupDataRef.current = null;
    draggedPouchDataRef.current = null;
    setHoveredSheetId(null);
    if (sheetHoverTimerRef.current) {
      clearTimeout(sheetHoverTimerRef.current);
      sheetHoverTimerRef.current = null;
    }
  }, []);
  const activeSheetComponentRef = useRef<any>(null);
  const globalInteractionRef = useRef<any>(null);
  /** 自タブの Bridge 反映で BroadcastChannel を二重適用しない */
  const bridgeSelfMutationIdRef = useRef<string | null>(null);
  
  // シート切り替え時のドラッグ状態を保持するためのref
  const preservedDragStateRef = useRef<{
    interaction: any;
    draggedFromOtherSheet: boolean;
    draggedGroupId?: string;
    tempPouchPosition?: { x: number; y: number };
    draggedGroupData?: any;
    draggedPouchData?: any;
  } | null>(null);

  // 元シートからグループを削除する関数
  const removeGroupFromSourceSheet = useCallback(async (groupId: string, sourceSheetId: string) => {
    try {
      // 元シートのデータを取得
      const sourceSheet = state.sheets.find(sheet => sheet.id === sourceSheetId);
      if (!sourceSheet) return;

      // ドラッグされた実際の要素情報を取得
      const draggedData = draggedGroupDataRef.current;
      if (!draggedData) {
        console.warn('ドラッグされた要素の情報が見つかりません');
        return;
      }

      // 実際に移動されたグループIDリストを作成（メイングループ + 内包グループ）
      const groupIdsToDelete = [draggedData.group.id];
      if (draggedData.containedGroups && draggedData.containedGroups.length > 0) {
        const containedGroupIds = draggedData.containedGroups.map(g => g.id);
        groupIdsToDelete.push(...containedGroupIds);
      }

      // 実際に移動されたパウチIDリストを作成
      const pouchIdsToDelete = draggedData.pouches ? draggedData.pouches.map(p => p.id) : [];

      // パウチを削除（実際に移動されたパウチのみ）
      const updatedPouches = sourceSheet.pouches.filter(pouch => !pouchIdsToDelete.includes(pouch.id));

      // グループを削除（実際に移動されたグループのみ）
      const updatedGroups = sourceSheet.groups.filter(g => !groupIdsToDelete.includes(g.id));

      // BP パウチがグループ移動に含まれている場合はクリア
      const bpCleared = sourceSheet.balancingPouchId ? pouchIdsToDelete.includes(sourceSheet.balancingPouchId) : false;

      // 元シートを更新
      const updatedSourceSheet: Sheet = {
        ...sourceSheet,
        pouches: updatedPouches,
        groups: updatedGroups,
        balancingPouchId: bpCleared ? null : sourceSheet.balancingPouchId,
        balancingTargetTotal: bpCleared ? undefined : sourceSheet.balancingTargetTotal,
        balancingPreferredSign: bpCleared ? undefined : sourceSheet.balancingPreferredSign,
        // 履歴エントリを追加（シート間移動でのグループ削除）
        history: [
          ...sourceSheet.history,
          {
            timestamp: new Date(),
            groups: updatedGroups,
            pouches: updatedPouches,
            type: HistoryActionType.DELETE_GROUP,
            description: getHistoryStorageCopy().dynamic.moveGroupTo(
              state.sheets.find(s => s.id === draggedData.sourceSheetId)?.title || t.default.unknownSheet
            ),
            targetId: groupId
          }
        ],
        historyIndex: sourceSheet.history.length // 新しい履歴エントリを指す
      };

      // 元シートを保存
      await SheetStorage.saveSheet(updatedSourceSheet);
      
      // DB保存の完了を確実に待つ
      await new Promise(resolve => setTimeout(resolve, 500));
      
      
      // 状態を更新
      dispatch({ type: 'UPDATE_SHEET', payload: updatedSourceSheet });
      
      // stateRefも同期的に更新
      stateRef.current = {
        ...stateRef.current,
        sheets: stateRef.current.sheets.map(s => 
          s.id === updatedSourceSheet.id ? updatedSourceSheet : s
        )
      };

    } catch (error) {
      console.error('元シートからのグループ削除に失敗しました:', error);
    }
  }, [state.sheets, dispatch, t.default.unknownSheet]);

  // 元シートからパウチを削除する関数
  const removePouchFromSourceSheet = useCallback(async (pouchId: string, sourceSheetId: string) => {
    
    try {
      // 元シートのデータを取得
      const sourceSheet = state.sheets.find(sheet => sheet.id === sourceSheetId);
      if (!sourceSheet) {
        return;
      }


      // 削除されるパウチのDOM要素を確実に削除
      const pouchElement = document.querySelector(`[data-pouch-id="${pouchId}"]`);
      if (pouchElement) {
        
        // イベントリスナーを無効化してから削除
        const clonedElement = pouchElement.cloneNode(true);
        pouchElement.parentNode?.replaceChild(clonedElement, pouchElement);
        
        // 少し遅延してから削除（イベントリスナーが完全に無効化されるまで待つ）
        setTimeout(() => {
          if (clonedElement.parentNode) {
            clonedElement.parentNode.removeChild(clonedElement);
          }
        }, 10);
      }

      // パウチを削除
      const updatedPouches = sourceSheet.pouches.filter(pouch => pouch.id !== pouchId);

      // 元シートを更新
      const updatedSourceSheet: Sheet = {
        ...sourceSheet,
        pouches: updatedPouches,
        // BP パウチが移動された場合はクリア
        balancingPouchId: sourceSheet.balancingPouchId === pouchId ? null : sourceSheet.balancingPouchId,
        balancingTargetTotal: sourceSheet.balancingPouchId === pouchId ? undefined : sourceSheet.balancingTargetTotal,
        balancingPreferredSign: sourceSheet.balancingPouchId === pouchId ? undefined : sourceSheet.balancingPreferredSign,
        // 履歴エントリを追加（シート間移動でのパウチ削除）
        history: [
          ...sourceSheet.history,
          {
            timestamp: new Date(),
            groups: sourceSheet.groups,
            pouches: updatedPouches,
            type: HistoryActionType.DELETE_POUCH,
            description: getHistoryStorageCopy().dynamic.movePouchTo(
              state.sheets.find(s => s.id === state.activeSheetId)?.title || t.default.unknownSheet
            ),
            targetId: pouchId
          }
        ],
        historyIndex: sourceSheet.history.length // 新しい履歴エントリを指す
      };


      // 元シートを保存
      await SheetStorage.saveSheet(updatedSourceSheet);
      
      // DB保存の完了を確実に待つ
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 状態を更新
      dispatch({ type: 'UPDATE_SHEET', payload: updatedSourceSheet });
      
      // stateRefも同期的に更新
      stateRef.current = {
        ...stateRef.current,
        sheets: stateRef.current.sheets.map(s => 
          s.id === updatedSourceSheet.id ? updatedSourceSheet : s
        )
      };
      
      // 元シートがアクティブシートの場合、強制的にリフレッシュしてイベントリスナーをクリーンアップ
      if (sourceSheetId === state.activeSheetId) {
        setTimeout(() => {
          // 強制的にシートを再レンダリング（同じシートIDで再設定）
          dispatch({ type: 'SET_ACTIVE_SHEET', payload: sourceSheetId });
        }, 100);
      }

    } catch (error) {
      console.error('元シートからのパウチ削除に失敗しました:', error);
    }
  }, [state.sheets, dispatch, state.activeSheetId, t.default.unknownSheet]);

  // デバウンスされたログ出力関数
  const debouncedLog = useDebounce(() => {
  }, 100);

  // タッチデバイス判定時の設定
  useEffect(() => {
    // HTML要素にタッチデバイス用のクラスを追加
    if (state.isTouchDevice) {
      document.documentElement.classList.add('touch-device');
    } else {
      document.documentElement.classList.remove('touch-device');
    }
    
    // HTML要素にモバイル用のクラスを追加
    if (state.isMobile) {
      document.documentElement.classList.add('mobile');
    } else {
      document.documentElement.classList.remove('mobile');
    }
  }, [state.isTouchDevice, state.isMobile]);

  // Capacitor ネイティブ（iPad 幅など）: シートタイトル上端を safe-area + 計算エリアに揃える
  useEffect(() => {
    const cap = (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    if (cap?.isNativePlatform?.()) {
      document.documentElement.classList.add('native-app');
    } else {
      document.documentElement.classList.remove('native-app');
    }
  }, []);
  
  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      // すべてのタイマーをクリア
      if (switchTimerRef.current !== null) {
        clearTimeout(switchTimerRef.current);
      }
      if (groupUpdateTimeoutRef.current !== null) {
        clearTimeout(groupUpdateTimeoutRef.current);
      }
      if (pouchUpdateTimeoutRef.current !== null) {
        clearTimeout(pouchUpdateTimeoutRef.current);
      }
      if (historyActionTimerRef.current !== null) {
        clearTimeout(historyActionTimerRef.current);
      }
    };
  }, []);
  
  // アクティブなシートを取得
  const activeSheet = useMemo(() => {
    return state.sheets.find(sheet => sheet.id === state.activeSheetId);
  }, [state.sheets, state.activeSheetId]);
  
  // pouchesの値×マルチの合計を計算（多層時はアクティブレイヤーの実効 multiplier）
  const pouchesTotal = useMemo(() => {
    if (!activeSheet?.pouches) return 0;
    const lid = resolveActiveLayerId(activeSheet);
    const mirrored = activeSheet.layers?.length
      ? mirrorPouchesForLayer(
          activeSheet.pouches,
          lid,
          activeSheet,
          activeSheet.groups ?? []
        )
      : activeSheet.pouches;
    return mirrored.reduce((sum, pouch) => sum + pouch.value * pouch.multiplier, 0);
  }, [activeSheet]);

  const activeLayerId = activeSheet ? resolveActiveLayerId(activeSheet) : 'default';

  const balancingTargetForDisplay = useMemo(
    () => (activeSheet ? getBalancingTargetForLayer(activeSheet, activeLayerId) : undefined),
    [activeSheet, activeLayerId]
  );

  /** 集計・フッター用（read-only mirror。multiplier を時間レイヤー値で上書きする） */
  const pouchesForActiveLayer = useMemo(() => {
    if (!activeSheet) return [];
    const lid = resolveActiveLayerId(activeSheet);
    return mirrorPouchesForLayer(
      activeSheet.pouches,
      lid,
      activeSheet,
      activeSheet.groups ?? []
    );
  }, [activeSheet]);

  /** ActiveSheetComponent 初期化用（canonical。mirror で multiplier を汚さない） */
  const pouchesForActiveSheet = useMemo(() => {
    if (!activeSheet?.pouches) return [];
    return activeSheet.layers?.length
      ? syncSheetPouchesToActiveLayer(activeSheet).pouches
      : activeSheet.pouches;
  }, [activeSheet]);

  const computedStats = useMemo((): Statistics => {
    if (!activeSheet) {
      return { count: 0, total: 0, average: 0, max: 0, min: 0 };
    }
    return calculateStatistics(
      pouchesForActiveLayer,
      activeSheet.groups ?? [],
      activeSheet.balancingPouchId,
      activeSheet
    );
  }, [pouchesForActiveLayer, activeSheet?.groups]);

  // リロード直後に1回だけパウチ状態をログ（dev）
  useEffect(() => {
    if (!activeSheet || initialPouchLogDoneRef.current) return;
    initialPouchLogDoneRef.current = true;
    const sheet =
      activeSheet.layers?.length ? syncSheetPouchesToActiveLayer(activeSheet) : activeSheet;
    logSheetPouchValues('load:reload', sheet);
  }, [activeSheet, computedStats.total]);

  const [layerSwitchDirection, setLayerSwitchDirection] = useState<LayerSwitchDirection>(0);
  const [layerSwitchNonce, setLayerSwitchNonce] = useState(0);
  const [layerSwitchFromLayerId, setLayerSwitchFromLayerId] = useState<string | null>(null);
  const layerSlideResetTimerRef = useRef<number | null>(null);

  const beginLayerSlide = useCallback((direction: LayerSwitchDirection) => {
    if (direction === 0) return;
    if (layerSlideResetTimerRef.current !== null) {
      window.clearTimeout(layerSlideResetTimerRef.current);
      layerSlideResetTimerRef.current = null;
    }
    setLayerSlideChromeLock(true);
    setLayerSwitchDirection(direction);
  }, []);

  const commitLayerSlide = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setLayerSwitchNonce(n => n + 1);
      });
    });
    if (layerSlideResetTimerRef.current !== null) {
      window.clearTimeout(layerSlideResetTimerRef.current);
    }
    layerSlideResetTimerRef.current = window.setTimeout(() => {
      setLayerSwitchDirection(0);
      setLayerSwitchFromLayerId(null);
      setLayerSlideChromeLock(false);
      layerSlideResetTimerRef.current = null;
    }, LAYER_SLIDE_MS * 2 + 10);
  }, []);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  /** フッター表示用（computedStats を useEffect で反映） */
  const [stats, setStats] = useState<Statistics>({
    count: 0,
    total: 0,
    average: 0,
    max: 0,
    min: 0,
  });

  useEffect(() => {
    setStats(computedStats);
  }, [computedStats]);

  const footerStatsShowsTime = useMemo(
    () =>
      activeSheet
        ? sheetContributingPouchesAreAllTime(
            pouchesForActiveLayer,
            activeSheet.groups ?? [],
            activeSheet.balancingPouchId
          )
        : false,
    [activeSheet, pouchesForActiveLayer]
  );
  const footerStatCounterDuration =
    isInitialLoading || isSheetSwitchingRef.current ? 600 : 200;
  const footerStatAnimateFromZero = isInitialLoading || isSheetSwitchingRef.current;

  const renderFooterStatCounter = useCallback(
    (statKey: string, value: number, tooltipHtml?: string) => (
      <span {...(tooltipHtml ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': tooltipHtml } : {})}>
        <LayerAwareCounter
          key={`${statKey}-${state.activeSheetId}`}
          sheet={activeSheet}
          value={Math.abs(value)}
          isNegative={value < 0}
          animateFromZero={footerStatAnimateFromZero}
          duration={footerStatCounterDuration}
          suppressTooltip
          displayAsTime={footerStatsShowsTime}
          timeDisplayTag="strong"
          showTimeConversion={false}
        />
      </span>
    ),
    [
      activeSheet,
      footerStatsShowsTime,
      footerStatCounterDuration,
      footerStatAnimateFromZero,
      state.activeSheetId,
    ]
  );

  const persistSheet = useCallback(async (sheet: Sheet) => {
    const synced = sheet.layers?.length ? syncSheetPouchesToActiveLayer(sheet) : sheet;
    dispatch({ type: 'UPDATE_SHEET', payload: synced });
    await SheetStorage.saveSheet(synced);
  }, []);

  const readSheetForBridge = useCallback((sheetId: string) => {
    const sheet = stateRef.current.sheets.find(s => s.id === sheetId);
    if (!sheet) {
      return {
        ok: false as const,
        code: 'SHEET_NOT_FOUND' as const,
        message: `Sheet not found: ${sheetId}`,
      };
    }
    return readSheetForApi(sheet);
  }, []);

  const getSheetApiContext = useCallback(() => {
    const { sheets, activeSheetId } = stateRef.current;
    const client = getBridgeClientInfo();
    return {
      activeSheetId,
      sheets: sheets.map(s => ({ id: s.id, title: s.title })),
      client: {
        kind: client.kind,
        origin: client.origin,
        apiVersion: client.apiVersion,
        capabilities: [...client.capabilities],
      },
    };
  }, []);

  const applySheetFromBridge = useCallback(async (
    sheetId: string,
    sheetJson: unknown,
    options?: { expectedRevision?: number; replaceSheet?: boolean },
  ): Promise<WriteSheetResult | SheetApiError> => {
    const existing = stateRef.current.sheets.find(s => s.id === sheetId);
    if (!existing) {
      return { ok: false, code: 'SHEET_NOT_FOUND', message: `Sheet not found: ${sheetId}` };
    }

    const parsed = applySheetFromJson({
      sheetId,
      sheetJson,
      currentRevision: existing.revision ?? 0,
      expectedRevision: options?.expectedRevision,
    });
    if (!parsed.ok) {
      return parsed;
    }

    const repairedGroups = repairGroupPaths(parsed.sheet.groups);
    let merged: Sheet = {
      ...existing,
      ...parsed.sheet,
      groups: repairedGroups,
      revision: parsed.revision,
      createdAt: parsed.sheet.createdAt ?? existing.createdAt,
    };

    if (!options?.replaceSheet) {
      const placement = applyBridgePlacement(
        existing.groups,
        existing.pouches,
        merged.pouches,
        merged.groups,
        parsed.autoPlacePouchIds,
      );
      merged = {
        ...merged,
        pouches: placement.pouches,
        groups: placement.groups,
      };
    }

    const balancingPouchId = merged.balancingPouchId ?? null;
    if (balancingPouchId) {
      const bpLayerId = resolveActiveLayerId(merged);
      const balancingTargetTotal = getBalancingTargetForLayer(merged, bpLayerId);
      if (balancingTargetTotal !== undefined) {
        let pouches = syncBalancingPouchTimeMode(
          merged.pouches,
          merged.groups,
          balancingPouchId,
          merged,
        );
        pouches = applyBalancingPouch(
          pouches,
          merged.groups,
          balancingPouchId,
          balancingTargetTotal,
          merged.balancingPreferredSign,
        );
        pouches = syncBalancingPouchToActiveLayer(pouches, balancingPouchId, {
          ...merged,
          pouches,
        });
        merged = { ...merged, pouches };
      }
    }

    if (merged.layers?.length) {
      merged = syncIncomingPouchScalarsToActiveLayer(merged);
      merged = syncSheetPouchesToActiveLayer(merged);
    }

    const isActiveSheet = sheetId === stateRef.current.activeSheetId;
    const hasVisualChange =
      merged.pouches.length !== existing.pouches.length ||
      merged.groups.length !== existing.groups.length ||
      merged.pouches.some(p => {
        const original = existing.pouches.find(o => o.id === p.id);
        return (
          !original ||
          original.x !== p.x ||
          original.y !== p.y ||
          original.value !== p.value ||
          original.multiplier !== p.multiplier
        );
      }) ||
      merged.groups.some(g => {
        const original = existing.groups.find(o => o.id === g.id);
        if (!original) return true;
        const oldIds = [...(original.pouches ?? [])].sort().join(',');
        const newIds = [...(g.pouches ?? [])].sort().join(',');
        return oldIds !== newIds || original.path !== g.path;
      });

    let withHistory = HistoryLogic.addEntry(
      merged,
      merged.groups,
      merged.pouches,
      HistoryActionType.PASTE_BULK_UPDATE,
      historyActionLabel(HistoryActionType.PASTE_BULK_UPDATE, HISTORY_STORAGE_LOCALE),
      undefined,
      true,
    );
    withHistory = syncBalancingTargetTotalToActiveLayer({
      ...withHistory,
      revision: merged.revision,
      lastModified: new Date(),
    });

    const bridgeAnimating =
      isActiveSheet &&
      hasVisualChange &&
      scheduleBridgeActiveSheetVisualUpdate(
        activeSheetRef.current,
        existing.pouches,
        existing.groups,
        merged.pouches,
        merged.groups,
        withHistory.pouches,
        withHistory.groups,
      );

    await SheetStorage.saveSheet(withHistory);

    dispatch({ type: 'UPDATE_SHEET', payload: withHistory });
    stateRef.current = {
      ...stateRef.current,
      sheets: stateRef.current.sheets.map(s =>
        s.id === sheetId ? withHistory : s
      ),
    };

    if (
      sheetId === stateRef.current.activeSheetId &&
      activeSheetRef.current &&
      !bridgeAnimating
    ) {
      activeSheetRef.current.setPouches(withHistory.pouches);
      activeSheetRef.current.setGroups(withHistory.groups);
    }

    const skipMutationId = uuidv4();
    bridgeSelfMutationIdRef.current = skipMutationId;
    postSheetMutatedBroadcast(
      sheetId,
      withHistory.revision ?? parsed.revision,
      skipMutationId,
    );

    const apiSheet = readSheetForApi(withHistory);
    return {
      ok: true,
      sheetId,
      revision: withHistory.revision ?? parsed.revision,
      sheet: apiSheet.sheet,
    };
  }, []);

  const tidySheetFromBridge = useCallback(async (
    sheetId: string,
    options?: { expectedRevision?: number },
  ): Promise<WriteSheetResult | SheetApiError> => {
    const existing = stateRef.current.sheets.find(s => s.id === sheetId);
    if (!existing) {
      return { ok: false, code: 'SHEET_NOT_FOUND', message: `Sheet not found: ${sheetId}` };
    }

    const baseRevision = existing.revision ?? 0;
    if (
      options?.expectedRevision !== undefined &&
      options.expectedRevision !== baseRevision
    ) {
      return {
        ok: false,
        code: 'REVISION_CONFLICT',
        message: `Expected revision ${options.expectedRevision} but current is ${baseRevision}`,
      };
    }

    const repairedGroups = repairGroupPaths(existing.groups);
    const layout = tidySheetLayout(existing.pouches, repairedGroups);
    let merged: Sheet = {
      ...existing,
      pouches: layout.pouches,
      groups: layout.groups,
      revision: baseRevision + 1,
    };

    const balancingPouchId = merged.balancingPouchId ?? null;
    if (balancingPouchId) {
      const bpLayerId = resolveActiveLayerId(merged);
      const balancingTargetTotal = getBalancingTargetForLayer(merged, bpLayerId);
      if (balancingTargetTotal !== undefined) {
        let pouches = syncBalancingPouchTimeMode(
          merged.pouches,
          merged.groups,
          balancingPouchId,
          merged,
        );
        pouches = applyBalancingPouch(
          pouches,
          merged.groups,
          balancingPouchId,
          balancingTargetTotal,
          merged.balancingPreferredSign,
        );
        pouches = syncBalancingPouchToActiveLayer(pouches, balancingPouchId, {
          ...merged,
          pouches,
        });
        merged = { ...merged, pouches };
      }
    }

    if (merged.layers?.length) {
      merged = syncIncomingPouchScalarsToActiveLayer(merged);
      merged = syncSheetPouchesToActiveLayer(merged);
    }

    const isActiveSheet = sheetId === stateRef.current.activeSheetId;
    const hasVisualChange =
      merged.pouches.some(p => {
        const original = existing.pouches.find(o => o.id === p.id);
        return !original || original.x !== p.x || original.y !== p.y;
      }) ||
      merged.groups.some(g => {
        const original = existing.groups.find(o => o.id === g.id);
        return !original || original.path !== g.path;
      });

    let withHistory = HistoryLogic.addEntry(
      merged,
      merged.groups,
      merged.pouches,
      HistoryActionType.PASTE_BULK_UPDATE,
      historyActionLabel(HistoryActionType.PASTE_BULK_UPDATE, HISTORY_STORAGE_LOCALE),
      undefined,
      true,
    );
    withHistory = syncBalancingTargetTotalToActiveLayer({
      ...withHistory,
      revision: merged.revision,
      lastModified: new Date(),
    });

    const bridgeAnimating =
      isActiveSheet &&
      hasVisualChange &&
      scheduleBridgeActiveSheetVisualUpdate(
        activeSheetRef.current,
        existing.pouches,
        existing.groups,
        merged.pouches,
        merged.groups,
        withHistory.pouches,
        withHistory.groups,
      );

    await SheetStorage.saveSheet(withHistory);

    dispatch({ type: 'UPDATE_SHEET', payload: withHistory });
    stateRef.current = {
      ...stateRef.current,
      sheets: stateRef.current.sheets.map(s =>
        s.id === sheetId ? withHistory : s
      ),
    };

    if (
      sheetId === stateRef.current.activeSheetId &&
      activeSheetRef.current &&
      !bridgeAnimating
    ) {
      activeSheetRef.current.setPouches(withHistory.pouches);
      activeSheetRef.current.setGroups(withHistory.groups);
    }

    const skipMutationId = uuidv4();
    bridgeSelfMutationIdRef.current = skipMutationId;
    postSheetMutatedBroadcast(
      sheetId,
      withHistory.revision ?? merged.revision,
      skipMutationId,
    );

    const apiSheet = readSheetForApi(withHistory);
    return {
      ok: true,
      sheetId,
      revision: withHistory.revision ?? merged.revision ?? 0,
      sheet: apiSheet.sheet,
    };
  }, []);

  const persistSheetWithLayerHistory = useCallback(async (
    sheet: Sheet,
    action:
      | HistoryActionType.ADD_SHEET_LAYER
      | HistoryActionType.REMOVE_SHEET_LAYER
      | HistoryActionType.RENAME_SHEET_LAYER,
    targetId?: string,
    forceNewEntry?: boolean
  ) => {
    const synced = sheet.layers?.length ? syncSheetPouchesToActiveLayer(sheet) : sheet;
    const currentSheet = stateRef.current.sheets.find(s => s.id === synced.id);
    if (!currentSheet) return;

    isInHistoryActionRef.current = true;
    document.documentElement.classList.add('in-action');
    if (historyActionTimerRef.current !== null) {
      clearTimeout(historyActionTimerRef.current);
      historyActionTimerRef.current = null;
    }

    // addEntry の layers は第1引数 sheet から取るため、操作後の synced と揃える
    const sheetForHistory: Sheet = {
      ...currentSheet,
      pouches: synced.pouches,
      groups: synced.groups,
      layers: synced.layers,
      activeLayerId: synced.activeLayerId,
      balancingTargets: synced.balancingTargets,
      balancingPouchId: synced.balancingPouchId,
      balancingTargetTotal: synced.balancingTargetTotal,
      balancingPreferredSign: synced.balancingPreferredSign,
    };

    const withHistory = HistoryLogic.addEntry(
      sheetForHistory,
      synced.groups,
      synced.pouches,
      action,
      action,
      targetId,
      forceNewEntry
    );

    const toSave = syncBalancingTargetTotalToActiveLayer({
      ...withHistory,
      revision: synced.revision ?? currentSheet.revision,
      layers: synced.layers,
      activeLayerId: synced.activeLayerId,
      balancingTargets: synced.balancingTargets,
      pouches: synced.pouches,
      groups: synced.groups,
      balancingPouchId: synced.balancingPouchId ?? currentSheet.balancingPouchId,
      balancingTargetTotal: synced.balancingTargetTotal ?? currentSheet.balancingTargetTotal,
      balancingPreferredSign: synced.balancingPreferredSign ?? currentSheet.balancingPreferredSign,
      lastModified: new Date(),
    });

    dispatch({ type: 'UPDATE_SHEET', payload: toSave });
    stateRef.current = {
      ...stateRef.current,
      sheets: stateRef.current.sheets.map(s =>
        s.id === toSave.id ? toSave : s
      ),
    };
    await SheetStorage.saveSheet(toSave);

    historyActionTimerRef.current = window.setTimeout(() => {
      if (isInHistoryActionRef.current) {
        isInHistoryActionRef.current = false;
        document.documentElement.classList.remove('in-action');
        historyActionTimerRef.current = null;
      }
    }, 600);

    return toSave;
  }, []);

  useEffect(() => {
    const onDocumentMouseDown = (e: MouseEvent) => {
      const target = e.target as Element;
      if (target.closest('.layer-remove-btn, .pouch-delete-button, .group-delete-button, .delete-sheet-button')) return;
      clearLayerRemoveConfirming();
      clearConfirmingDeleteButtons();
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, []);

  const handleSetActiveLayer = useCallback(async (layerId: string) => {
    clearLayerRemoveConfirming();
    clearConfirmingDeleteButtons();
    if (!activeSheet || layerId === resolveActiveLayerId(activeSheet)) return;
    logSheetPouchValues('layer-switch:before', activeSheet);
    const fromLayerId = resolveActiveLayerId(activeSheet);
    const { sheet, direction } = setSheetActiveLayer(activeSheet, layerId);
    setLayerSwitchFromLayerId(fromLayerId);
    beginLayerSlide(direction);
    let sheetForLayer = syncSheetPouchesToActiveLayer({ ...sheet, activeLayerId: layerId });
    let syncedPouches = sheetForLayer.pouches;
    const bpId = sheet.balancingPouchId ?? null;
    if (bpId) {
      const ensured = ensureBalancingTargetForLayer(
        sheetForLayer,
        layerId,
        syncedPouches,
        sheet.groups
      );
      sheetForLayer = ensured.sheet;
      const bpTarget = ensured.target;
      if (bpTarget !== undefined) {
        syncedPouches = syncBalancingPouchTimeMode(
          syncedPouches,
          sheet.groups,
          bpId,
          { ...sheetForLayer, pouches: syncedPouches }
        );
        syncedPouches = applyBalancingPouch(
          syncedPouches,
          sheet.groups,
          bpId,
          bpTarget,
          sheet.balancingPreferredSign
        );
        syncedPouches = syncBalancingPouchToActiveLayer(syncedPouches, bpId, {
          ...sheetForLayer,
          pouches: syncedPouches,
        });
      }
    }
    const sheetToSave = syncBalancingTargetTotalToActiveLayer({
      ...sheetForLayer,
      pouches: syncedPouches,
    });
    logSheetPouchValues('layer-switch:after', sheetToSave, syncedPouches);
    await persistSheet(sheetToSave);
    if (direction !== 0) {
      commitLayerSlide();
    }
  }, [activeSheet, persistSheet, beginLayerSlide, commitLayerSlide]);

  const handleAddSheetLayer = useCallback(async () => {
    clearLayerRemoveConfirming();
    clearConfirmingDeleteButtons();
    const base = stateRef.current.sheets.find(s => s.id === activeSheet?.id) ?? activeSheet;
    if (!base) return;
    const sheet = addSheetLayer(base);
    const newLayerId = resolveActiveLayerId(sheet);
    setLayerSwitchFromLayerId(resolveActiveLayerId(base));
    beginLayerSlide(1);
    await persistSheetWithLayerHistory(
      syncSheetPouchesToActiveLayer(sheet),
      HistoryActionType.ADD_SHEET_LAYER,
      newLayerId
    );
    commitLayerSlide();
  }, [activeSheet, persistSheetWithLayerHistory, beginLayerSlide, commitLayerSlide]);

  const handleRenameSheetLayer = useCallback(async (layerId: string, name: string) => {
    const base = stateRef.current.sheets.find(s => s.id === activeSheet?.id) ?? activeSheet;
    if (!base) return;
    const sheet = updateSheetLayerName(base, layerId, name);
    await persistSheetWithLayerHistory(sheet, HistoryActionType.RENAME_SHEET_LAYER, layerId);
  }, [activeSheet, persistSheetWithLayerHistory]);

  const handleRemoveSheetLayer = useCallback(async (layerId: string) => {
    const base = stateRef.current.sheets.find(s => s.id === activeSheet?.id) ?? activeSheet;
    if (!base) return;
    const removedWasActive = resolveActiveLayerId(base) === layerId;
    const sheet = removeSheetLayer(base, layerId);
    const slideDirection = removedWasActive
      ? compareLayerOrder(base, layerId, resolveActiveLayerId(sheet))
      : 0;
    if (slideDirection !== 0) {
      setLayerSwitchFromLayerId(layerId);
      beginLayerSlide(slideDirection);
    }
    await persistSheetWithLayerHistory(
      syncSheetPouchesToActiveLayer(sheet),
      HistoryActionType.REMOVE_SHEET_LAYER,
      layerId
    );
    if (slideDirection !== 0) {
      commitLayerSlide();
    }
  }, [activeSheet, persistSheetWithLayerHistory, beginLayerSlide, commitLayerSlide]);

  const pendingLayerNameEditRef = useRef(false);

  // Option/Alt レイヤー操作（Mac の Option = e.altKey）
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isLayerShortcutChord(e)) return;
      if (!activeSheet?.layers?.length) return;
      if ((window as Window & { isIMEComposing?: boolean }).isIMEComposing) return;

      const el = document.activeElement;
      const inLayerNameInput =
        el instanceof HTMLElement && !!el.closest('.sheet-layer-calc-item-input');
      if (isEditableElement(el) && !inLayerNameInput) return;

      const sorted = getSortedLayers(activeSheet);
      const activeId = resolveActiveLayerId(activeSheet);
      const activeIdx = sorted.findIndex(l => l.id === activeId);

      const stopLayerKey = () => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      };

      const num = isLayerSwitchNumberKey(e);
      if (num != null) {
        const layer = sorted[num - 1];
        if (layer) {
          stopLayerKey();
          void handleSetActiveLayer(layer.id);
        }
        return;
      }

      const prevNext = isLayerPrevNextKey(e);
      if (prevNext && sorted.length > 1 && activeIdx >= 0) {
        stopLayerKey();
        const nextIdx =
          prevNext === 'next'
            ? (activeIdx + 1) % sorted.length
            : (activeIdx - 1 + sorted.length) % sorted.length;
        void handleSetActiveLayer(sorted[nextIdx].id);
        return;
      }

      if (isLayerAddKey(e)) {
        stopLayerKey();
        pendingLayerNameEditRef.current = true;
        void handleAddSheetLayer();
        return;
      }

      if (isLayerRemoveKey(e) && sorted.length > 1) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleKeyboardLayerRemove(activeId, true, layerId => {
          void handleRemoveSheetLayer(layerId);
        });
        return;
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [activeSheet, handleSetActiveLayer, handleAddSheetLayer, handleRemoveSheetLayer]);

  // ActiveSheetComponentへの参照を保持する変数を追加
  const activeSheetRef = useRef<ActiveSheetComponentRef>(null);
  const activeSheetTitleInputRef = useRef<HTMLInputElement>(null);
  const [isActiveSheetTitleFocused, setIsActiveSheetTitleFocused] = useState(false);

  const activeSheetTitleEditTooltipHtml = useMemo(() => {
    if (!isGuideEnabled) return '';
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `${formatKeyDescLine('Enter', esc(tt.commitTitle))}<br/>${formatKeyDescLine('Esc', esc(tt.discard))}`;
  }, [isGuideEnabled, tt.commitTitle, tt.discard]);
  
  // 前回のhover状態を記録（不要な更新を避けるため）
  const lastHoverStateRef = useRef<{groupId: string | null, pouchId: string | null}>({groupId: null, pouchId: null});
  
  // stateが変更されたらrefも更新（クロージャ問題を回避）
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // アプリケーションの初期化
  useEffect(() => {
    const initApp = async () => {
      try {
        logImportLaunch('initApp start');
        // Service Worker の登録（Electron の file:// では登録しない。誤登録時の reload でインポートが消える）
        const isElectronApp = !!(window as unknown as { electron?: { isElectron?: boolean } }).electron?.isElectron;
        if ('serviceWorker' in navigator && !isElectronApp) {
          try {
            const registration = await navigator.serviceWorker.register('/pouches/sw.js');
            
            
            // Service Worker 更新検知
            registration.addEventListener('updatefound', () => {
              
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'activated') {
                    
                    // 新しいService Workerが有効になったらページをリロード
                    window.location.reload();
                  }
                });
              }
            });
          } catch (error) {
            
          }
        }

        // リロード時はアニメーションフラグをON
        setIsInitialLoading(true);
        
        // データベースの初期化
        await initializeData();
        
        // リロード時に全パウチをリセット
        // リロード時はActiveSheetComponentの初期化時に処理されるため、ここでは処理しない
        
        // 初期シートを取得（ストレージから読み込み、または新規作成）
        logImportLaunch('initApp before getInitialSheets');
        const initialSheets = await getInitialSheets();
        logImportLaunch('initApp after getInitialSheets', { count: initialSheets.length });
        
        // 各シートのグループパスを修復
        const repairedSheets = await Promise.all(
          initialSheets.map(async (sheet) => {
            const repairedGroups = repairGroupPaths(sheet.groups);
            if (repairedGroups !== sheet.groups) {
              const repairedSheet = { ...sheet, groups: repairedGroups };
              await SheetStorage.saveSheet(repairedSheet);
              return repairedSheet;
            }
            return sheet;
          })
        );
        
        // 設定の取得
        const settings = await SettingsStorage.getSettings();
        
        // アクティブなシートIDの取得
        const storedActiveSheetId = SheetStorage.getActiveSheetId();
        const mergedInitialSheets = mergeSheetsForState(stateRef.current.sheets, repairedSheets);

        dispatch({
          type: 'INIT_APP',
          payload: { sheets: mergedInitialSheets, settings },
        });
        
        // アクティブシートを設定（シートが存在する場合は必ず選択）
        if (mergedInitialSheets.length > 0) {
          if (storedActiveSheetId && mergedInitialSheets.find(sheet => sheet.id === storedActiveSheetId)) {
            dispatch({ type: 'SET_ACTIVE_SHEET', payload: storedActiveSheetId });
          } else {
            const latestSheet = mergedInitialSheets[0];
            dispatch({ type: 'SET_ACTIVE_SHEET', payload: latestSheet.id });
            SheetStorage.saveActiveSheetId(latestSheet.id);
          }
        }
        
        // 設定値からサイドバーの状態を設定
        if (settings.isLeftSidebarOpen !== undefined) {
          dispatch({ type: 'TOGGLE_MENU', payload: settings.isLeftSidebarOpen });
        }
        
        if (settings.isRightSidebarOpen !== undefined) {
          dispatch({ type: 'TOGGLE_SIDEBAR', payload: settings.isRightSidebarOpen });
        }
        
        // 初期化処理完了後、タイマーでフラグをリセット
        setTimeout(() => {
          setIsInitialLoading(false);
        }, 1000);

        const legacyStatus = entitlementService.applyLegacyIfNeeded(repairedSheets.length);
        setEntitlementStatus(legacyStatus);
        logImportLaunch('initApp before entitlement refresh');
        void entitlementService.refresh(repairedSheets.length).then((status) => {
          logImportLaunch('initApp entitlement refresh done', status);
          setEntitlementStatus(status);
        });

        // ネイティブのディープリンクインポートはここ以降に実行（INIT_APP より前だと上書きされる）
        logImportLaunch('initApp complete (app init gate open)');
        appInitReadyRef.current?.resolve();
        
        // ファイルハンドラーとURLパラメータのチェック
        try {
          // Share Target API (POST)からのファイル受信をチェック
          if (window.location.search.includes('files') || document.referrer.includes('share-target')) {
            
            // POSTデータの処理は通常Service Workerで行われるため、
            // ここではログ出力のみ
          }
          
          // URLパラメータをチェック
          const urlParams = new URLSearchParams(window.location.search);
          const importUrl = urlParams.get('import');
          const importFileParam = urlParams.get('import'); // import_file → importに変更
          const importPublicParam = urlParams.get('import_public'); // 公開フォルダからのインポート
          
          // 直接共有URLでアクセスされた場合（例: /pouches/import/filename.pouches）
          const currentPath = window.location.pathname;
          const isDirectImportUrl = currentPath.includes('/import/') && currentPath.endsWith('.pouches');
          
          const inBrowserTab =
            !window.matchMedia('(display-mode: standalone)').matches &&
            !window.matchMedia('(display-mode: window-controls-overlay)').matches;
          if (inBrowserTab && isStoreHandoffBlockingWebImport(window.location.href)) {
            logImportLaunch('initApp: waiting for Store handoff or user chose web import');
            return;
          }

          // importパラメータをチェック（通常のインポート）
          if (importFileParam) {
            
            
            // ファイル名からシート名を推定してタイトルを変更
            const sheetName = importFileParam.replace('.pouches', '');
            document.title = `${sheetName} - POUCHES`;
            
            // メタディスクリプションを更新
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
              metaDescription.setAttribute('content', t.meta.importSheet(sheetName));
            }
            
            const fullImportUrl = buildServerImportUrl(importFileParam);
            clearImportLaunchDeclined(fullImportUrl);
            clearImportPromptPending(fullImportUrl);
            urlParamImportRef.current.search = window.location.search;
            urlParamImportRef.current.inFlight = true;
            try {
              await importFromServerFile(fullImportUrl);
            } finally {
              urlParamImportRef.current.inFlight = false;
            }
          }
          // import_publicパラメータをチェック（公開フォルダからのインポート、削除期限なし）
          else if (importPublicParam) {
            
            
            // ファイル名からシート名を推定してタイトルを変更
            const sheetName = importPublicParam.replace('.pouches', '');
            document.title = `${sheetName} - POUCHES`;
            
            // メタディスクリプションを更新
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
              metaDescription.setAttribute('content', t.meta.importSheet(sheetName));
            }
            
            const fullImportUrl = buildServerImportUrl(importPublicParam, true);
            clearImportLaunchDeclined(fullImportUrl);
            clearImportPromptPending(fullImportUrl);
            urlParamImportRef.current.search = window.location.search;
            urlParamImportRef.current.inFlight = true;
            try {
              await importFromServerFile(fullImportUrl);
            } finally {
              urlParamImportRef.current.inFlight = false;
            }
          }
          // 直接共有URLでアクセスされた場合をチェック（旧方式、互換性のため残す）
          else if (isDirectImportUrl) {
            
            
            // パスからファイル名を抽出してタイトルを変更
            const fileName = currentPath.split('/').pop() || '';
            const sheetName = fileName.replace('.pouches', '');
            document.title = `${sheetName} - POUCHES`;
            
            // メタディスクリプションを更新
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
              metaDescription.setAttribute('content', t.meta.importSheet(sheetName));
            }
            
            const fullImportUrl = `https://apps.tomippe.jp${currentPath}`;
            await importFromServerFile(fullImportUrl);
          }

          // 通常のimportパラメータもチェック
          else if (importUrl) {
            // URL から .pouches ファイルをダウンロードしてインポート
            const response = await fetch(importUrl);
            if (response.ok) {
              const blob = await response.blob();
              // URLから適切なファイル名を取得
              const urlFileName = importUrl.split('/').pop() || 'imported.pouches';
              const file = new File([blob], urlFileName, { type: 'application/pouches' });
              
              // 既存ウィンドウがあるかチェック
              const existingWindows = await checkForExistingWindows();
              
              if (existingWindows.length > 0) {
                // 既存ウィンドウにファイルを送信
                const success = await sendFileToExistingWindow(file, existingWindows[0]);
                
                // ファイル送信が成功した場合のみウィンドウを閉じる
                if (success) {
                  // ファイルのダブルクリック時のみウィンドウを閉じる
                  // 他の場合は閉じない
                } else {
                  // 送信に失敗した場合は現在のウィンドウで処理
                  await importSheetFromFile(file);
                }
              } else {
                // 新しいウィンドウまたは現在のウィンドウで処理
                await importSheetFromFile(file);
              }
            }
            
            // URLパラメータをクリア
            // window.history.replaceState(null, '', window.location.pathname);
          }
          
          // Web Share Target API からのファイル受信をチェック
          const shareParams = new URLSearchParams(window.location.search);
          if (shareParams.has('file')) {
            try {
              // 共有されたファイルの処理
              
              const fileParam = shareParams.get('file');
              if (fileParam) {
                // 既存ウィンドウがあるかチェック
                const existingWindows = await checkForExistingWindows();
                
                
                
                if (existingWindows.length > 0) {
                  
                  // ファイルパラメータからファイルを取得する処理
                  // 実際の実装はブラウザ依存のため、今後の対応で改善予定
                  
                  // 共有ファイル処理後、新しいウィンドウを閉じる
                  // ファイルのダブルクリック時のみウィンドウを閉じる
                } else {
                  
                  // 新しいウィンドウで処理
                }
              }
            } catch (shareError) {
              
            }
            
            // Share Target URLパラメータをクリア（重複処理防止）
            const url = new URL(window.location.href);
            url.searchParams.delete('file');
            window.history.replaceState(null, '', url.pathname + url.search);
            
          }
        } catch (error) {
          console.error('ファイルハンドラーからのインポートに失敗しました:', error);
        }
        
      } catch (error) {
        console.error('アプリケーションの初期化に失敗しました:', error);
        // エラー時もフラグをリセット
        setIsInitialLoading(false);
        appInitReadyRef.current?.resolve();
      }
    };
    
    initApp();
  }, []);
  
  // サーバーからファイルを取得してインポートする関数
  const importFromServerFile = useCallback(async (serverFilePath: string, skipConfirm: boolean = false, skipWindowClose: boolean = false, skipBroadcast: boolean = false): Promise<'imported' | 'cancelled' | 'failed'> => {
    try {
      const inBrowserTab =
        !window.matchMedia('(display-mode: standalone)').matches &&
        !window.matchMedia('(display-mode: window-controls-overlay)').matches;
      if (inBrowserTab && isStoreHandoffBlockingWebImport(window.location.href)) {
        return 'cancelled';
      }

      logImportLaunch('importFromServerFile start', { serverFilePath, skipConfirm });
      if (isImportLaunchDeclined(serverFilePath)) {
        logImportLaunch('importFromServerFile skipped: declined', serverFilePath);
        return 'cancelled';
      }
      if (isImportPromptPending(serverFilePath)) {
        logImportLaunch('importFromServerFile skipped: prompt pending', serverFilePath);
        return 'cancelled';
      }

      const response = await fetchImportFile(serverFilePath);
      logImportLaunch('importFromServerFile fetch', { ok: response.ok, status: response.status });

      if (response.ok) {
        const jsonData = response.text;
        const parsedData = JSON.parse(jsonData);
        
        // DB内のタイトルを使用（ファイル名ではなく）
        const sheetTitle = parsedData.sheets?.[0]?.title || t.default.importedSheet;
        
        // タイトルとディスクリプションを実際のシート名で更新
        document.title = `${sheetTitle} - POUCHES`;
        
        // メタディスクリプションを更新
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', t.meta.importSheet(sheetTitle));
        }
        
        const file = new File([jsonData], `${sheetTitle}.pouches`, { type: 'application/pouches' });

        const finishImport = async (): Promise<'imported'> => {
          await executeImport(file, serverFilePath, skipWindowClose, skipBroadcast);
          clearImportPromptPending(serverFilePath);
          clearImportLaunchDeclined(serverFilePath);
          clearPendingImportLaunchUrl();
          clearImportLaunchUrl();
          return 'imported';
        };

        // インポート確認ダイアログ（importボタンからの場合はスキップ）
        if (skipConfirm) {
          const result = await finishImport();
          logImportLaunch('importFromServerFile finishImport', result);
          return result;
        }

        markImportPromptPending(serverFilePath);
        return await new Promise<'imported' | 'cancelled'>((resolve) => {
          showCustomConfirm(
            sheetTitle,
            () => {
              void finishImport().then(() => resolve('imported'));
            },
            () => {
              clearImportPromptPending(serverFilePath);
              markImportLaunchDeclined(serverFilePath);
              resolve('cancelled');
            }
          );
        });
      } else {
        console.error('[Protocol Import] サーバーファイルの取得に失敗:', response.status);
        alert(t.error.importFailed);
        return 'failed';
      }
    } catch (error) {
      console.error('[Protocol Import] エラー:', error);
      alert(t.error.importFailed);
      return 'failed';
    }
  }, [t]);
  
  // URLパラメータの変更を監視（エアドロップ等で search だけ変わる場合）。初回は initApp が担当。
  useEffect(() => {
    const handleUrlChange = () => {
      const search = window.location.search;
      const urlParams = new URLSearchParams(search);
      const importFileParam = urlParams.get('import');
      const importPublicParam = urlParams.get('import_public');

      if (!importFileParam && !importPublicParam) {
        urlParamImportRef.current.search = '';
        return;
      }

      if (urlParamImportRef.current.inFlight) return;
      if (urlParamImportRef.current.search === search) return;

      const processImport = async () => {
        urlParamImportRef.current.search = search;
        urlParamImportRef.current.inFlight = true;
        try {
          if (importFileParam) {
            const sheetName = importFileParam.replace('.pouches', '');
            document.title = `${sheetName} - POUCHES`;
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
              metaDescription.setAttribute('content', t.meta.importSheet(sheetName));
            }
            const fullImportUrl = buildServerImportUrl(importFileParam);
            clearImportLaunchDeclined(fullImportUrl);
            clearImportPromptPending(fullImportUrl);
            await importFromServerFile(fullImportUrl);
          } else if (importPublicParam) {
            const sheetName = importPublicParam.replace('.pouches', '');
            document.title = `${sheetName} - POUCHES`;
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
              metaDescription.setAttribute('content', t.meta.importSheet(sheetName));
            }
            const fullImportUrl = buildServerImportUrl(importPublicParam, true);
            clearImportLaunchDeclined(fullImportUrl);
            clearImportPromptPending(fullImportUrl);
            await importFromServerFile(fullImportUrl);
          }
        } catch (error) {
          console.error('[URL Change] Failed to process import:', error);
        } finally {
          urlParamImportRef.current.inFlight = false;
        }
      };

      void processImport();
    };

    window.addEventListener('popstate', handleUrlChange);
    const intervalId = setInterval(handleUrlChange, 1000);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      clearInterval(intervalId);
    };
  }, [importFromServerFile, t]);
  
  // ウィンドウリサイズ時の処理
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      
      // モバイル状態が変わった場合のみ更新
      if (mobile !== state.isMobile) {
        // isMobileの状態を更新
        dispatch({ type: 'SET_MOBILE_STATE', payload: mobile });
        
        // モバイル状態になった場合は、メニューとサイドバーを閉じる
        if (mobile) {
          dispatch({ type: 'TOGGLE_MENU', payload: false });
          dispatch({ type: 'TOGGLE_SIDEBAR', payload: false });
          
          // 設定も更新
          SettingsStorage.saveSettings({
            ...state.settings,
            isLeftSidebarOpen: false,
            isRightSidebarOpen: false
          });
        } else {
          // PCに戻った場合は、設定に保存されている状態を復元
          dispatch({ type: 'TOGGLE_MENU', payload: state.settings.isLeftSidebarOpen });
          dispatch({ type: 'TOGGLE_SIDEBAR', payload: state.settings.isRightSidebarOpen });
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [state.isMobile, state.settings]);
  
  // メニューとサイドバーの切り替え
  const toggleMenu = (e?: React.MouseEvent | React.TouchEvent) => {
    dispatch({ type: 'TOGGLE_MENU' });
    
    // 設定を保存
    const newIsLeftSidebarOpen = !state.isLeftSidebarOpen;
    SettingsStorage.saveSettings({
      ...state.settings,
      isLeftSidebarOpen: newIsLeftSidebarOpen
    });
  };
  
  const toggleSidebar = (e?: React.MouseEvent | React.TouchEvent) => {
    // タッチイベントの場合はpreventDefaultを実行
    if (e && 'touches' in e) {
      e.preventDefault();
    }
    dispatch({ type: 'TOGGLE_SIDEBAR' });
    
    // 設定を保存
    const newIsRightSidebarOpen = !state.isRightSidebarOpen;
    SettingsStorage.saveSettings({
      ...state.settings,
      isRightSidebarOpen: newIsRightSidebarOpen
    });
  };

  const refreshEntitlementState = useCallback(async (sheetCount = state.sheets.length) => {
    const status = await entitlementService.refresh(sheetCount);
    setEntitlementStatus(status);
    return status;
  }, [state.sheets.length]);

  const blockAddSheetWithPaywall = useCallback(async (
    sheetCount: number,
    status: EntitlementStatus,
  ): Promise<boolean> => {
    if (canAddSheet(sheetCount, status)) return false;
    if (!(await isBillingPaywallEnabled())) return false;
    setPaywallError(undefined);
    setPaywallOpen(true);
    if (isIosInAppPurchasePlatform()) {
      void prefetchIosStoreCatalog()
        .then((next) => setEntitlementStatus(next))
        .catch(() => undefined);
    }
    return true;
  }, []);

  useEffect(() => {
    if (!paywallOpen || !isIosInAppPurchasePlatform()) return;
    let cancelled = false;
    void prefetchIosStoreCatalog()
      .then((next) => {
        if (!cancelled) setEntitlementStatus(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [paywallOpen]);

  const ensureCanAddSheet = useCallback(async (sheetCount = state.sheets.length): Promise<boolean> => {
    const status = await refreshEntitlementState(sheetCount);
    if (await blockAddSheetWithPaywall(sheetCount, status)) return false;
    return true;
  }, [refreshEntitlementState, blockAddSheetWithPaywall]);

  const createSheetFromBridge = useCallback(async (options?: {
    title?: string;
    activate?: boolean;
  }): Promise<CreateSheetResult | SheetApiError> => {
    const sheetsCount = stateRef.current.sheets.length;
    const status = await refreshEntitlementState(sheetsCount);
    if (!canAddSheet(sheetsCount, status)) {
      return {
        ok: false,
        code: 'SHEET_LIMIT_REACHED',
        message: `Cannot create a new sheet (free limit: ${sheetsCount} sheets)`,
      };
    }

    try {
      let newSheet = await createAndSaveApiSheet(sheetsCount);
      const title = options?.title?.trim();
      if (title) {
        newSheet = { ...newSheet, title };
        await SheetStorage.saveSheet(newSheet);
      }

      authorizeNextAddSheet();
      dispatch({ type: 'ADD_SHEET', payload: newSheet });
      if (options?.activate !== false) {
        dispatch({ type: 'SET_ACTIVE_SHEET', payload: newSheet.id });
        await SheetStorage.saveActiveSheetId(newSheet.id);
      }

      const apiSheet = readSheetForApi(newSheet);
      return {
        ok: true,
        sheetId: newSheet.id,
        revision: apiSheet.revision,
        sheet: apiSheet.sheet,
      };
    } catch (error) {
      return {
        ok: false,
        code: 'INTERNAL_ERROR',
        message: error instanceof Error ? error.message : String(error),
      };
    }
  }, [refreshEntitlementState]);


  const handlePaywallPurchase = useCallback(async () => {
    setPaywallBusy(true);
    setPaywallError(undefined);
    try {
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      await new Promise((resolve) => setTimeout(resolve, 500));
      await entitlementService.purchase();
      const status = await refreshEntitlementState();
      setEntitlementStatus(status);
      if (status.tier === 'pro') setPaywallOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (error instanceof PurchaseCancelledError || isPurchaseCancelledMessage(message)) return;
      const billing = getBillingCopy(locale);
      const localized = localizeEntitlementError(message, locale);
      if (localized === null) return;
      setPaywallError(localized || billing.purchaseFailedTitle);
    } finally {
      setPaywallBusy(false);
    }
  }, [refreshEntitlementState, locale]);

  const handlePaywallOpenStore = useCallback(async () => {
    const store = (window as { electron?: { store?: { openProductPage?: () => Promise<void> } } }).electron?.store;
    if (!store?.openProductPage) return;
    try {
      await store.openProductPage();
    } catch {
      // ignore — fallback link only
    }
  }, []);

  const handlePaywallRestore = useCallback(async () => {
    setPaywallBusy(true);
    setPaywallError(undefined);
    try {
      const restored = await entitlementService.restore();
      const status = await refreshEntitlementState();
      const billing = getBillingCopy();
      if (!restored || !canAddSheet(state.sheets.length, status)) {
        setPaywallError(billing.restoreNone);
        return;
      }
      setPaywallOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (isPurchaseCancelledMessage(message)) return;
      const billing = getBillingCopy(locale);
      const localized = localizeEntitlementError(message, locale);
      if (localized === null) return;
      setPaywallError(localized || billing.restoreFailedTitle);
    } finally {
      setPaywallBusy(false);
    }
  }, [refreshEntitlementState, state.sheets.length, locale]);

  const handlePaywallManage = useCallback(async () => {
    setPaywallBusy(true);
    setPaywallError(undefined);
    try {
      await entitlementService.manageSubscription();
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (isPurchaseCancelledMessage(message)) return;
      const billing = getBillingCopy(locale);
      const localized = localizeEntitlementError(message, locale);
      if (localized === null) return;
      setPaywallError(localized || billing.purchaseFailedTitle);
    } finally {
      setPaywallBusy(false);
    }
  }, [locale]);

  const handlePaywallRedeem = useCallback(async () => {
    setPaywallBusy(true);
    setPaywallError(undefined);
    try {
      const status = await entitlementService.redeemCode(paywallRedeemCodeInput);
      setEntitlementStatus(status);
      if (status.tier === 'pro') {
        setPaywallOpen(false);
        setPaywallRedeemCodeInput('');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      const billing = getBillingCopy(locale);
      const localized = localizeEntitlementError(message, locale);
      if (localized === null) return;
      setPaywallError(localized || billing.purchaseFailedTitle);
    } finally {
      setPaywallBusy(false);
    }
  }, [locale, paywallRedeemCodeInput]);

  const handleSidebarBillingStatusClick = useCallback(async () => {
    if (!billingPaywallEnabled) return;
    if (entitlementStatus.source === 'debug') return;

    const isWebRedeem =
      detectBillingPlatform() === 'web' && !(window as { electron?: unknown }).electron;
    if (!isWebRedeem && !isNativeBillingPaywallPlatform()) return;

    if (canIssueWebRedeemCode(entitlementStatus) && isNativeBillingPaywallPlatform()) {
      setPaywallError(undefined);
      setPaywallOpen(true);
      return;
    }

    if (!isProEntitlement(entitlementStatus)) {
      setPaywallError(undefined);
      setPaywallOpen(true);
    }
  }, [billingPaywallEnabled, entitlementStatus]);

  
  // シートの新規作成
  const handleCreateNewSheet = async (e?: React.MouseEvent | React.TouchEvent) => {
    // モバイルの場合は左サイドバーを閉じる
    if (state.isMobile && state.isLeftSidebarOpen) {
      dispatch({ type: 'TOGGLE_MENU', payload: false });
    }

    if (!(await ensureCanAddSheet())) return;
    
    try {
      // 事前読み込み方式で新しいシート（初期パウチ付き）を作成
      const newSheet = await createAndSaveNewSheet(state.sheets.length);
      
      // Reducerに新しいシートを追加
      authorizeNextAddSheet();
      dispatch({ type: 'ADD_SHEET', payload: newSheet });
      dispatch({ type: 'SET_ACTIVE_SHEET', payload: newSheet.id });
      
      // タイトル入力にフォーカスを当てて文字列を選択状態にする
      setTimeout(() => {
        const titleInput = document.querySelector(
          `.sheet-item[data-sheet-id="${newSheet.id}"] input`
        ) as HTMLInputElement;
        if (titleInput) {
          titleInput.focus();
          titleInput.select(); // 文字列を選択状態にする
        }
      }, 100);
      
    } catch (error) {
      console.error('新規シート作成でエラーが発生しました:', error);
    }
  };
  
  // シートの選択
  const handleSelectSheet = async (sheetId: string, e?: React.MouseEvent | React.TouchEvent) => {
    // タッチイベントの場合はpreventDefaultを実行
    if (e && 'touches' in e) {
      e.preventDefault();
      
      // input要素内のタップでは処理しない
      const target = e.target as HTMLElement;
      if (target.tagName.toLowerCase() === 'input') {
        return;
      }
    }

    // 全ての削除ボタンからconfirmingクラスを削除
    document.querySelectorAll('.delete-sheet-button').forEach(button => {
      button.classList.remove('confirming');
    });
    clearLayerRemoveConfirming();
    clearConfirmingDeleteButtons();
    
    // シート間移動中でない場合のみ、全てのインタラクション状態をリセット
    if (!isDraggingToSheet) {
      
      // グローバルインタラクション状態をクリア
      if (globalInteractionRef.current) {
        globalInteractionRef.current = null;
      }
      
      // 現在のActiveSheetComponentのインタラクション状態をリセット
      if (activeSheetRef.current) {
        const currentComponent = activeSheetRef.current as any;
        if (currentComponent.resetInteraction) {
          currentComponent.resetInteraction();
        }
      }
      
      // ドラッグ関連の参照をクリア
      draggedGroupDataRef.current = null;
      draggedPouchDataRef.current = null;
      sourceSheetGroupRef.current = null;
      sourceSheetPouchRef.current = null;
      
      // 保存されたドラッグ状態もクリア
      preservedDragStateRef.current = null;
      
      // ホバータイマーもクリア
      if (sheetHoverTimerRef.current) {
        clearTimeout(sheetHoverTimerRef.current);
        sheetHoverTimerRef.current = null;
      }
      setHoveredSheetId(null);
      
    } else {
    }
    
    beginSheetSwitch(sheetId);
    await selectSheet(sheetId, dispatch);
  };
  
  // シートタイトルの編集
  const handleSheetTitleChange = async (sheetId: string, newTitle: string) => {
    await updateSheetTitle(sheetId, newTitle, state.sheets, dispatch);
    
    // stateRefも同期的に更新（setTimeout内のクロージャが古いデータを参照する問題を防ぐ）
    stateRef.current = {
      ...stateRef.current,
      sheets: stateRef.current.sheets.map(s => 
        s.id === sheetId ? { ...s, title: newTitle, lastModified: new Date() } : s
      )
    };
  };
  
  // シートの削除
  const handleDeleteSheet = async (sheetId: string, e?: React.MouseEvent | React.TouchEvent) => {
    // タッチイベントの場合はpreventDefaultを実行
    if (e && 'touches' in e) {
      e.preventDefault();
    }

    // クリックされた削除ボタンを取得
    const target = e?.target as HTMLElement;
    const deleteButton = target.closest('.delete-sheet-button') as HTMLButtonElement;
    
    // Shiftキーが押されている場合はシートを選択してから削除処理を実行
    if (e && 'shiftKey' in e && e.shiftKey) {
      const targetSheet = state.sheets.find(sheet => sheet.id === sheetId);
      if (!targetSheet) return;

      try {
        // まずそのシートを選択する
        await handleSelectSheet(sheetId, e);
        
        // シート選択後に削除処理を実行
        // 現在の状態を保存（アニメーション用）
        const currentPouches = targetSheet.pouches;
        const currentGroups = targetSheet.groups;
        
        // 空の状態を作成
        const emptyPouches: Pouch[] = [];
        const emptyGroups: Group[] = [];
        
        // applyAnimationFlagsを使用してアニメーション用フラグを設定
        const animationResult = HistoryLogic.applyAnimationFlags(
          currentPouches,
          emptyPouches,
          currentGroups,
          emptyGroups
        );
        
        // アクティブシートの場合は即座にアニメーション状態を反映
        if (activeSheetRef.current) {
          activeSheetRef.current.setPouches(animationResult.pouches);
          activeSheetRef.current.setGroups(animationResult.groups);
        }
        
        // 300ms後に最終状態に更新
        setTimeout(async () => {
          // 履歴の処理：削除前の履歴とクリア履歴のみ残す
          const currentHistory = targetSheet.history || [];
          const currentIndex = targetSheet.historyIndex || 0;
          
          // 削除前の履歴（現在の履歴エントリ）を保存
          const beforeDeleteEntry = currentHistory[currentIndex];
          
          // 新しい履歴エントリを作成（クリア後の状態）
          const clearEntry: SheetHistoryEntry = {
            timestamp: new Date(),
            groups: [],
            pouches: [],
            description: getHistoryStorageCopy().dynamic.allClear,
            type: HistoryActionType.DELETE_GROUP,
            targetId: sheetId
          };
          
          // 履歴を再構築：削除前の履歴 + クリア履歴
          const newHistory = beforeDeleteEntry 
            ? [beforeDeleteEntry, clearEntry]
            : [clearEntry];
          
          // シート内の全グループとパウチを削除
          const updatedSheet = {
            ...targetSheet,
            pouches: [],
            groups: [],
            history: newHistory,
            historyIndex: newHistory.length - 1, // 最後のエントリ（クリア履歴）を指す
            lastModified: new Date()
          };

          // シートを更新
          dispatch({ type: 'UPDATE_SHEET', payload: updatedSheet });
          
          // stateRefも同期的に更新
          stateRef.current = {
            ...stateRef.current,
            sheets: stateRef.current.sheets.map(s => 
              s.id === updatedSheet.id ? updatedSheet : s
            )
          };

          // 永続化
          await SheetStorage.saveSheet(updatedSheet);
        }, 300);
      } catch (error) {
        console.error('シート内のグループとパウチの削除に失敗しました:', error);
      }
      return;
    }

    // confirmingクラスがついている場合は削除を実行
    if (deleteButton?.classList.contains('confirming')) {
      try {
        await deleteSheet(sheetId, state.sheets, dispatch);
        
        // 最新のシート数を取得
        const currentSheets = state.sheets.filter(sheet => sheet.id !== sheetId);
        
        // シートがなくなった場合は初期化シートを作成
        if (currentSheets.length === 0) {
          try {
            // 事前読み込み方式で初期化シート（初期パウチ付き）を作成
            const initSheet = await createAndSaveInitializationSheet();
            
            // Reducerに初期化シートを追加
            authorizeNextAddSheet();
            dispatch({ type: 'ADD_SHEET', payload: initSheet });
            dispatch({ type: 'SET_ACTIVE_SHEET', payload: initSheet.id });
            
            return;
          } catch (error) {
            console.error('初期化シート作成でエラーが発生しました:', error);
            return;
          }
        }
        
        // シート数が1になった場合の自動パウチ作成処理を削除
        // 最後のシートを削除して初期シートが作られた場合のみパウチを作成するため
      } catch (error) {
        console.error('シートの削除に失敗しました:', error);
      }
      return;
    }

    // confirmingクラスを付与
    if (deleteButton) {
      deleteButton.classList.add('confirming');
    } else {
      console.error('削除ボタンが見つかりません');
    }
    setTimeout(() => {
      deleteButton?.classList.remove('confirming');
    }, 2000);
  };
  
  // シートの複製
  const handleDuplicateSheet = async (sheetId: string, e?: React.MouseEvent | React.TouchEvent) => {
    // タッチイベントの場合はpreventDefaultを実行
    if (e && 'touches' in e) {
      e.preventDefault();
    }

    // 複製元のシートを取得
    const sourceSheet = state.sheets.find(sheet => sheet.id === sheetId);
    if (!sourceSheet) return;

    if (!(await ensureCanAddSheet())) return;

    try {
      // 新しいシートIDを生成
      const newSheetId = uuidv4();

      // 複製したシートを作成（履歴は除外。レイヤー・帳尻は引き継ぐ）
      const duplicatedSheet: Sheet = {
        id: newSheetId,
        title: `${sourceSheet.title}${t.cloneSuffix}`,
        pouches: sourceSheet.pouches.map(pouch => duplicatePouchRecord(pouch, uuidv4())),
        groups: sourceSheet.groups.map(group => ({
          ...group,
          id: uuidv4(), // 新しいIDを生成
          pouches: [] // パウチIDは後で更新
        })),
        createdAt: new Date(),
        lastModified: new Date(),
        lastPouchValue: sourceSheet.lastPouchValue,
        lastPouchColor: sourceSheet.lastPouchColor,
        lastGroupColor: sourceSheet.lastGroupColor,
        colorLegends: sourceSheet.colorLegends,
        layers: sourceSheet.layers?.map(layer => ({ ...layer })),
        activeLayerId: sourceSheet.activeLayerId,
        balancingPouchId: sourceSheet.balancingPouchId,
        balancingTargets: sourceSheet.balancingTargets
          ? { ...sourceSheet.balancingTargets }
          : undefined,
        balancingTargetTotal: sourceSheet.balancingTargetTotal,
        balancingPreferredSign: sourceSheet.balancingPreferredSign,
        history: [], // 履歴は新規作成
        historyIndex: 0,
        snapshots: [] // スナップショットは後で更新
      };

      // グループ内のパウチIDを更新
      const pouchIdMap = new Map(
        sourceSheet.pouches.map((oldPouch, index) => [
          oldPouch.id,
          duplicatedSheet.pouches[index].id
        ])
      );

      // グループIDのマッピングを作成
      const groupIdMap = new Map(
        sourceSheet.groups.map((oldGroup, index) => [
          oldGroup.id,
          duplicatedSheet.groups[index].id
        ])
      );

      duplicatedSheet.groups = duplicatedSheet.groups.map(group => ({
        ...group,
        pouches: group.pouches.map(oldPouchId => pouchIdMap.get(oldPouchId) || oldPouchId)
      }));

      // スナップショット内のIDも新しいものに更新
      duplicatedSheet.snapshots = (sourceSheet.snapshots || []).map(snapshot => ({
        ...snapshot,
        id: uuidv4(), // スナップショットIDも新規生成
        pouches: snapshot.pouches.map(pouch => {
          const next = duplicatePouchRecord(pouch, pouchIdMap.get(pouch.id) || uuidv4());
          if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
            return {
              ...next,
              referencedGroupId:
                groupIdMap.get(pouch.referencedGroupId) || pouch.referencedGroupId,
            };
          }
          return next;
        }),
        groups: snapshot.groups.map(group => ({
          ...group,
          id: groupIdMap.get(group.id) || uuidv4(),
          pouches: (group.pouches || []).map(oldPouchId => pouchIdMap.get(oldPouchId) || oldPouchId)
        }))
      }));

      // GTPのreferencedGroupIdを更新
      duplicatedSheet.pouches = duplicatedSheet.pouches.map(pouch => {
        if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
          return {
            ...pouch,
            referencedGroupId: groupIdMap.get(pouch.referencedGroupId) || pouch.referencedGroupId
          };
        }
        return pouch;
      });

      if (duplicatedSheet.balancingPouchId) {
        duplicatedSheet.balancingPouchId =
          pouchIdMap.get(duplicatedSheet.balancingPouchId) ?? null;
      }

      const migratedSheet = migrateSheetLayers(duplicatedSheet);

      // シートを追加
      authorizeNextAddSheet();
      dispatch({ type: 'ADD_SHEET', payload: migratedSheet });
      
      // アクティブシートを変更
      dispatch({ type: 'SET_ACTIVE_SHEET', payload: migratedSheet.id });

      // 永続化
      await SheetStorage.saveSheet(migratedSheet);
      await SheetStorage.saveActiveSheetId(migratedSheet.id);

      // タイトル入力にフォーカスを当てて文字列を選択状態にする
      setTimeout(() => {
        const titleInput = document.querySelector(
          `.sheet-item[data-sheet-id="${migratedSheet.id}"] input`
        ) as HTMLInputElement;
        if (titleInput) {
          titleInput.focus();
          titleInput.select(); // 文字列を選択状態にする
        }
      }, 100);

    } catch (error) {
      console.error('シートの複製に失敗しました:', error);
    }
  };

  // 共有メニューを表示する関数
  const showShareMenu = useCallback(async (httpsUrl: string, title: string) => {
    const electron = (window as unknown as {
      electron?: {
        shareUrl?: (url: string, sheetTitle?: string) => Promise<{ ok?: boolean; canceled?: boolean; method?: string }>;
      };
    }).electron;
    if (electron?.shareUrl) {
      try {
        const result = await electron.shareUrl(httpsUrl, title);
        if (result?.canceled || result?.ok) return;
      } catch {
        // fall through to Web Share / clipboard
      }
    }

    if (navigator.share) {
      try {
        await navigator.share({ url: httpsUrl });
        if (!navigator.userAgent.match(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i)) {
          await copyToClipboard(httpsUrl);
        }
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        await copyToClipboard(httpsUrl);
        return;
      }
    }

    await copyToClipboard(httpsUrl);
  }, []);

  // シンプルなクリップボードコピー関数
  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // フォールバック: execCommand
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
    } catch (error) {
      
    }
  };
  // サーバーにファイルをアップロードする関数
  const uploadSheetToServer = async (jsonData: string, fileName: string, isPublic: boolean = false): Promise<string | null> => {
    try {
      // シリアライズされたファイル名を生成（タイムスタンプ + ランダム文字列）
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 15);
      const serializedFileName = `${timestamp}_${randomStr}.pouches`;
      
      // FormDataを作成
      const formData = new FormData();
      const blob = new Blob([jsonData], { type: 'application/pouches' });
      
      // ファイルサイズをログ出力
      const fileSizeMB = (blob.size / 1024 / 1024).toFixed(2);
      console.log(`アップロードファイルサイズ: ${fileSizeMB} MB (${blob.size} bytes)`);
      
      formData.append('file', blob, serializedFileName);
      formData.append('originalFileName', fileName);
      formData.append('isPublic', isPublic.toString());
      
      const apiUrl = getPouchesApiUrl('/api/upload-temp.php');
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        return result.fileName || serializedFileName;
      } else {
        // エラーレスポンスの詳細を取得
        const errorText = await response.text();
        console.error('サーバーアップロードに失敗:', response.statusText);
        console.error('エラー詳細:', errorText);
        return null;
      }
    } catch (error) {
      console.error('サーバーアップロードでエラー:', error);
      return null;
    }
  };

  // エクスポートアクション実行（download / private / public）
  const executeExportAction = useCallback(async (sheetId: string, action: 'download' | 'private' | 'public', existingFilePath?: string) => {
    const targetSheet = state.sheets.find(sheet => sheet.id === sheetId);
    if (!targetSheet) return;
    const fileName = `${targetSheet.title || t.placeholder.noTitle}.pouches`;

    if (action === 'download') {
      const exportData = { sheets: [targetSheet] };
      const jsonData = JSON.stringify(exportData, null, 2);
      const electron = (window as unknown as { electron?: { showSaveDialog: (n?: string) => Promise<{ canceled: boolean; filePath?: string }>; writeFile: (p: string, c: string) => Promise<{ success: boolean }> } }).electron;
      if (electron?.showSaveDialog && electron?.writeFile) {
        let pathToUse = existingFilePath;
        if (!pathToUse) {
          const { canceled, filePath } = await electron.showSaveDialog(fileName);
          pathToUse = filePath;
        }
        if (pathToUse) {
          const result = await electron.writeFile(pathToUse, jsonData);
          if (result.success) activeSheetFilePathRef.current.set(sheetId, pathToUse);
          else alert(t.error.saveFailed);
        }
      } else {
        const blob = new Blob([jsonData], { type: 'application/pouches' });
        const ok = await shareOrDownloadBlob(blob, fileName);
        if (!ok) alert(t.error.saveFailed);
      }
    } else {
      const isPublic = action === 'public';
      const sheetWithoutHistory = { ...targetSheet, history: [], historyIndex: 0 };
      const exportData = { sheets: [sheetWithoutHistory] };
      const jsonData = JSON.stringify(exportData, null, 2);
      const serializedFileName = await uploadSheetToServer(jsonData, fileName, isPublic);
      if (!serializedFileName) {
        alert(t.error.saveFailed);
        return;
      }
      const urlParam = isPublic ? 'import_public' : 'import';
      await showShareMenu(`https://apps.tomippe.jp/pouches/run/?${urlParam}=${serializedFileName}`, targetSheet.title || t.placeholder.noTitle);
    }
  }, [state.sheets, uploadSheetToServer, showShareMenu, t.placeholder.noTitle, t.error.saveFailed]);

  // シートのエクスポート
  const handleExportSheet = async (sheetId: string, e?: React.MouseEvent | React.TouchEvent, directAction?: 'download' | 'private' | 'public') => {
    if (e && 'touches' in e) e.preventDefault();
    const targetSheet = state.sheets.find(sheet => sheet.id === sheetId);
    if (!targetSheet) return;
    try {
      if (directAction) {
        await executeExportAction(sheetId, directAction);
      } else {
        showCustomExportConfirm(targetSheet.title || t.placeholder.noTitle, async (action) => {
          await executeExportAction(sheetId, action);
        });
      }
    } catch (error) {
      console.error('Export failed:', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  // デバウンス待ち・進行中のシート保存をフラッシュ（シート切替・インポート前）
  const flushPendingSheetWrites = useCallback(async (sheetId?: string) => {
    if (pouchUpdateTimeoutRef.current !== null) {
      clearTimeout(pouchUpdateTimeoutRef.current);
      pouchUpdateTimeoutRef.current = null;
    }

    const pending = pendingDebouncedSheetRef.current;
    if (pending && (!sheetId || pending.id === sheetId)) {
      pendingDebouncedSheetRef.current = null;
      pouchSaveGenerationRef.current += 1;
      await SheetStorage.saveSheet(pending);
      dispatch({ type: 'UPDATE_SHEET', payload: pending });
      stateRef.current = {
        ...stateRef.current,
        sheets: stateRef.current.sheets.map((s) => (s.id === pending.id ? pending : s)),
      };
    }

    await (pouchSaveInFlightRef.current ?? Promise.resolve());

    if (sheetId) {
      const sheet = stateRef.current.sheets.find((s) => s.id === sheetId);
      if (sheet) {
        await SheetStorage.saveSheet(sheet);
      }
    }
  }, []);

  // ファイルからシートをインポートする共通処理
  const importSheetFromFile = useCallback(async (file: File, skipDuplicateCheck = false, skipBroadcast = false, filePath?: string) => {
    const importId = `IMPORT_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const priorActiveId = stateRef.current.activeSheetId;
    if (priorActiveId) {
      await flushPendingSheetWrites(priorActiveId);
    }

    const sheetCount = stateRef.current.sheets.length;
    const status = await entitlementService.refresh(sheetCount);
    setEntitlementStatus(status);
    if (await blockAddSheetWithPaywall(sheetCount, status)) return;
    
    try {
      
      
      // ファイルの内容を読み取り
      const text = await file.text();
      
      
      // 重複チェック（BroadcastChannel経由の場合のみ）
      if (!skipDuplicateCheck) {
        
        // ファイル内容のハッシュ生成（簡易版）
        let hash = 0;
        for (let i = 0; i < text.length; i++) {
          const char = text.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash; // 32bit整数に変換
        }
        
        // 重複インポート防止：ファイル内容ベースで判定
        const contentKey = `import_content_${hash}`;
        const fileKey = `import_${file.name}_${file.size}_${file.lastModified}`;
        const lastImportTime = sessionStorage.getItem(contentKey) || sessionStorage.getItem(fileKey);
        const currentTime = Date.now();
        
        
        
        
        
        
        
        if (lastImportTime && (currentTime - parseInt(lastImportTime)) < 5000) { // 5秒の重複防止
          const timeDiff = currentTime - parseInt(lastImportTime);
          return;
        }
        
        // インポート時刻を記録（両方のキーで）
        sessionStorage.setItem(contentKey, currentTime.toString());
        sessionStorage.setItem(fileKey, currentTime.toString());
        
        
      }
      
      // 既に読み取り済みのテキストを使用
      const importData = JSON.parse(text);

      // データ構造の検証
      if (!importData.sheets || !Array.isArray(importData.sheets) || importData.sheets.length === 0) {
        console.error(`[${importId}] 無効なファイル形式`);
        alert(t.error.invalidFileFormat);
        return;
      }

      // 最初のシートを取得
      const sourceSheet = importData.sheets[0];
      
      
      // ファイル名から拡張子を除いたシート名を取得
      const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
      
      // 新しいシートIDを生成
      const newSheetId = uuidv4();

      // インポートしたシートを作成（新しいIDで）
      const importedSheet: Sheet = {
        ...sourceSheet,
        id: newSheetId,
        title: fileNameWithoutExtension, // ファイル名（拡張子除く）をシート名に設定
        pouches: sourceSheet.pouches.map((pouch: any) => ({
          ...pouch,
          id: uuidv4() // 新しいIDを生成
        })),
        groups: sourceSheet.groups.map((group: any) => ({
          ...group,
          id: uuidv4(), // 新しいIDを生成
          pouches: [] // パウチIDは後で更新
        })),
        createdAt: new Date(),
        lastModified: new Date(),
        history: [], // 後で更新
        historyIndex: 0,
        snapshots: [] // 後で更新
      };

      // グループ内のパウチIDを更新
      const pouchIdMap = new Map(
        sourceSheet.pouches.map((oldPouch: any, index: number) => [
          oldPouch.id,
          importedSheet.pouches[index].id
        ])
      );

      const groupIdMap = new Map<string, string>(
        sourceSheet.groups.map((oldGroup: any, index: number) => [
          oldGroup.id,
          importedSheet.groups[index].id
        ])
      );

      importedSheet.groups = importedSheet.groups.map((group, index) => ({
        ...group,
        pouches: (sourceSheet.groups[index].pouches || []).map((oldPouchId: string) => pouchIdMap.get(oldPouchId) || oldPouchId)
      }));

      // GTPのreferencedGroupIdを更新
      importedSheet.pouches = importedSheet.pouches.map((pouch): Pouch => {
        if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
          const newReferencedGroupId: string | undefined = groupIdMap.get(pouch.referencedGroupId);
          return {
            ...pouch,
            referencedGroupId: newReferencedGroupId ? newReferencedGroupId : pouch.referencedGroupId
          };
        }
        return pouch;
      });

      // 履歴内のIDも新しいものに更新
      importedSheet.history = (sourceSheet.history || []).map((entry: any) => ({
        ...entry,
        timestamp: new Date(entry.timestamp),
        pouches: entry.pouches.map((pouch: any) => {
          const newPouchId = pouchIdMap.get(pouch.id);
          const newReferencedGroupId = pouch.isGroupTotalPouch && pouch.referencedGroupId
            ? (groupIdMap.get(pouch.referencedGroupId) || pouch.referencedGroupId)
            : pouch.referencedGroupId;
          return {
            ...pouch,
            id: newPouchId ? newPouchId : pouch.id,
            referencedGroupId: newReferencedGroupId
          };
        }),
        groups: entry.groups.map((group: any) => ({
          ...group,
          id: groupIdMap.get(group.id) || group.id,
          pouches: (group.pouches || []).map((oldPouchId: string) => pouchIdMap.get(oldPouchId) || oldPouchId)
        }))
      }));
      
      // historyIndexも保持
      importedSheet.historyIndex = Math.min(sourceSheet.historyIndex || 0, importedSheet.history.length - 1);
      if (importedSheet.historyIndex < 0 && importedSheet.history.length > 0) {
        importedSheet.historyIndex = 0;
      }

      // スナップショット内のIDも新しいものに更新
      importedSheet.snapshots = (sourceSheet.snapshots || []).map((snapshot: any) => ({
        ...snapshot,
        id: uuidv4(), // スナップショットIDも新規生成
        timestamp: new Date(snapshot.timestamp),
        pouches: snapshot.pouches.map((pouch: any) => {
          const newPouchId = pouchIdMap.get(pouch.id);
          const newReferencedGroupId = pouch.isGroupTotalPouch && pouch.referencedGroupId
            ? (groupIdMap.get(pouch.referencedGroupId) || pouch.referencedGroupId)
            : pouch.referencedGroupId;
          return {
            ...pouch,
            id: newPouchId ? newPouchId : uuidv4(),
            referencedGroupId: newReferencedGroupId
          };
        }),
        groups: snapshot.groups.map((group: any) => ({
          ...group,
          id: groupIdMap.get(group.id) || uuidv4(),
          pouches: (group.pouches || []).map((oldPouchId: string) => pouchIdMap.get(oldPouchId) || oldPouchId)
        }))
      }));

      // レイヤー未搭載の .pouches / URL 取り込み: value をレイヤー1(primary) の layerValues へ
      const migratedSheet = migrateSheetLayers(importedSheet);

      authorizeNextAddSheet();
      dispatch({ type: 'ADD_SHEET', payload: migratedSheet });

      dispatch({ type: 'SET_ACTIVE_SHEET', payload: migratedSheet.id });

      if (filePath) activeSheetFilePathRef.current.set(migratedSheet.id, filePath);

      await SheetStorage.saveSheet(migratedSheet);
      
      await SheetStorage.saveActiveSheetId(migratedSheet.id);

      // タイトル入力にフォーカスを当てて全選択
      setTimeout(() => {
        const titleInput = document.querySelector(
          `.sheet-item[data-sheet-id="${migratedSheet.id}"] input`
        ) as HTMLInputElement;
        if (titleInput) {
          titleInput.focus();
          titleInput.select(); // テキストを全選択
        }
      }, 100);

      

    } catch (error) {
      console.error(`[${importId}] importSheetFromFileエラー:`, error);
      console.error('ファイルの読み込みに失敗しました:', error);
      alert(t.error.invalidFileFormat);
    }
  }, [t, flushPendingSheetWrites]);

  // インポート実行関数（重複実行を防ぐため外部に定義）
  const executeImport = useCallback(async (file: File, serverFilePath: string, skipWindowClose: boolean = false, skipBroadcast: boolean = false) => {
    
    
    // より厳密な重複実行防止のチェック（ファイル内容も含む）
    const fileContent = await file.text();
    const contentHash = fileContent.substring(0, 100); // 最初の100文字でハッシュ的にチェック
    const importKey = `${file.name}_${file.lastModified}_${file.size}_${contentHash.length}`;
    const existingImport = sessionStorage.getItem(`importing_${importKey}`);
    if (existingImport) {
      
      return;
    }
    
    // インポート開始フラグを設定
    sessionStorage.setItem(`importing_${importKey}`, Date.now().toString());
    
    
    // インポートボタンからの実行時はBroadcastChannel通知を抑制するフラグを設定
    if (skipBroadcast) {
      
      sessionStorage.setItem('skip_broadcast_refresh', 'true');
    }
    
    try {
      // 既に読み込んだファイル内容から新しいFileオブジェクトを作成
      const processFile = new File([fileContent], file.name, { type: file.type, lastModified: file.lastModified });
      
      // 既存ウィンドウがあるかチェック
      const existingWindows = await checkForExistingWindows();
      
      if (existingWindows.length > 0) {
        // 既存ウィンドウにファイルを送信
        const success = await sendFileToExistingWindow(processFile, existingWindows[0]);
        
        if (success) {
          // インポート成功後、Private Shareの場合のみ15分後にサーバーファイルを削除
          // Public Share（import_public）の場合は削除しない
          if (!serverFilePath.includes('/import_public/')) {
            setTimeout(async () => {
              
              await fetch(getDeleteTempFileUrl(serverFilePath), {
                method: 'GET'
              });
            }, 900000); // 15分 = 900,000ms
          }
          
          // ファイルのダブルクリック時のみウィンドウを閉じる
          // 他の場合は閉じない
        } else {
          // 送信に失敗した場合は現在のウィンドウで処理
          await importSheetFromFile(processFile, true, skipBroadcast); // 重複チェックとBroadcast通知をスキップ
          
          // インポート成功後、Private Shareの場合のみ15分後にサーバーファイルを削除
          // Public Share（import_public）の場合は削除しない
          if (!serverFilePath.includes('/import_public/')) {
            setTimeout(async () => {
              
              await fetch(getDeleteTempFileUrl(serverFilePath), {
                method: 'GET'
              });
            }, 900000); // 15分 = 900,000ms
          }
        }
      } else {
        // 新しいウィンドウまたは現在のウィンドウで処理
        await importSheetFromFile(processFile, true, skipBroadcast); // 重複チェックとBroadcast通知をスキップ
        
        // インポート成功後、Private Shareの場合のみ15分後にサーバーファイルを削除
        // Public Share（import_public）の場合は削除しない
        if (!serverFilePath.includes('/import_public/')) {
          setTimeout(async () => {
            
            await fetch(getDeleteTempFileUrl(serverFilePath), {
              method: 'GET'
            });
          }, 900000); // 15分 = 900,000ms
        }
      }
    } finally {
      // インポート完了後にフラグを削除
      sessionStorage.removeItem(`importing_${importKey}`);
      if (skipBroadcast) {
        sessionStorage.removeItem('skip_broadcast_refresh');
        
      }
      
    }
  }, []);


  // インポートコードまたはURLからインポートする関数
  const handleImportFromCode = async (input: string) => {
    try {
      let fileName = '';
      
      // フルURLの場合（https://apps.tomippe.jp/pouches/run/?import=xxx.pouches）
      let isPublicImport = false;
      if (input.startsWith('https://') || input.startsWith('http://')) {
        const url = new URL(input);
        const importFileParam = url.searchParams.get('import');
        const importPublicParam = url.searchParams.get('import_public');
        if (importFileParam) {
          fileName = importFileParam;
        } else if (importPublicParam) {
          fileName = importPublicParam;
          isPublicImport = true;
        } else {
          alert(t.error.invalidUrl);
          return;
        }
      } 
      // インポートコードのみの場合（xxx.pouches または xxx）
      else {
        fileName = input.endsWith('.pouches') ? input : `${input}.pouches`;
      }
      
      
      
      // ファイル名からシート名を推定してタイトルを変更
      const sheetName = fileName.replace('.pouches', '');
      document.title = `${sheetName} - POUCHES`;
      
      // メタディスクリプションを更新
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', t.meta.importSheet(sheetName));
      }
      
      // サーバーファイルのフルパスを構築（import_publicパラメータがある場合は公開フォルダから）
      const fullImportUrl = isPublicImport 
        ? `https://apps.tomippe.jp/pouches/import_public/${fileName}`
        : `https://apps.tomippe.jp/pouches/import/${fileName}`;
      
      // サーバーからファイルを取得してインポート（importボタンからは確認スキップ、ウィンドウクローズもスキップ、Broadcast通知もスキップ）
      await importFromServerFile(fullImportUrl, true, true, true);
      
    } catch (error) {
      console.error('[Import Code] エラー:', error);
      alert(t.error.importFailed);
    }
  };

  // カスタムプロンプトの状態管理
  const [showImportPrompt, setShowImportPrompt] = useState(false);
  const [importInputValue, setImportInputValue] = useState('');
  
  // カスタム確認ダイアログの状態管理
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [confirmSheetTitle, setConfirmSheetTitle] = useState('');
  const [confirmCallback, setConfirmCallback] = useState<(() => void) | null>(null);
  const [confirmCancelCallback, setConfirmCancelCallback] = useState<(() => void) | null>(null);

  // エクスポート確認ダイアログの状態管理
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [exportSheetTitle, setExportSheetTitle] = useState('');
  const [exportCallback, setExportCallback] = useState<((action: 'download' | 'private' | 'public') => void) | null>(null);

  // シートのインポート（ボタンクリック）
  const handleImportSheet = async (e?: React.MouseEvent | React.TouchEvent) => {
    // タッチイベントの場合はpreventDefaultを実行
    if (e && 'touches' in e) {
      e.preventDefault();
    }

    // PWA/Electron 共通：カスタムプロンプトを表示（URL入力 or ファイル選択）
    setImportInputValue('');
    setShowImportPrompt(true);
  };

  // カスタムプロンプトでOKが押された時の処理
  const handleImportPromptOK = async () => {
    setShowImportPrompt(false);

    if (importInputValue.trim() === '') {
      // 空の場合はファイルダイアログを開く
      const electron = (window as unknown as { electron?: { showOpenDialog: () => Promise<{ canceled: boolean; filePaths: string[] }>; readFile: (p: string) => Promise<{ success: boolean; content?: string; fileName?: string }> } }).electron;
      if (electron?.showOpenDialog) {
        try {
          const { canceled, filePaths } = await electron.showOpenDialog();
          if (!canceled && filePaths?.[0]) {
            const result = await electron.readFile(filePaths[0]);
            if (result.success && result.content) {
              const file = new File([result.content], result.fileName || 'imported.pouches', { type: 'application/pouches' });
              await handleUnifiedFileImport(file, true, filePaths[0]);
            } else {
              alert(t.error.readFileFailed);
            }
          }
        } catch (err) {
          console.error('Import failed:', err);
          alert(t.error.importFailed);
        }
      } else {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pouches';
        input.onchange = async (event) => {
          const file = (event.target as HTMLInputElement).files?.[0];
          if (!file) return;
          await handleUnifiedFileImport(file, true);
        };
        input.click();
      }
    } else {
      await handleImportFromCode(importInputValue.trim());
    }
  };

  // カスタムプロンプトでキャンセルが押された時の処理
  const handleImportPromptCancel = () => {
    setShowImportPrompt(false);
    setImportInputValue('');
  };

  // カスタム確認ダイアログのハンドラー
  const showCustomConfirm = (title: string, callback: () => void, cancelCallback?: () => void) => {
    setConfirmSheetTitle(title);
    setConfirmCallback(() => callback);
    setConfirmCancelCallback(() => cancelCallback ?? null);
    setShowImportConfirm(true);
  };

  // カスタムエクスポート確認ダイアログを表示する関数
  const showCustomExportConfirm = (title: string, callback: (action: 'download' | 'private' | 'public') => void) => {
    setExportSheetTitle(title);
    setExportCallback(() => callback);
    setShowExportConfirm(true);
  };

  const handleConfirmOK = () => {
    setShowImportConfirm(false);
    if (confirmCallback) {
      confirmCallback();
    }
    setConfirmCallback(null);
    setConfirmCancelCallback(null);
  };

  const handleConfirmCancel = () => {
    setShowImportConfirm(false);
    if (confirmCancelCallback) {
      confirmCancelCallback();
    }
    setConfirmCallback(null);
    setConfirmCancelCallback(null);
  };

  const handleExportAction = (action: 'download' | 'private' | 'public', e?: React.MouseEvent | React.TouchEvent) => {
    if (e && 'touches' in e) e.preventDefault();
    if (exportCallback) {
      exportCallback(action);
    }
    setShowExportConfirm(false);
    setExportCallback(null);
  };

  const handleExportCancel = () => {
    setShowExportConfirm(false);
    setExportCallback(null);
  };


  
  // シート全体の統合更新処理（パウチとグループを一度に更新）
  const handleUpdateActiveSheet = useCallback(async (
    pouches: Pouch[] | null,
    groups: Group[] | null,
    isCritical: boolean = false,
    saveToDb: boolean = true,
    sheetParams?: Partial<Sheet>,
    historyActionType?: HistoryActionType,
    forceNewEntry?: boolean,
    sourceSheetId?: string,
    /** キーボード新規キャンセル等: 直近が該当パウチの Add Pouch ならその履歴エントリも削除 */
    stripAddPouchHistoryForPouchId?: string
  ) => {
    if (!activeSheet) {
      isUpdatingActiveSheetRef.current = false;
      return;
    }
    
    // 並行実行を防ぐ（重要更新は進行中の保存完了後に再試行）
    if (isUpdatingActiveSheetRef.current) {
      if (isCritical) {
        await (pouchSaveInFlightRef.current ?? Promise.resolve());
      } else {
        isUpdatingActiveSheetRef.current = false;
        return;
      }
    }
    
    // 通常の処理を継続（亡霊検出は削除）

    isUpdatingActiveSheetRef.current = true;
    
    try {
      // 最新のstateをrefから取得
      const currentState = stateRef.current;
      
      // 最新のstateからactiveSheetを取得して比較
      let latestActiveSheet = currentState.sheets.find(s => s.id === activeSheet.id) || activeSheet;
      if (sheetParams?.layers) {
        latestActiveSheet = { ...latestActiveSheet, layers: sheetParams.layers };
      }
      
      // 最新の状態を取得（最新のactiveSheetを使用）
      let updatedPouches = pouches ? [...pouches] : latestActiveSheet.pouches;
      let updatedGroups = groups ? [...groups] : latestActiveSheet.groups;
      
      
      // 削除中(_isDeleting)のパウチを処理
      const deletingPouches = updatedPouches.filter(p => (p as any)._isDeleting);
      if (deletingPouches.length > 0) {
        
        // 再確認: 削除フラグが確実に設定されていることを確認
        const uiPouches = updatedPouches;
        
        // DB保存用の配列（削除中フラグ付きのパウチを除外）
        const dbPouches = updatedPouches.filter(p => !(p as any)._isDeleting);
        // まずUIを更新して削除アニメーションを開始
        dispatch({ 
          type: 'SET_ACTIVE_SHEET_POUCHES', 
          payload: uiPouches 
        });
        
        // 削除アニメーション後に実際に削除
        setTimeout(() => {
          
          // 400ms後にUIからも削除
          dispatch({ 
            type: 'SET_ACTIVE_SHEET_POUCHES', 
            payload: dbPouches 
          });
        }, 400);
        
        // DB保存用には削除済みの配列を使用（ただし、UI更新は400ms後に実行されるため、ここでは更新しない）
        // updatedPouches = dbPouches; // 即座に削除されるのを防ぐためコメントアウト
      }
      
      // 追加中(_isAdding)のグループを処理
      const addingGroups = updatedGroups.filter(g => (g as any)._isAdding);
      if (addingGroups.length > 0) {
        // アニメーション後にフラグをリセット
        const timerId = setTimeout(() => {
          const groupsWithoutAddingFlag = updatedGroups.map(g => 
            (g as any)._isAdding ? { ...g, _isAdding: false } : g
          );
          if (activeSheetRef.current) {
            activeSheetRef.current.setGroups(groupsWithoutAddingFlag);
          }
        }, 300);
      }
      
      // 削除中(_isDeleting)のグループを処理（ActiveSheetComponentで処理済みの場合はスキップ）
      const deletingGroups = updatedGroups.filter(g => (g as any)._isDeleting);
      if (deletingGroups.length > 0) {
        // 通常の削除処理（Shift+Merge等）の場合は、App.tsxでタイマー処理
        setTimeout(() => {
          const groupsWithoutDeleted = updatedGroups.filter(g => !(g as any)._isDeleting);
          dispatch({ 
            type: 'SET_ACTIVE_SHEET_GROUPS', 
            payload: groupsWithoutDeleted 
          });
        }, 300);
        
        // DB保存用には削除済みの配列を使用
        updatedGroups = updatedGroups.filter(g => !(g as any)._isDeleting);
      }
      
      // ジオメトリ（=ラベル/内包/境界キャッシュに影響する要素）が変化したかを一度だけ算出して共有する。
      // 値・乗数・符号の変化はここに含めない（それらはキャッシュ無効化に無関係）。
      let __geometryChangedMemo: boolean | undefined;
      const computeGeometryChanged = (): boolean => {
        if (__geometryChangedMemo !== undefined) return __geometryChangedMemo;
        let changed = false;
        if (pouches) {
          const orig = latestActiveSheet.pouches;
          if (updatedPouches.length !== orig.length) {
            changed = true;
          } else {
            for (const p of updatedPouches) {
              const o = orig.find(x => x.id === p.id);
              if (!o || o.x !== p.x || o.y !== p.y || (o as any)._isDeleting !== (p as any)._isDeleting) {
                changed = true;
                break;
              }
            }
          }
        }
        if (!changed && groups) {
          const og = latestActiveSheet.groups;
          if (updatedGroups.length !== og.length) {
            changed = true;
          } else {
            for (const g of updatedGroups) {
              const o = og.find(x => x.id === g.id);
              if (!o || o.path !== g.path || o.color !== g.color) {
                changed = true;
                break;
              }
              const op = o.pouches || [];
              const np = g.pouches || [];
              if (op.length !== np.length) {
                changed = true;
                break;
              }
              for (let i = 0; i < op.length; i++) {
                if (op[i] !== np[i]) {
                  changed = true;
                  break;
                }
              }
              if (changed) break;
            }
          }
        }
        __geometryChangedMemo = changed;
        return changed;
      };

      if (pouches) {
        // 通常の更新（削除中パウチがない場合）
        // パウチの移動フラグをログに出力（デバッグ用）
        const movedPouches = pouches.filter(p => (p as any)._isMoving);
        
        // shouldShowTotalLabel / isGroupPhysicallyContained / getPathBounds の各キャッシュは
        // パウチの「値・乗数・符号」には依存しない（物理位置・グループ path・所属・mute 色のみに依存）。
        // 値編集のたびに全消去すると、グループ数 G に比例して TOTAL ラベル判定が再計算され重くなるため、
        // ジオメトリ（位置・path・所属・色・増減）が実際に変化したときだけクリアする。
        if (computeGeometryChanged()) {
          clearGroupLogicCache();
        }
        
        // 削除中パウチがない場合のみパウチの状態を更新
        if (deletingPouches.length === 0) {
          dispatch({ 
            type: 'SET_ACTIVE_SHEET_POUCHES', 
            payload: updatedPouches 
          });
        }
      }
      
      // グループ更新時は、常にパウチとグループの関係を更新する
      // undo/redo中（html.undoredoクラスが設定されている間）はz-index再計算をスキップ
      const isUndoRedoAnimation = document.documentElement.classList.contains('undoredo');
      if (!isUndoRedoAnimation && pouches) {
        updatedGroups = syncGroupMembershipFromPouchPositions(
          updatedPouches,
          updatedGroups,
        );
      }
      if ((groups || pouches) && !isUndoRedoAnimation) {
        
        // GTP作成時はキャッシュをクリアしない（パウチ位置が元に戻るのを防ぐ）
        const isGTPCreation = pouches && pouches.length > latestActiveSheet.pouches.length && 
          pouches.some(p => p.isGroupTotalPouch && !latestActiveSheet.pouches.some(ap => ap.id === p.id));
        
        if (!isGTPCreation && computeGeometryChanged()) {
          // グループのジオメトリ（path・所属・色・増減）またはパウチ位置が変化した場合のみキャッシュをクリア。
          // 値編集のみのときはラベル/内包判定は不変なのでクリアしない（再計算コスト削減）。
          clearGroupLogicCache();
          
          // グループリサイズ時は特に境界計算のキャッシュをクリア
          clearGroupResizeCache();
        }
        
        // Z-indexを再計算
        const { getPathBounds } = await import('./logic/GroupLogic');
        
        // 全グループのboundsを事前計算
        const groupBoundsMap = new Map();
        updatedGroups.forEach(group => {
          groupBoundsMap.set(group.id, getPathBounds(group.path));
        });

        // ミュートグループの事前抽出とbounds計算
        const muteGroups = updatedGroups.filter(g => g.color === 'mute');
        const muteBoundsMap = new Map();
        muteGroups.forEach(group => {
          muteBoundsMap.set(group.id, getPathBounds(group.path));
        });


        // パウチ位置 → グループ所属は上で同期済み

        // 各グループのミュート内包フラグを更新
        updatedGroups = updatedGroups.map(group => {
          const bounds = groupBoundsMap.get(group.id);
          let isInMuteGroup = false;

          if (bounds && muteGroups.length > 0) {
            for (const muteGroup of muteGroups) {
              const muteBounds = muteBoundsMap.get(muteGroup.id);
              if (muteBounds) {
                if (
                  bounds.left >= muteBounds.left &&
                  bounds.right <= muteBounds.right &&
                  bounds.top >= muteBounds.top &&
                  bounds.bottom <= muteBounds.bottom
                ) {
                  isInMuteGroup = true;
                  break;
                }
              }
            }
          }

          debouncedLog();

          if (group._isInMuteGroup === isInMuteGroup) {
            return group;
          }
          return { ...group, _isInMuteGroup: isInMuteGroup };
        });
          
        // グループの状態を更新
        dispatch({ 
          type: 'SET_ACTIVE_SHEET_GROUPS', 
          payload: updatedGroups 
        });
      }

      // --- 帳尻パウチ（Balancing Pouch）再計算 ---
      let balancingPouchId = latestActiveSheet.balancingPouchId ?? null;
      const bpLayerId = resolveActiveLayerId(latestActiveSheet);
      let sheetForBpMeta = latestActiveSheet;
      let balancingTargetTotal = getBalancingTargetForLayer(latestActiveSheet, bpLayerId);
      let balancingPreferredSign = latestActiveSheet.balancingPreferredSign;

      if (balancingPouchId) {
        const ensured = ensureBalancingTargetForLayer(
          sheetForBpMeta,
          bpLayerId,
          updatedPouches,
          updatedGroups
        );
        sheetForBpMeta = ensured.sheet;
        if (balancingTargetTotal === undefined) {
          balancingTargetTotal = ensured.target;
        }

        const bpPouch = updatedPouches.find(p => p.id === balancingPouchId);

        if (!bpPouch || bpPouch._isDeleting) {
          // BP パウチが削除された → BP をクリア
          balancingPouchId = null;
          balancingTargetTotal = undefined;
          balancingPreferredSign = undefined;
        } else if (
          historyActionType === HistoryActionType.MERGE_POUCH ||
          historyActionType === HistoryActionType.SPLIT_POUCH
        ) {
          // マージ・分解発生 → BP をオフにする
          balancingPouchId = null;
          balancingTargetTotal = undefined;
          balancingPreferredSign = undefined;
        } else if (balancingTargetTotal !== undefined) {
          // 他パウチの変更 → BP の value を自動調整
          updatedPouches = syncBalancingPouchTimeMode(
            updatedPouches,
            updatedGroups,
            balancingPouchId,
            { ...sheetForBpMeta, pouches: updatedPouches }
          );
          updatedPouches = applyBalancingPouch(
            updatedPouches,
            updatedGroups,
            balancingPouchId,
            balancingTargetTotal,
            balancingPreferredSign
          );
          updatedPouches = syncBalancingPouchToActiveLayer(
            updatedPouches,
            balancingPouchId,
            { ...sheetForBpMeta, pouches: updatedPouches }
          );

          // UI への即時反映
          if (deletingPouches.length === 0) {
            dispatch({ type: 'SET_ACTIVE_SHEET_POUCHES', payload: updatedPouches });
          }
        }
      }

      // MOVE_POUCH でBP状態を移動先シートに引き継ぐ
      if (historyActionType === HistoryActionType.MOVE_POUCH && sourceSheetId) {
        const srcSheet = stateRef.current.sheets.find(s => s.id === sourceSheetId);
        if (srcSheet?.balancingPouchId) {
          const newPouch = updatedPouches.find(p =>
            !latestActiveSheet.pouches.some(op => op.id === p.id)
          );
          if (newPouch && newPouch.id === srcSheet.balancingPouchId) {
            balancingPouchId = newPouch.id;
            balancingTargetTotal = calculateSheetTotal(updatedPouches, updatedGroups);
            balancingPreferredSign = newPouch.value >= 0 ? 1 : -1;
          }
        }
      }

      // stateRef の古い balancingPouchId で上書きされないよう、パウチ一覧と突き合わせて確定
      const refSheetAfterBp = stateRef.current.sheets.find(s => s.id === activeSheet.id);
      const bpReconciled = reconcileBalancingAfterSheetUpdate(
        balancingPouchId,
        balancingTargetTotal,
        balancingPreferredSign,
        updatedPouches,
        refSheetAfterBp
      );
      balancingPouchId = bpReconciled.balancingPouchId;
      balancingTargetTotal = bpReconciled.balancingTargetTotal;
      balancingPreferredSign = bpReconciled.balancingPreferredSign;

      let balancingTargetsPatch: Sheet['balancingTargets'] = sheetForBpMeta.balancingTargets;
      if (balancingPouchId && balancingTargetTotal !== undefined) {
        const withTargets = withBalancingTargetForLayer(
          { ...sheetForBpMeta, balancingPouchId, balancingTargetTotal, balancingPreferredSign },
          bpLayerId,
          balancingTargetTotal
        );
        balancingTargetsPatch = withTargets.balancingTargets;
      } else if (!balancingPouchId) {
        balancingTargetsPatch = undefined;
      }

      if (
        historyActionType === HistoryActionType.EDIT_POUCH_VALUE &&
        pouches != null
      ) {
        const sheetForLog = syncBalancingTargetTotalToActiveLayer({
          ...latestActiveSheet,
          activeLayerId: resolveActiveLayerId(latestActiveSheet),
          pouches: updatedPouches,
          balancingPouchId,
          balancingTargetTotal,
          balancingPreferredSign,
          balancingTargets: balancingTargetsPatch,
        });
        logSheetPouchValues('pouch-value:after', sheetForLog, updatedPouches);
      }

      // 重要な更新があるかチェック（パウチの場合）
      let hasCriticalUpdates = isCritical;
      const criticalUpdateReason: string[] = [];
      
      if (isCritical) {
        criticalUpdateReason.push('明示的なisCriticalフラグ');
      }
      
      if (pouches) {
        const hasPouchStateFlags = pouches.some(p => 
          (p as any)._isDragging || 
          // (p as any)._animateMultiplier ||
          // (p as any)._animateSignChange ||
          (p as any)._isMoving
        );
        
        if (hasPouchStateFlags) {
          hasCriticalUpdates = true;
          
          if (pouches.some(p => (p as any)._isDragging)) criticalUpdateReason.push('_isDragging');
          // if (pouches.some(p => (p as any)._animateMultiplier)) criticalUpdateReason.push('_animateMultiplier');
          // if (pouches.some(p => (p as any)._animateSignChange)) criticalUpdateReason.push('_animateSignChange');
          if (pouches.some(p => (p as any)._isMoving)) criticalUpdateReason.push('_isMoving');
        }
      }
      
      // DBに保存しない場合は、pouches/groupsのみ更新して終了
      if (!saveToDb) {
        // stateRef.currentから最新シートを取得（クロージャのstateは古い可能性がある）
        const currentSheet = stateRef.current.sheets.find(s => s.id === activeSheet.id);
        if (!currentSheet) return;
        
        const updatedSheet = syncBalancingTargetTotalToActiveLayer({
          ...currentSheet,
          pouches: updatedPouches,
          groups: updatedGroups,
          balancingPouchId,
          balancingTargetTotal,
          balancingPreferredSign,
          balancingTargets: balancingTargetsPatch,
        });
        
        // 既存のsetTimeoutをクリア（古いデータで上書きされるのを防ぐ）
        if (pouchUpdateTimeoutRef.current) {
          clearTimeout(pouchUpdateTimeoutRef.current);
          pouchUpdateTimeoutRef.current = null;
        }
        
        dispatch({ type: 'UPDATE_SHEET', payload: updatedSheet });
        
        // stateRefも同期的に更新（useEffectのstateRef同期前に別の操作が走った場合の汚染を防ぐ）
        stateRef.current = {
          ...stateRef.current,
          sheets: stateRef.current.sheets.map(s =>
            s.id === updatedSheet.id ? updatedSheet : s
          )
        };
        
        isUpdatingActiveSheetRef.current = false;
        return;
      }
      
      // シートメタデータの更新検出（グループカラー、パウチカラー、パウチ値）
      let newLastGroupColor = latestActiveSheet.lastGroupColor;
      let newLastPouchColor = latestActiveSheet.lastPouchColor;
      let newLastPouchValue = latestActiveSheet.lastPouchValue;
      
      // 1. グループカラー変更の検出
      if (groups) {
        // 更新があったグループのうち、色が変更されたものを探す
        const colorChangedGroup = updatedGroups.find(group => {
          // 元のグループを見つける
          const originalGroup = latestActiveSheet.groups.find(g => g.id === group.id);
          // 色が変更されたかチェック
          return originalGroup && originalGroup.color !== group.color;
        });
        
        // 色が変更されたグループがあれば、そのカラーをlastGroupColorとして使用
        if (colorChangedGroup) {
          
          newLastGroupColor = colorChangedGroup.color;
        }
      }
      
      // 2. パウチカラー変更の検出
      if (pouches) {
        // カラー変更されたパウチを探す
        const colorChangedPouch = updatedPouches.find(pouch => {
          // 元のパウチを見つける
          const originalPouch = latestActiveSheet.pouches.find(p => p.id === pouch.id);
          // 色が変更されたかチェック
          return originalPouch && originalPouch.color !== pouch.color;
        });
        
        // 色が変更されたパウチがあれば、そのカラーをlastPouchColorとして使用（GTPは除外）
        if (colorChangedPouch && !colorChangedPouch.isGroupTotalPouch) {
          
          newLastPouchColor = colorChangedPouch.color;
        }
        
        // 3. 新規作成パウチがあれば、その値とカラーを保存（GTPは除外、GTPの自動更新時も除外）
        const newPouch = updatedPouches.find(pouch => 
          !latestActiveSheet.pouches.some(op => op.id === pouch.id) && !pouch.isGroupTotalPouch
        );
        
        if (newPouch && historyActionType) {
          
          newLastPouchColor = newPouch.color;
          newLastPouchValue = newPouch.value;
        }
      }
      
      // colorLegendsの初期化チェック（ActiveSheetComponentからの移行）
      // 注意: stateRef.currentの更新はuseEffectで非同期に行われるため、
      // handleUpdateColorLegendで更新した直後にパウチ操作をすると古いデータを参照してしまう問題がある
      // そのため、latestActiveSheetではなくactiveSheet（useCallbackの依存配列で最新が保証される）を優先使用
      let initialColorLegends = activeSheet.colorLegends || latestActiveSheet.colorLegends;
      
      // sheetParamsパラメータがある場合は、それを適用する
      if (sheetParams && sheetParams.colorLegends) {
        initialColorLegends = sheetParams.colorLegends;
      }
      // sheetParamsがない場合で、colorLegendsが空の場合は、POUCH_COLORSに基づいて初期化
      else if (!initialColorLegends || initialColorLegends.length === 0) {
        // colorLegendsが空の場合は、POUCH_COLORSに基づいて初期化
        initialColorLegends = POUCH_COLORS.map(color => ({
          colorId: color.id,
          legend: ''
        }));
      }
      
      // sheetParamsパラメータがある場合は、その他のパラメータも適用する
      if (sheetParams) {
        if (sheetParams.lastGroupColor) {
          newLastGroupColor = sheetParams.lastGroupColor;
        }
        if (sheetParams.lastPouchColor) {
          newLastPouchColor = sheetParams.lastPouchColor;
        }
        if (sheetParams.lastPouchValue) {
          newLastPouchValue = sheetParams.lastPouchValue;
        }
      }
      
      // historyActionTypeがある場合は履歴エントリを追加
      // 最新のactiveSheetを使用（古い参照を避ける）
      const currentActiveSheet = latestActiveSheet;
      let updatedSheetWithHistory = { ...currentActiveSheet };
      if (historyActionType) {
        // 履歴作成処理開始 - in-actionクラスを追加
        isInHistoryActionRef.current = true;
        document.documentElement.classList.add('in-action');
        
        // 前回のタイマーをクリア
        if (historyActionTimerRef.current !== null) {
          clearTimeout(historyActionTimerRef.current);
          historyActionTimerRef.current = null;
        }
        
        const { HistoryLogic } = await import('./utils/HistoryLogic');
        
        // 説明文を生成
        const storageHistory = getHistoryStorageCopy();
        let description: string = historyActionLabel(historyActionType, HISTORY_STORAGE_LOCALE);
        
        // シート間移動・複製の場合は移動元シート名を含む説明文を生成（内部は英語で保存）
        
        if (sourceSheetId && (
          historyActionType === HistoryActionType.MOVE_POUCH ||
          historyActionType === HistoryActionType.MOVE_GROUP ||
          historyActionType === HistoryActionType.CLONE_POUCH ||
          historyActionType === HistoryActionType.CLONE_GROUP
        )) {
          const sourceSheet = stateRef.current.sheets.find(s => s.id === sourceSheetId);
          const sourceSheetName = sourceSheet?.title || t.default.unknownSheet;
          
          if (historyActionType === HistoryActionType.MOVE_POUCH) {
            description = storageHistory.dynamic.movePouchFrom(sourceSheetName);
          } else if (historyActionType === HistoryActionType.MOVE_GROUP) {
            description = storageHistory.dynamic.moveGroupFrom(sourceSheetName);
          } else if (historyActionType === HistoryActionType.CLONE_POUCH) {
            description = storageHistory.dynamic.clonePouchFrom(sourceSheetName);
          } else if (historyActionType === HistoryActionType.CLONE_GROUP) {
            description = storageHistory.dynamic.cloneGroupFrom(sourceSheetName);
          }
        }
        
        // アクションタイプに応じたtargetIdを特定
        let targetId: string | undefined;
        
        switch (historyActionType) {
          case HistoryActionType.ADD_POUCH:
          case HistoryActionType.EDIT_POUCH_VALUE:
          case HistoryActionType.MOVE_POUCH:
          case HistoryActionType.CHANGE_POUCH_COLOR:
          case HistoryActionType.CLONE_POUCH:
          case HistoryActionType.SPLIT_POUCH:
          case HistoryActionType.EDIT_POUCH_TITLE:
          case HistoryActionType.PASTE_BULK_UPDATE:
            // パウチに関連するアクション - 変更されたパウチを特定
            if (pouches) {
              // 値、位置、色、タイトルが変更されたパウチを特定
              const modifiedPouch = updatedPouches.find(pouch => {
                const originalPouch = latestActiveSheet.pouches.find(p => p.id === pouch.id);
                if (!originalPouch) return historyActionType === HistoryActionType.ADD_POUCH; // ADD_POUCHの場合は新規作成パウチを対象とする
                
                // 操作タイプに応じて変更を検出
                if (historyActionType === HistoryActionType.EDIT_POUCH_VALUE || 
                    historyActionType === HistoryActionType.PASTE_BULK_UPDATE) {
                  return HistoryLogic.pouchEditDiffersForHistory(originalPouch, pouch);
                }
                else if (historyActionType === HistoryActionType.MOVE_POUCH) {
                  return originalPouch.x !== pouch.x || originalPouch.y !== pouch.y;
                }
                else if (historyActionType === HistoryActionType.CHANGE_POUCH_COLOR) {
                  return originalPouch.color !== pouch.color;
                }
                else if (historyActionType === HistoryActionType.EDIT_POUCH_TITLE) {
                  return originalPouch.title !== pouch.title;
                }
                // CLONE_POUCHの場合は新規作成されたパウチ
                return false;
              });
              
              // 操作されたパウチのIDをtargetIdに設定
              if (modifiedPouch) {
                targetId = modifiedPouch.id;
              }
              // ADD_POUCHまたはCLONE_POUCHの場合は新規作成されたパウチを特定
              else if (historyActionType === HistoryActionType.ADD_POUCH || 
                       historyActionType === HistoryActionType.CLONE_POUCH ||
                       historyActionType === HistoryActionType.SPLIT_POUCH) {
                const newPouch = updatedPouches.find(p => 
                  !latestActiveSheet.pouches.some(op => op.id === p.id)
                );
                if (newPouch) targetId = newPouch.id;
              }
            }
            break;

          case HistoryActionType.ADD_GROUP:
          case HistoryActionType.MOVE_GROUP:
          case HistoryActionType.RESIZE_GROUP:
          case HistoryActionType.CHANGE_GROUP_COLOR:
          case HistoryActionType.CLONE_GROUP:
          case HistoryActionType.EDIT_GROUP_TITLE:
            // グループに関連するアクション - 変更されたグループを特定
            if (groups) {
              // 追加、移動、リサイズ、色変更、タイトル変更されたグループを特定
              const modifiedGroup = updatedGroups.find(group => {
                const originalGroup = latestActiveSheet.groups.find(g => g.id === group.id);
                if (!originalGroup) return false; // 新規作成のグループ
                
                // 操作タイプに応じて変更を検出
                if (historyActionType === HistoryActionType.MOVE_GROUP) {
                  // パスが変更されているか確認（移動）
                  return originalGroup.path !== group.path;
                }
                else if (historyActionType === HistoryActionType.RESIZE_GROUP) {
                  // パスが変更されているか確認（リサイズ）
                  return originalGroup.path !== group.path;
                }
                else if (historyActionType === HistoryActionType.CHANGE_GROUP_COLOR) {
                  // 色が変更されているか確認
                  return originalGroup.color !== group.color;
                }
                else if (historyActionType === HistoryActionType.EDIT_GROUP_TITLE) {
                  // タイトルが変更されているか確認
                  return originalGroup.title !== group.title;
                }
                // その他のケース
                return false;
              });
              
              // 操作されたグループのIDをtargetIdに設定
              if (modifiedGroup) {
                targetId = modifiedGroup.id;
              }
              // ADD_GROUPまたはCLONE_GROUPの場合は新規作成されたグループを特定
              else if (historyActionType === HistoryActionType.ADD_GROUP || 
                      historyActionType === HistoryActionType.CLONE_GROUP) {
                const newGroup = updatedGroups.find(g => 
                  !latestActiveSheet.groups.some(og => og.id === g.id)
                );
                if (newGroup) targetId = newGroup.id;
              }
            }
            break;
            
          // その他のアクションタイプはtargetIdを使用しない
          default:
            targetId = undefined;
        }
        
        // 履歴エントリを追加
        updatedSheetWithHistory = HistoryLogic.addEntry(
          currentActiveSheet,
          updatedGroups,
          updatedPouches,
          historyActionType,
          description,
          targetId,
          forceNewEntry
        );
        
        // 履歴作成処理完了後にタイマーでin-actionクラスを削除
        historyActionTimerRef.current = window.setTimeout(() => {
          if (isInHistoryActionRef.current) {
            isInHistoryActionRef.current = false;
            document.documentElement.classList.remove('in-action');
            historyActionTimerRef.current = null;
          }
        }, 600); // 600ms後に削除
      } else if (stripAddPouchHistoryForPouchId) {
        const { HistoryLogic } = await import('./utils/HistoryLogic');
        const stripped = HistoryLogic.stripLatestHistoryEntryIfAddPouchForTarget(
          currentActiveSheet,
          stripAddPouchHistoryForPouchId
        );
        if (stripped) {
          updatedSheetWithHistory = {
            ...updatedSheetWithHistory,
            history: stripped.history,
            historyIndex: stripped.historyIndex
          };
        }
      }
      
      // DBに保存するための現在の完全なシート情報（balancing は BP ブロック後に reconcile 済み）
      const updatedSheet = syncBalancingTargetTotalToActiveLayer({
        ...updatedSheetWithHistory,
        pouches: updatedPouches,
        groups: updatedGroups,
        balancingPouchId,
        balancingTargetTotal,
        balancingPreferredSign,
        balancingTargets: balancingTargetsPatch,
        lastGroupColor: newLastGroupColor,
        lastPouchColor: newLastPouchColor,
        lastPouchValue: (() => {
          if (historyActionType === HistoryActionType.EDIT_POUCH_VALUE || 
              historyActionType === HistoryActionType.PASTE_BULK_UPDATE) {
            const changedPouch = updatedPouches.find(p => {
              const originalPouch = currentActiveSheet.pouches.find(op => op.id === p.id);
              return originalPouch && originalPouch.value !== p.value && !p.isGroupTotalPouch;
            });
            if (changedPouch) {
              if (changedPouch.id === currentActiveSheet.balancingPouchId) {
                return currentActiveSheet.lastPouchValue;
              }
              return changedPouch.value;
            }
          }
          return currentActiveSheet.lastPouchValue;
        })(),
        colorLegends: initialColorLegends,
        snapshots: updatedSheetWithHistory.snapshots || currentActiveSheet.snapshots || [],
        lastModified: new Date(),
      });
      
        // 前回のタイマーをクリア（実行中の async 保存は世代で無効化）
        if (pouchUpdateTimeoutRef.current !== null) {
          clearTimeout(pouchUpdateTimeoutRef.current);
          pouchUpdateTimeoutRef.current = null;
        }
        const saveGeneration = ++pouchSaveGenerationRef.current;

        const commitSheetSave = async (sheetToSave: Sheet): Promise<void> => {
          if (saveGeneration !== pouchSaveGenerationRef.current) return;
          dispatch({ type: 'UPDATE_SHEET', payload: sheetToSave });
          const savePromise = SheetStorage.saveSheet(sheetToSave);
          pouchSaveInFlightRef.current = savePromise;
          try {
            await savePromise;
          } finally {
            if (pouchSaveInFlightRef.current === savePromise) {
              pouchSaveInFlightRef.current = null;
            }
          }
        };
      
      if (hasCriticalUpdates) {
        // 重要な更新は即時実行
        // 即時実行時も最新のstateから colorLegends と title を取得（古いクロージャで上書きされる問題を防ぐ）
        const latestStateForCritical = stateRef.current;
        const latestSheetForCritical = latestStateForCritical.sheets.find(s => s.id === activeSheet.id);
        const bpPersistCritical = pickBalancingForPersist(
          latestSheetForCritical,
          {
            balancingPouchId: updatedSheet.balancingPouchId ?? null,
            balancingTargetTotal: updatedSheet.balancingTargetTotal,
            balancingPreferredSign: updatedSheet.balancingPreferredSign,
          },
          updatedPouches
        );
        
        const finalUpdatedSheetCritical = {
          ...updatedSheet,
          title: latestSheetForCritical?.title || updatedSheet.title,
          colorLegends: latestSheetForCritical?.colorLegends || updatedSheet.colorLegends,
          balancingPouchId: bpPersistCritical.balancingPouchId,
          balancingTargetTotal: bpPersistCritical.balancingTargetTotal,
          balancingPreferredSign: bpPersistCritical.balancingPreferredSign,
        };
        
        await commitSheetSave(finalUpdatedSheetCritical);
        isUpdatingActiveSheetRef.current = false;
        return;
      }
      
      // 通常の更新はデバウンスして実行（500ms待機）
      pendingDebouncedSheetRef.current = {
        ...updatedSheet,
        pouches: updatedPouches,
        groups: updatedGroups,
      };
      pouchUpdateTimeoutRef.current = window.setTimeout(async () => {
        if (saveGeneration !== pouchSaveGenerationRef.current) {
          pouchUpdateTimeoutRef.current = null;
          pendingDebouncedSheetRef.current = null;
          return;
        }
        // タイマー実行時に最新のstateから最新のシート情報を取得
        const latestState = stateRef.current;
        const latestSheet = latestState.sheets.find(s => s.id === activeSheet.id);
        const bpPersistDebounced = pickBalancingForPersist(
          latestSheet,
          {
            balancingPouchId: updatedSheet.balancingPouchId ?? null,
            balancingTargetTotal: updatedSheet.balancingTargetTotal,
            balancingPreferredSign: updatedSheet.balancingPreferredSign,
          },
          updatedPouches
        );
        
        // 最新のシート情報と、保存予定だったupdatedSheetをマージ
        const finalUpdatedSheet = {
          ...updatedSheet,
          // 最新のtitleやその他のプロパティを反映
          title: latestSheet?.title || updatedSheet.title,
          // colorLegendsも最新のものを使用（凡例タイトルが消える問題の修正）
          colorLegends: latestSheet?.colorLegends || updatedSheet.colorLegends,
          balancingPouchId: bpPersistDebounced.balancingPouchId,
          balancingTargetTotal: bpPersistDebounced.balancingTargetTotal,
          balancingPreferredSign: bpPersistDebounced.balancingPreferredSign,
        };
        
        await commitSheetSave(finalUpdatedSheet);
        pendingDebouncedSheetRef.current = null;
        pouchUpdateTimeoutRef.current = null;
      }, 500);
    } catch (error) {
      console.error('シート情報の保存に失敗しました:', error);
    } finally {
      // 並行実行フラグをリセット
      isUpdatingActiveSheetRef.current = false;
    }
  }, [activeSheet, debouncedLog, t.default.unknownSheet]);
  
  // 帳尻パウチ（BP）トグル処理
  const handleToggleBalancingPouch = useCallback(async (pouchId: string) => {
    if (!activeSheet) return;
    const currentSheet = stateRef.current.sheets.find(s => s.id === activeSheet.id) || activeSheet;
    const bpAction: 'off' | 'on' | 'change' =
      currentSheet.balancingPouchId === pouchId
        ? 'off'
        : currentSheet.balancingPouchId
          ? 'change'
          : 'on';
    logSheetPouchValues(
      `bp-toggle:before ${bpAction}`,
      currentSheet.layers?.length ? syncSheetPouchesToActiveLayer(currentSheet) : currentSheet
    );

    let newBalancingPouchId: string | null;
    let newBalancingTargetTotal: number | undefined;
    let newBalancingPreferredSign: 1 | -1 | undefined;

    if (currentSheet.balancingPouchId === pouchId) {
      newBalancingPouchId = null;
      newBalancingTargetTotal = undefined;
      newBalancingPreferredSign = undefined;
    } else {
      newBalancingPouchId = pouchId;
      const lid = resolveActiveLayerId(currentSheet);
      const mirrored = mirrorPouchesForLayer(
        currentSheet.pouches,
        lid,
        currentSheet,
        currentSheet.groups ?? []
      );
      // BP 新規 ON: 表示中レイヤーの葉合計のみ（他レイヤーに残った balancingTargets を使わない）
      if (bpAction === 'on') {
        newBalancingTargetTotal = calculateSheetTotalForLayer(
          currentSheet.pouches,
          currentSheet.groups,
          lid,
          currentSheet
        );
      } else {
        newBalancingTargetTotal =
          getBalancingTargetForLayer(currentSheet, lid) ??
          calculateSheetTotalForLayer(
            currentSheet.pouches,
            currentSheet.groups,
            lid,
            currentSheet
          );
      }
      const bp = mirrored.find(p => p.id === pouchId);
      newBalancingPreferredSign = (bp?.value ?? 0) >= 0 ? 1 : -1;
    }

    // BP 状態を更新したシートを作成
    let sheetWithNewBp: Sheet = {
      ...currentSheet,
      balancingPouchId: newBalancingPouchId,
      balancingTargetTotal: newBalancingTargetTotal,
      balancingPreferredSign: newBalancingPreferredSign,
      balancingTargets: newBalancingPouchId ? currentSheet.balancingTargets : undefined,
    };
    if (newBalancingPouchId && newBalancingTargetTotal !== undefined) {
      sheetWithNewBp = withBalancingTargetForLayer(
        sheetWithNewBp,
        resolveActiveLayerId(sheetWithNewBp),
        newBalancingTargetTotal
      );
    }

    let fullPouches = currentSheet.pouches;
    if (newBalancingPouchId && newBalancingTargetTotal !== undefined) {
      fullPouches = syncBalancingPouchTimeMode(
        fullPouches,
        currentSheet.groups ?? [],
        newBalancingPouchId,
        sheetWithNewBp
      );
    }
    let pouchesForBp = mirrorPouchesForLayer(
      fullPouches,
      resolveActiveLayerId(sheetWithNewBp),
      sheetWithNewBp,
      currentSheet.groups ?? []
    );
    if (newBalancingPouchId && newBalancingTargetTotal !== undefined) {
      pouchesForBp = applyBalancingPouch(
        pouchesForBp,
        currentSheet.groups,
        newBalancingPouchId,
        newBalancingTargetTotal,
        newBalancingPreferredSign
      );
      pouchesForBp = syncBalancingPouchToActiveLayer(
        pouchesForBp,
        newBalancingPouchId,
        { ...sheetWithNewBp, pouches: pouchesForBp }
      );
    }

    // 履歴エントリを作成（新しい BP 状態を含むシートで addEntry）
    const { HistoryLogic } = await import('./utils/HistoryLogic');
    const storageHistory = getHistoryStorageCopy();
    let bpDescription: string;
    if (!newBalancingPouchId) {
      bpDescription = storageHistory.dynamic.disableBalancing;
    } else if (currentSheet.balancingPouchId && currentSheet.balancingPouchId !== pouchId) {
      bpDescription = storageHistory.dynamic.changeBalancing;
    } else {
      bpDescription = storageHistory.dynamic.enableBalancing;
    }
    const updatedSheet = HistoryLogic.addEntry(
      { ...sheetWithNewBp, pouches: pouchesForBp },
      currentSheet.groups,
      pouchesForBp,
      HistoryActionType.TOGGLE_BALANCING_POUCH,
      bpDescription,
      pouchId
    );
    const sheetForLog = syncBalancingTargetTotalToActiveLayer(updatedSheet);
    logSheetPouchValues(`bp-toggle:after ${bpAction}`, sheetForLog, sheetForLog.pouches);

    // 既存のデバウンスタイマーをキャンセル（古いBP状態での上書き防止）
    if (pouchUpdateTimeoutRef.current !== null) {
      clearTimeout(pouchUpdateTimeoutRef.current);
      pouchUpdateTimeoutRef.current = null;
    }

    dispatch({ type: 'UPDATE_SHEET', payload: sheetForLog });

    // stateRefも同期的に更新（デバウンス保存との競合防止）
    stateRef.current = {
      ...stateRef.current,
      sheets: stateRef.current.sheets.map(s =>
        s.id === sheetForLog.id ? sheetForLog : s
      )
    };

    await SheetStorage.saveSheet(sheetForLog);
  }, [activeSheet]);

  // デバイスタイプ更新処理
  const handleUpdateDeviceType = useCallback((isTouchDevice: boolean) => {
    // appStateのisTouchDeviceを更新
    if (state.isTouchDevice !== isTouchDevice) {
      // state内のisTouchDeviceを直接変更するような操作はないためカスタムロジックで実装
      const newState = {
        ...state,
        isTouchDevice: isTouchDevice
      };
      
      // reducerを使わずに状態を更新
      (state as any).isTouchDevice = isTouchDevice;
      
      // デバッグ出力
      
      // HTML要素にタッチデバイス用のクラスを更新
      if (isTouchDevice) {
        document.documentElement.classList.add('touch-device');
      } else {
        document.documentElement.classList.remove('touch-device');
      }
    }
  }, [state]);

  // サイドバーが閉じたときの遅延削除処理
  useEffect(() => {
    if (!state.isRightSidebarOpen && !state.isRightSidebarFullyClosed) {
      // サイドバーが閉じた場合、300ms後にコンポーネントを削除
      const timer = setTimeout(() => {
        dispatch({ type: 'SET_RIGHT_SIDEBAR_FULLY_CLOSED', payload: true });
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [state.isRightSidebarOpen, state.isRightSidebarFullyClosed]);
  
  // 凡例更新処理
  const handleUpdateColorLegend = useCallback((sheetId: string, colorId: string, legend: string) => {
    // stateRef.currentから最新シートを取得（クロージャのactiveSheetは古い可能性がある）
    const latestSheet = stateRef.current.sheets.find(s => s.id === sheetId);
    if (!latestSheet) return;

    const updatedColorLegends = latestSheet.colorLegends?.map(cl => 
      cl.colorId === colorId ? { ...cl, legend } : cl
    ) || [];
    
    const updatedSheet = { ...latestSheet, colorLegends: updatedColorLegends };
    dispatch({ type: 'UPDATE_SHEET', payload: updatedSheet });
    SheetStorage.saveSheet(updatedSheet);
    
    // stateRefも同期的に更新（setTimeout内のクロージャが古いデータを参照する問題を防ぐ）
    stateRef.current = {
      ...stateRef.current,
      sheets: stateRef.current.sheets.map(s => 
        s.id === sheetId ? updatedSheet : s
      )
    };
  }, []);
  
  // 色別合計計算処理
  const handleCalculateColorTotal = useCallback((colorId: string) => {
    if (activeSheet) {
      return calculateColorTotal(activeSheet.pouches, colorId, activeSheet.groups);
    }
    return 0;
  }, [activeSheet]);
  
  // 色別カウント計算処理
  const handleCalculateColorCount = useCallback((colorId: string) => {
    if (activeSheet) {
      return calculateColorCount(activeSheet.pouches, colorId, activeSheet.groups);
    }
    return 0;
  }, [activeSheet]);
  
  // 編集確定処理
  const handleEditConfirm = useCallback(() => {
    // 編集確定時の処理（必要に応じて実装）
  }, []);
  
  // シート切替時にウィンドウタイトルを「POUCHES」に戻す
  useEffect(() => {
    document.title = 'POUCHES';
  }, [state.activeSheetId]);

  // シート切替時の処理
  useEffect(() => {
    const prevActiveSheetId = prevActiveSheetIdForSwitchRef.current;
    prevActiveSheetIdForSwitchRef.current = state.activeSheetId;

    if (state.activeSheetId) {
      const isActualSheetChange =
        prevActiveSheetId !== null &&
        prevActiveSheetId !== state.activeSheetId;

      if (isActualSheetChange && prevActiveSheetId) {
        void flushPendingSheetWrites(prevActiveSheetId);
      }

      // 前回のタイマーをクリア
      if (switchTimerRef.current !== null) {
        clearTimeout(switchTimerRef.current);
      }
      
      // グループ更新タイマーもクリア
      if (groupUpdateTimeoutRef.current !== null) {
        clearTimeout(groupUpdateTimeoutRef.current);
        groupUpdateTimeoutRef.current = null;
        pendingGroupUpdateRef.current = null;
      }
      
      // パウチ更新タイマーもクリア
      if (pouchUpdateTimeoutRef.current !== null) {
        clearTimeout(pouchUpdateTimeoutRef.current);
        pouchUpdateTimeoutRef.current = null;
        pendingPouchUpdateRef.current = null;
      }

      if (isActualSheetChange && !isSheetSwitchingRef.current) {
        isSheetSwitchingRef.current = true;
        setIsSheetSwitching(true);
      }
      
      // シート切り替え時に全パウチをリセット
      if (isActualSheetChange && activeSheetRef.current) {
        // ActiveSheetComponentのpouchesRefから現在のパウチを取得してリセット
        const currentPouches = (activeSheetRef.current as any).pouchesRef?.current || [];
        const resetPouches = currentPouches.map((pouch: any) => ({
          ...pouch,
        }));
        activeSheetRef.current.setPouches(resetPouches);
      }
      
      if (isActualSheetChange) {
        switchTimerRef.current = window.setTimeout(() => {
          isSheetSwitchingRef.current = false;
          setIsSheetSwitching(false);
          switchTimerRef.current = null;
        }, 600);
      }

      return () => {
        if (switchTimerRef.current !== null) {
          clearTimeout(switchTimerRef.current);
        }
      };
    }
  }, [state.activeSheetId]);

  // 履歴コンポーネントからの状態更新ハンドラ
  const handleHistoryStateUpdate = async (newSheet: Sheet, newPouches: Pouch[], newGroups: Group[]) => {
    const prevPouches = activeSheet?.pouches ?? [];
    const prevGroups = activeSheet?.groups ?? [];

    const triggerUndoRedoRevealScroll = (nextPouches: Pouch[], nextGroups: Group[]) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          activeSheetRef.current?.revealScrollForUndoRedo(
            prevPouches,
            prevGroups,
            nextPouches,
            nextGroups,
          );
        });
      });
    };

    let sheet = newSheet;
    let pouches = newPouches;
    let groups = newGroups;

    // Undo/Redo: 操作時のアクティブレイヤーへ切替し pouch.value を同期
    if (newSheet.layers?.length) {
      const prevLayerId = activeSheet ? resolveActiveLayerId(activeSheet) : resolveActiveLayerId(newSheet);
      const synced = syncSheetPouchesToActiveLayer({ ...newSheet, pouches: newPouches, groups: newGroups });
      sheet = synced;
      pouches = synced.pouches;
      groups = synced.groups;
      const nextLayerId = resolveActiveLayerId(synced);
      if (prevLayerId !== nextLayerId) {
        setLayerSwitchDirection(compareLayerOrder(synced, prevLayerId, nextLayerId));
        setLayerSwitchNonce(n => n + 1);
      }
    }

    // 履歴からの更新であることを示すフラグ
    const isHistoryUpdate = true;
    // スナップショット関連の変更を検出
    const isSnapshotChange = 
      sheet.snapshots?.length !== activeSheet?.snapshots?.length ||
      JSON.stringify(sheet.snapshots?.map(s => s.id).sort()) !== 
      JSON.stringify(activeSheet?.snapshots?.map(s => s.id).sort());
    
    // スナップショット復元の判定
    const isSnapshotRestore = sheet.history?.[sheet.historyIndex]?.type === HistoryActionType.RESTORE_SNAPSHOT;
    
    // 削除対象のパウチとグループを見つける
    const deletingPouches = pouches.filter(p => (p as any)._isDeleting);
    const deletingGroups = groups.filter(g => (g as any)._isDeleting);
    
    if (deletingPouches.length > 0 || deletingGroups.length > 0) {
      // 削除アニメーションを表示するため、一時的に削除フラグ付きのパウチとグループを含めて更新
      dispatch({ 
        type: 'SET_ACTIVE_SHEET_POUCHES', 
        payload: pouches 
      });
      
      dispatch({ 
        type: 'SET_ACTIVE_SHEET_GROUPS', 
        payload: groups 
      });
      
      // アクティブシートを更新
      dispatch({ 
        type: 'UPDATE_SHEET', 
        payload: { ...sheet, pouches, groups }
      });
      
      if (activeSheetRef.current) {
        activeSheetRef.current.setPouches(pouches);
        activeSheetRef.current.setGroups(groups);
      }
      triggerUndoRedoRevealScroll(pouches, groups);

      // 常に永続化する - historyIndexの変更を保存するため
        await SheetStorage.saveSheet({ ...sheet, pouches, groups });
      
      // 削除アニメーション後に実際に削除されたパウチとグループを除外
      setTimeout(async () => {
        // 履歴からのredoの場合は、元々の履歴状態（削除フラグなし）を使用
        // 通常の削除の場合は、削除フラグ付きパウチを除外
        const pouchesWithoutDeleted = (isHistoryUpdate || isSnapshotRestore)
          ? pouches.filter(p => !(p as any)._isDeleting)  // 履歴更新でも削除フラグ付きパウチを除外
          : pouches.filter(p => !(p as any)._isDeleting);  // 通常の削除の場合はフィルタリング
        
        // グループの削除処理も実行
        const groupsWithoutDeleted = groups.filter(g => !(g as any)._isDeleting);
        
        // アクティブシートを更新
        const updatedSheet = { ...sheet, pouches: pouchesWithoutDeleted, groups: groupsWithoutDeleted };
        dispatch({ 
          type: 'UPDATE_SHEET', 
          payload: updatedSheet
        });
        
        // ActiveSheetComponentの状態を更新
        if (activeSheetRef.current) {
          activeSheetRef.current.setPouches(pouchesWithoutDeleted);
          activeSheetRef.current.setGroups(groupsWithoutDeleted);
          
          // スナップショット復元時に強制的に再レンダリング
          if (isSnapshotRestore) {
            activeSheetRef.current.forceUpdate();
          }
        }

        triggerUndoRedoRevealScroll(pouchesWithoutDeleted, groupsWithoutDeleted);
        
        // 永続化（アニメーション完了後の最終状態）
          await SheetStorage.saveSheet(updatedSheet);
      }, 400); // 削除アニメーションの持続時間に合わせる
      
      // 履歴からの更新でもaddingフラグの削除処理を実行
      const addingGroups = groups.filter(g => (g as any)._isAdding);
      if (addingGroups.length > 0) {
        // アニメーション後にフラグをリセット
        const timerId = setTimeout(() => {
          const groupsWithoutAddingFlag = groups.map(g => 
            (g as any)._isAdding ? { ...g, _isAdding: false } : g
          );
          // ヒストリーエントリを作成せずに状態を更新
          if (activeSheetRef.current) {
            activeSheetRef.current.setGroups(groupsWithoutAddingFlag);
          }
        }, 300);
      }
    } else {
      // 通常の更新（削除するパウチがない場合）
      
      // 追加中(_isAdding)のグループを処理
      const addingGroups = groups.filter(g => (g as any)._isAdding);
      if (addingGroups.length > 0) {
        // アニメーション後にフラグをリセット
        const timerId = setTimeout(() => {
          const groupsWithoutAddingFlag = groups.map(g => 
            (g as any)._isAdding ? { ...g, _isAdding: false } : g
          );
          // ヒストリーエントリを作成せずに状態を更新
          if (activeSheetRef.current) {
            activeSheetRef.current.setGroups(groupsWithoutAddingFlag);
          }
        }, 300);
      }
      
      // 削除中(_isDeleting)のグループを処理
      const deletingGroups = groups.filter(g => (g as any)._isDeleting);
      if (deletingGroups.length > 0) {
        // アニメーション後にグループを実際に削除
        setTimeout(() => {
          const groupsWithoutDeleted = groups.filter(g => !(g as any)._isDeleting);
          dispatch({ 
            type: 'SET_ACTIVE_SHEET_GROUPS', 
            payload: groupsWithoutDeleted 
          });
          
          // ActiveSheetComponentの状態も更新
          if (activeSheetRef.current) {
            activeSheetRef.current.setGroups(groupsWithoutDeleted);
          }
        }, 300);
      }
      
      // アクティブシートを更新
      dispatch({ type: 'UPDATE_SHEET', payload: sheet });
      
      // stateRefも同期的に更新（履歴操作後のパウチ削除等で古いデータを参照する問題を防ぐ）
      stateRef.current = {
        ...stateRef.current,
        sheets: stateRef.current.sheets.map(s => 
          s.id === sheet.id ? sheet : s
        )
      };
      
      // パウチとグループの状態をセット
      dispatch({ 
        type: 'SET_ACTIVE_SHEET_POUCHES', 
        payload: pouches 
      });
      
      dispatch({ 
        type: 'SET_ACTIVE_SHEET_GROUPS', 
        payload: groups 
      });
      
      // ActiveSheetComponentの状態を更新
      if (activeSheetRef.current) {
        activeSheetRef.current.setPouches(pouches);
        activeSheetRef.current.setGroups(groups);
        
        // スナップショット復元時に強制的に再レンダリング
        if (isSnapshotRestore) {
          activeSheetRef.current.forceUpdate();
        }
      }

      triggerUndoRedoRevealScroll(pouches, groups);
      
      // 常に永続化する - historyIndexの変更を保存するため
        await SheetStorage.saveSheet(sheet);
    }
  };

  const navigateSheetHistoryFromBridge = useCallback(async (
    sheetId: string,
    direction: 'undo' | 'redo',
    options?: { expectedRevision?: number },
  ): Promise<WriteSheetResult | SheetApiError> => {
    const existing = stateRef.current.sheets.find(s => s.id === sheetId);
    if (!existing) {
      return { ok: false, code: 'SHEET_NOT_FOUND', message: `Sheet not found: ${sheetId}` };
    }

    const baseRevision = existing.revision ?? 0;
    if (
      options?.expectedRevision !== undefined &&
      options.expectedRevision !== baseRevision
    ) {
      return {
        ok: false,
        code: 'REVISION_CONFLICT',
        message: `Expected revision ${options.expectedRevision} but current is ${baseRevision}`,
      };
    }

    const navigated = applyHistoryNavigation(existing, direction);
    if (!navigated) {
      return {
        ok: false,
        code: 'HISTORY_NAVIGATION_UNAVAILABLE',
        message: direction === 'undo' ? 'Nothing to undo' : 'Nothing to redo',
      };
    }

    const { sheet, pouches, groups } = navigated;
    const isActiveSheet = sheetId === stateRef.current.activeSheetId;

    if (isActiveSheet) {
      document.dispatchEvent(
        new CustomEvent('setUndoRedoClass', { detail: { type: direction } }),
      );
      await new Promise<void>(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      await handleHistoryStateUpdate(sheet, pouches, groups);
    } else {
      const finalSheet = { ...sheet, pouches, groups };
      await SheetStorage.saveSheet(finalSheet);
      dispatch({ type: 'UPDATE_SHEET', payload: finalSheet });
      stateRef.current = {
        ...stateRef.current,
        sheets: stateRef.current.sheets.map(s =>
          s.id === sheetId ? finalSheet : s,
        ),
      };
    }

    const skipMutationId = uuidv4();
    bridgeSelfMutationIdRef.current = skipMutationId;
    postSheetMutatedBroadcast(
      sheetId,
      stateRef.current.sheets.find(s => s.id === sheetId)?.revision ?? baseRevision,
      skipMutationId,
    );

    const updated = stateRef.current.sheets.find(s => s.id === sheetId)!;
    const apiSheet = readSheetForApi(updated);
    return {
      ok: true,
      sheetId,
      revision: apiSheet.revision,
      sheet: apiSheet.sheet,
    };
  }, []);

  const undoSheetFromBridge = useCallback(
    (sheetId: string, options?: { expectedRevision?: number }) =>
      navigateSheetHistoryFromBridge(sheetId, 'undo', options),
    [navigateSheetHistoryFromBridge],
  );

  const redoSheetFromBridge = useCallback(
    (sheetId: string, options?: { expectedRevision?: number }) =>
      navigateSheetHistoryFromBridge(sheetId, 'redo', options),
    [navigateSheetHistoryFromBridge],
  );

  const checkBridgeRevision = useCallback((
    sheet: Sheet,
    expectedRevision?: number,
  ): SheetApiError | null => {
    const currentRevision = sheet.revision ?? 0;
    if (
      expectedRevision !== undefined &&
      expectedRevision !== currentRevision
    ) {
      return {
        ok: false,
        code: 'REVISION_CONFLICT',
        message: `Expected revision ${expectedRevision} but current is ${currentRevision}`,
      };
    }
    return null;
  }, []);

  const publishBridgeMutation = useCallback((sheetId: string, revision?: number) => {
    const skipMutationId = uuidv4();
    bridgeSelfMutationIdRef.current = skipMutationId;
    postSheetMutatedBroadcast(sheetId, revision, skipMutationId);
  }, []);

  const activateSheetFromBridge = useCallback(async (
    sheetId: string,
  ): Promise<ActivateSheetResult | SheetApiError> => {
    const existing = stateRef.current.sheets.find(s => s.id === sheetId);
    if (!existing) {
      return { ok: false, code: 'SHEET_NOT_FOUND', message: `Sheet not found: ${sheetId}` };
    }

    beginSheetSwitch(sheetId);
    await selectSheet(sheetId, dispatch);
    stateRef.current = {
      ...stateRef.current,
      activeSheetId: sheetId,
    };

    return { ok: true, activeSheetId: sheetId, sheetId };
  }, [beginSheetSwitch]);

  const deleteSheetFromBridge = useCallback(async (
    sheetId: string,
    options?: { expectedRevision?: number },
  ): Promise<DeleteSheetResult | SheetApiError> => {
    const currentSheets = stateRef.current.sheets;
    const existing = currentSheets.find(s => s.id === sheetId);
    if (!existing) {
      return { ok: false, code: 'SHEET_NOT_FOUND', message: `Sheet not found: ${sheetId}` };
    }

    const revisionError = checkBridgeRevision(existing, options?.expectedRevision);
    if (revisionError) return revisionError;

    const wasActive = stateRef.current.activeSheetId === sheetId;
    const remaining = currentSheets.filter(sheet => sheet.id !== sheetId);

    await deleteSheet(sheetId, currentSheets, dispatch);

    if (remaining.length === 0) {
      const initSheet = await createAndSaveInitializationSheet();
      authorizeNextAddSheet();
      dispatch({ type: 'ADD_SHEET', payload: initSheet });
      dispatch({ type: 'SET_ACTIVE_SHEET', payload: initSheet.id });
      await SheetStorage.saveActiveSheetId(initSheet.id);
      stateRef.current = {
        ...stateRef.current,
        sheets: [initSheet],
        activeSheetId: initSheet.id,
      };
      publishBridgeMutation(sheetId, existing.revision);
      return {
        ok: true,
        deletedSheetId: sheetId,
        activeSheetId: initSheet.id,
        sheets: [{ id: initSheet.id, title: initSheet.title }],
        createdSheet: readSheetForApi(initSheet).sheet,
      };
    }

    const activeSheetId = wasActive ? remaining[0].id : stateRef.current.activeSheetId;
    if (wasActive) {
      beginSheetSwitch(activeSheetId);
      dispatch({ type: 'SET_ACTIVE_SHEET', payload: activeSheetId });
      await SheetStorage.saveActiveSheetId(activeSheetId);
    }

    stateRef.current = {
      ...stateRef.current,
      sheets: remaining,
      activeSheetId,
    };
    publishBridgeMutation(sheetId, existing.revision);

    return {
      ok: true,
      deletedSheetId: sheetId,
      activeSheetId,
      sheets: remaining.map(sheet => ({ id: sheet.id, title: sheet.title })),
    };
  }, [beginSheetSwitch, checkBridgeRevision, publishBridgeMutation]);

  const addLayerFromBridge = useCallback(async (
    sheetId: string,
    options?: { name?: string; expectedRevision?: number },
  ): Promise<WriteSheetResult | SheetApiError> => {
    const existing = stateRef.current.sheets.find(s => s.id === sheetId);
    if (!existing) {
      return { ok: false, code: 'SHEET_NOT_FOUND', message: `Sheet not found: ${sheetId}` };
    }

    const revisionError = checkBridgeRevision(existing, options?.expectedRevision);
    if (revisionError) return revisionError;

    const baseRevision = existing.revision ?? 0;
    const nextSheet = addSheetLayer(existing, options?.name || undefined);
    const newLayerId = resolveActiveLayerId(nextSheet);
    const saved = await persistSheetWithLayerHistory(
      { ...nextSheet, revision: baseRevision + 1 },
      HistoryActionType.ADD_SHEET_LAYER,
      newLayerId,
    );
    const updated = saved ?? stateRef.current.sheets.find(s => s.id === sheetId);
    if (!updated) {
      return { ok: false, code: 'SHEET_NOT_FOUND', message: `Sheet not found after update: ${sheetId}` };
    }
    publishBridgeMutation(sheetId, updated.revision);
    const apiSheet = readSheetForApi(updated);
    return {
      ok: true,
      sheetId,
      revision: apiSheet.revision,
      sheet: apiSheet.sheet,
    };
  }, [checkBridgeRevision, persistSheetWithLayerHistory, publishBridgeMutation]);

  const deleteLayerFromBridge = useCallback(async (
    sheetId: string,
    layerId: string,
    options?: { expectedRevision?: number },
  ): Promise<WriteSheetResult | SheetApiError> => {
    const existing = stateRef.current.sheets.find(s => s.id === sheetId);
    if (!existing) {
      return { ok: false, code: 'SHEET_NOT_FOUND', message: `Sheet not found: ${sheetId}` };
    }

    const layers = getSortedLayers(existing);
    if (!layers.some(layer => layer.id === layerId)) {
      return { ok: false, code: 'LAYER_NOT_FOUND', message: `Layer not found: ${layerId}` };
    }
    if (layers.length <= 1) {
      return {
        ok: false,
        code: 'VALIDATION_ERROR',
        message: 'Cannot delete the last layer',
      };
    }

    const revisionError = checkBridgeRevision(existing, options?.expectedRevision);
    if (revisionError) return revisionError;

    const baseRevision = existing.revision ?? 0;
    const nextSheet = removeSheetLayer(existing, layerId);
    const saved = await persistSheetWithLayerHistory(
      { ...nextSheet, revision: baseRevision + 1 },
      HistoryActionType.REMOVE_SHEET_LAYER,
      layerId,
    );
    const updated = saved ?? stateRef.current.sheets.find(s => s.id === sheetId);
    if (!updated) {
      return { ok: false, code: 'SHEET_NOT_FOUND', message: `Sheet not found after update: ${sheetId}` };
    }
    publishBridgeMutation(sheetId, updated.revision);
    const apiSheet = readSheetForApi(updated);
    return {
      ok: true,
      sheetId,
      revision: apiSheet.revision,
      sheet: apiSheet.sheet,
    };
  }, [checkBridgeRevision, persistSheetWithLayerHistory, publishBridgeMutation]);

  const createSnapshotFromBridge = useCallback(async (
    sheetId: string,
    options?: { name?: string; description?: string; expectedRevision?: number },
  ): Promise<CreateSnapshotResult | SheetApiError> => {
    const existing = stateRef.current.sheets.find(s => s.id === sheetId);
    if (!existing) {
      return { ok: false, code: 'SHEET_NOT_FOUND', message: `Sheet not found: ${sheetId}` };
    }

    const revisionError = checkBridgeRevision(existing, options?.expectedRevision);
    if (revisionError) return revisionError;

    const snapshotNumber = (existing.snapshots?.length ?? 0) + 1;
    const name = options?.name || `Snapshot ${snapshotNumber}`;
    const description =
      options?.description || `Created by API at ${new Date().toLocaleString()}`;
    const updatedSheet = HistoryLogic.createSnapshot(
      existing,
      existing.pouches,
      existing.groups,
      name,
      description,
    );
    const revision = (existing.revision ?? 0) + 1;
    const toSave = {
      ...updatedSheet,
      revision,
      lastModified: new Date(),
    };

    dispatch({ type: 'UPDATE_SHEET', payload: toSave });
    stateRef.current = {
      ...stateRef.current,
      sheets: stateRef.current.sheets.map(s =>
        s.id === sheetId ? toSave : s,
      ),
    };
    await SheetStorage.saveSheet(toSave);
    publishBridgeMutation(sheetId, revision);

    const snapshot = toSave.snapshots[toSave.snapshots.length - 1];
    return {
      ok: true,
      sheetId,
      revision,
      snapshotId: snapshot.id,
      sheet: readSheetForApi(toSave).sheet,
    };
  }, [checkBridgeRevision, publishBridgeMutation]);

  useEffect(() => {
    let stopBridge: (() => void) | undefined;

    void waitForAppInit().then(() => {
      const host = new SheetApiHost({
        getState: () => ({
          sheets: stateRef.current.sheets,
          activeSheetId: stateRef.current.activeSheetId,
        }),
        createSheet: options => createSheetFromBridge(options),
        applySheetWrite: (targetSheetId, sheet, writeOptions) =>
          applySheetFromBridge(targetSheetId, sheet, writeOptions),
        deleteSheet: (targetSheetId, deleteOptions) =>
          deleteSheetFromBridge(targetSheetId, deleteOptions),
        activateSheet: targetSheetId =>
          activateSheetFromBridge(targetSheetId),
        tidySheet: (targetSheetId, tidyOptions) =>
          tidySheetFromBridge(targetSheetId, tidyOptions),
        addLayer: (targetSheetId, layerOptions) =>
          addLayerFromBridge(targetSheetId, layerOptions),
        deleteLayer: (targetSheetId, layerId, layerOptions) =>
          deleteLayerFromBridge(targetSheetId, layerId, layerOptions),
        createSnapshot: (targetSheetId, snapshotOptions) =>
          createSnapshotFromBridge(targetSheetId, snapshotOptions),
        undoSheet: (targetSheetId, undoOptions) =>
          undoSheetFromBridge(targetSheetId, undoOptions),
        redoSheet: (targetSheetId, redoOptions) =>
          redoSheetFromBridge(targetSheetId, redoOptions),
        readSheet: readSheetForBridge,
        getContext: getSheetApiContext,
      });
      stopBridge = startBridgeClient(host);
    });

    return () => {
      stopBridge?.();
    };
  }, [
    waitForAppInit,
    createSheetFromBridge,
    applySheetFromBridge,
    deleteSheetFromBridge,
    activateSheetFromBridge,
    tidySheetFromBridge,
    addLayerFromBridge,
    deleteLayerFromBridge,
    createSnapshotFromBridge,
    undoSheetFromBridge,
    redoSheetFromBridge,
    readSheetForBridge,
    getSheetApiContext,
  ]);

  // ウィンドウ間通信でファイルインポートを処理
  const handleFileImportMessage = useCallback(async (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    
    if (event.data.type === 'IMPORT_POUCHES_FILE' && event.data.fileData) {
      try {
        
        // Base64データからファイルを復元
        const response = await fetch(event.data.fileData);
        const blob = await response.blob();
        const fileName = event.data.fileName || 'imported.pouches';
        const originalLastModified = event.data.lastModified || Date.now();
        const file = new File([blob], fileName, { 
          type: 'application/pouches',
          lastModified: originalLastModified
        });
        
        
        await importSheetFromFile(file);
        
        // ウィンドウにフォーカスを当てる
        window.focus();
      } catch (error) {
        console.error('ウィンドウ間通信でのファイルインポートに失敗しました:', error);
      }
    }
  }, [importSheetFromFile]);

  // ウィンドウ間通信のリスナーを設定
  useEffect(() => {
    window.addEventListener('message', handleFileImportMessage);
    return () => {
      window.removeEventListener('message', handleFileImportMessage);
    };
  }, [handleFileImportMessage]);

  // Electron: .pouches ファイルの open-file イベントを処理
  useEffect(() => {
    const electron = (window as unknown as { electron?: { onOpenFile: (cb: (p: string) => void) => () => void; readFile: (p: string) => Promise<{ success: boolean; content?: string; fileName?: string }> } }).electron;
    if (!electron?.onOpenFile) return;
    const cleanup = electron.onOpenFile(async (filePath: string) => {
      if (!filePath.endsWith('.pouches')) return;
      try {
        const result = await electron.readFile(filePath);
        if (!result.success || !result.content) {
          console.error('Electron: Failed to read file');
          return;
        }
        const file = new File([result.content], result.fileName || 'imported.pouches', {
          type: 'application/pouches'
        });
        await importSheetFromFile(file, false, false, filePath);
      } catch (error) {
        console.error('Electron: Import failed', error);
      }
    });
    return cleanup;
  }, [importSheetFromFile]);

  // Electron: 共有 URL / Universal Link / カスタムスキーム
  useEffect(() => {
    const electron = (window as unknown as {
      electron?: {
        onOpenUrl: (cb: (url: string) => void) => () => void;
        getPendingOpenUrl?: () => Promise<string | null>;
      notifyRendererReady?: (locale?: 'ja' | 'en' | 'zh', origin?: string) => void;
        readFile: (p: string) => Promise<{ success: boolean; content?: string; fileName?: string }>;
      };
    }).electron;
    if (!electron?.onOpenUrl) return;

    const handleOpenUrl = async (url: string) => {
      logImportLaunch('electron open-url received', url);
      const parsed = parseImportLaunchUrl(url);
      if (!parsed) {
        logImportLaunch('electron parse failed', url);
        return;
      }
      logImportLaunch('electron parsed', parsed);
      logImportLaunch('electron waiting app init');
      await waitForAppInit();
      logImportLaunch('electron app init done');
      rememberPendingImportLaunchUrl(url);
      if (parsed.kind === 'server') {
        clearImportLaunchDeclined(parsed.serverUrl);
        applyImportPageTitle(parsed.sheetName);
        const result = await importFromServerFile(parsed.serverUrl, false);
        logImportLaunch('electron server import result', { result, serverUrl: parsed.serverUrl });
        return;
      }
      if (parsed.fileUrl.startsWith('file://') || parsed.fileUrl.startsWith('/')) {
        try {
          const result = await electron.readFile(parsed.fileUrl.replace('file://', ''));
          if (!result.success || !result.content) return;
          const file = new File([result.content], result.fileName || 'imported.pouches', {
            type: 'application/pouches',
          });
          await importSheetFromFile(file, false, false, parsed.fileUrl);
        } catch (error) {
          console.error('Electron: Import from URL failed', error);
        }
        return;
      }
      try {
        const response = await fetch(parsed.fileUrl);
        if (!response.ok) return;
        const content = await response.text();
        const fileName = decodeURIComponent(parsed.fileUrl.split('/').pop()?.split('?')[0] || 'imported.pouches');
        const file = new File([content], fileName, { type: 'application/pouches' });
        await importSheetFromFile(file, false, false, parsed.fileUrl);
      } catch (error) {
        console.error('Electron: Import from URL failed', error);
      }
    };

    const cleanup = electron.onOpenUrl((url) => {
      void handleOpenUrl(url);
    });

    void (async () => {
      if (electron.getPendingOpenUrl) {
        const pending = await electron.getPendingOpenUrl();
        if (pending) await handleOpenUrl(pending);
      }
      electron.notifyRendererReady?.(locale, window.location.origin);
    })();

    return cleanup;
  }, [importFromServerFile, importSheetFromFile, waitForAppInit]);

  // Capacitor: 共有 URL / Universal Link / カスタムスキーム / .pouches
  useEffect(() => {
    let sub: { remove: () => void } | undefined;
    const handleLaunchUrl = async (url: string) => {
      console.log('[ImportLaunch] capacitor url:', url);
      const parsed = parseImportLaunchUrl(url);
      if (!parsed) return;
      await waitForAppInit();
      rememberPendingImportLaunchUrl(url);
      if (parsed.kind === 'server') {
        clearImportLaunchDeclined(parsed.serverUrl);
        applyImportPageTitle(parsed.sheetName);
        await importFromServerFile(parsed.serverUrl, true);
        return;
      }
      try {
        const response = await fetchImportFile(parsed.fileUrl);
        if (!response.ok) {
          alert(t.error.importFailed);
          return;
        }
        const fileName = decodeURIComponent(parsed.fileUrl.split('/').pop()?.split('?')[0] || 'imported.pouches');
        const file = new File([response.text], fileName, { type: 'application/pouches' });
        await importSheetFromFile(file, false, false, parsed.fileUrl);
      } catch (error) {
        console.error('Capacitor: Import failed', error);
        alert(t.error.importFailed);
      }
    };
    const setup = async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        const launch = await App.getLaunchUrl();
        if (launch?.url) await handleLaunchUrl(launch.url);
        sub = await App.addListener('appUrlOpen', ({ url }) => handleLaunchUrl(url));
      } catch {
        // Web / Electron では @capacitor/app なし
      }
    };
    void setup();
    return () => sub?.remove();
  }, [importFromServerFile, importSheetFromFile, waitForAppInit, t.error.importFailed]);

  // キーボードショートカットのリスナーを設定
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Alt+N (Mac/Windows共通) で新規シート作成
      const isAltKey = e.altKey;
      const isNKey = e.key === 'n' || e.key === 'N' || e.code === 'KeyN';
      
      if (isAltKey && isNKey) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleCreateNewSheet();
        return false;
      }
      
      // F3 で左サイドバーの開閉トグル
      if (e.key === 'F3') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        toggleMenu();
        return false;
      }
      
      // F4 で右サイドバーの開閉トグル
      if (e.key === 'F4') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        toggleSidebar();
        return false;
      }
      
      // Esc でドラッグを強制キャンセル
      if (e.key === 'Escape') {
        const tag = document.activeElement?.tagName;
        // テキスト入力中は編集側の Escape を優先（グループタイトルは textarea）
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
          return false;
        }
        
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // ドラッグ状態を強制リセット
        const dragCancelEvent = new CustomEvent('forceDragCancel');
        document.dispatchEvent(dragCancelEvent);
        return false;
      }
    };

    // イベントリスナーを設定
    document.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [handleCreateNewSheet, toggleMenu, toggleSidebar]);

  // 強制アンドゥイベントリスナー
  useEffect(() => {
    const handleForceUndo = () => {
      if (activeSheet) {
        // 履歴からアンドゥを実行
        const undoResult = HistoryLogic.undo(activeSheet);
        if (undoResult) {
          const setUndoRedoClassEvent = new CustomEvent('setUndoRedoClass', { 
            detail: { type: 'undo' } 
          });
          document.dispatchEvent(setUndoRedoClassEvent);

          // クラスが確実に適用されるのを待ってから実際のundo処理を実行
          // 複数のrequestAnimationFrameを使用してより確実にクラス適用を待つ
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              void handleHistoryStateUpdate(
                undoResult.newSheet,
                undoResult.newPouches,
                undoResult.newGroups
              );
            });
          });
        }
      }
    };

    document.addEventListener('forceUndo', handleForceUndo);
    return () => {
      document.removeEventListener('forceUndo', handleForceUndo);
    };
  }, [activeSheet]);

  // ウィンドウIDを一度だけ生成（モジュールレベル）
  const windowId = useRef<string>('');
  if (!windowId.current) {
    windowId.current = Date.now().toString() + '-' + Math.random().toString(36).substr(2, 9);
  }

  // 既存ウィンドウをチェックする関数
  const checkForExistingWindows = useCallback(async (): Promise<Window[]> => {
    return new Promise((resolve) => {
      // 一度生成されたウィンドウIDを使用
      const myWindowId = windowId.current!;
      
      // より確実な方法：専用のチャンネルで一意のメッセージIDを使用
      const channelName = `pouches-window-check-${Date.now()}`;
      const channel = new BroadcastChannel(channelName);
      const checkId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      let otherWindowsFound = false;
      let hasResponded = false;
      
      
      
      const responseHandler = (event: MessageEvent) => {
        if (hasResponded) {
          
          return;
        }
        
        
        if (event.data.type === 'WINDOW_RESPONSE' && event.data.checkId === checkId) {
          
          // 自分自身からの応答でないかチェック
          if (event.data.windowId !== myWindowId) {
            
            otherWindowsFound = true;
          } else {
            
          }
          hasResponded = true;
        }
      };
      
      // 既存ウィンドウからの応答をリッスン
      const globalChannel = new BroadcastChannel('pouches-window-exists');
      globalChannel.addEventListener('message', responseHandler);
      
      // 既存ウィンドウに存在確認を送信
      
      globalChannel.postMessage({
        type: 'WINDOW_CHECK',
        checkId: checkId,
        senderWindowId: myWindowId,
        responseChannel: channelName
      });
      
      // 100ms待ってレスポンスを確認
      setTimeout(() => {
        globalChannel.removeEventListener('message', responseHandler);
        globalChannel.close();
        channel.close();
        
        
        
        if (otherWindowsFound) {
          
          resolve([window]); // ダミーウィンドウ
        } else {
          
          resolve([]);
        }
      }, 100);
    });
  }, [windowId]);

  // 既存ウィンドウにファイルを送信する関数
  const sendFileToExistingWindow = useCallback(async (file: File, targetWindow: Window): Promise<boolean> => {
    return new Promise((resolve) => {
      try {
        // ファイルをBase64に変換
        const reader = new FileReader();
        reader.onload = () => {
          const channel = new BroadcastChannel('pouches-file-import');
          const responseChannel = new BroadcastChannel('pouches-import-response');
          const importId = Date.now().toString();
          
          // 応答リスナーを設定
          const responseHandler = (event: MessageEvent) => {
            if (event.data.type === 'IMPORT_SUCCESS' && event.data.importId === importId) {
              responseChannel.removeEventListener('message', responseHandler);
              responseChannel.close();
              resolve(true);
            }
          };
          
          responseChannel.addEventListener('message', responseHandler);
          
          // ファイルデータを送信
          channel.postMessage({
            type: 'IMPORT_POUCHES_FILE',
            fileData: reader.result,
            fileName: file.name,
            lastModified: file.lastModified,
            importId: importId,
            senderWindowId: windowId.current // 送信者のウィンドウIDを追加
          });
          channel.close();
          
          // 3秒でタイムアウト
          setTimeout(() => {
            responseChannel.removeEventListener('message', responseHandler);
            responseChannel.close();
            resolve(false);
          }, 3000);
        };
        
        reader.onerror = () => {
          resolve(false);
        };
        
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('ファイル送信に失敗しました:', error);
        resolve(false);
      }
    });
  }, []);

  // 統合ファイルインポート関数（ダブルクリック以外用）
  const handleUnifiedFileImport = useCallback(async (file: File, skipDuplicateCheck: boolean = false, filePath?: string) => {
    const existingWindows = await checkForExistingWindows();
    if (existingWindows.length > 0) {
      const success = await sendFileToExistingWindow(file, existingWindows[0]);
      if (!success) await importSheetFromFile(file, skipDuplicateCheck, false, filePath);
    } else {
      await importSheetFromFile(file, skipDuplicateCheck, false, filePath);
    }
  }, [checkForExistingWindows, sendFileToExistingWindow, importSheetFromFile]);

  // File Handling API（.pouches の OS 関連付け）— 初期化完了を待たず登録
  useEffect(() => {
    if (!('launchQueue' in window)) {
      return;
    }

    const LOOP_GUARD_KEY = 'pouches_file_open_guard';
    const loopGuardMs = 8000;

    const markHandled = () => {
      sessionStorage.setItem(LOOP_GUARD_KEY, Date.now().toString());
    };

    const shouldSkipEmptyLaunch = (): boolean => {
      const last = sessionStorage.getItem(LOOP_GUARD_KEY);
      if (!last) {
        return false;
      }
      return Date.now() - parseInt(last, 10) < loopGuardMs;
    };

    const importPouchesLaunchFile = async (file: File) => {
      markHandled();
      if (isWindowsPwa()) {
        await importSheetFromFile(file, false);
        return;
      }
      await handleUnifiedFileImport(file, false);
    };

    const handleEmptyFileLaunch = async () => {
      if (shouldSkipEmptyLaunch()) {
        return;
      }
      markHandled();
      if (!isWindowsPwa()) {
        return;
      }
      const file = await pickPouchesFile();
      if (file?.name.endsWith('.pouches')) {
        await importSheetFromFile(file, false);
      }
    };

    (window as Window & { launchQueue: { setConsumer: (fn: (p: { targetURL?: string; files?: FileSystemFileHandle[] }) => void | Promise<void>) => void } }).launchQueue.setConsumer(
      async (launchParams) => {
        try {
          if (launchParams.targetURL) {
            const parsed = parseImportLaunchUrl(launchParams.targetURL);
            if (parsed?.kind === 'server') {
              await waitForAppInit();
              clearImportLaunchDeclined(parsed.serverUrl);
              applyImportPageTitle(parsed.sheetName);
              await importFromServerFile(parsed.serverUrl, true);
              return;
            }
          }

          const files = launchParams.files ?? [];
          if (files.length === 0) {
            await handleEmptyFileLaunch();
            return;
          }

          for (const fileHandle of files) {
            const file = await fileHandle.getFile();
            if (!file.name.endsWith('.pouches')) {
              continue;
            }

            const fileSpecificKey = `pouches_file_${file.name}_${file.lastModified}_${file.size}`;
            const lastProcessedTime = sessionStorage.getItem(fileSpecificKey);
            if (lastProcessedTime) {
              const timeDiff = Date.now() - parseInt(lastProcessedTime, 10);
              if (timeDiff < 5000) {
                continue;
              }
            }
            sessionStorage.setItem(fileSpecificKey, Date.now().toString());

            await importPouchesLaunchFile(file);
            break;
          }
        } catch (error) {
          console.error('launchQueue file import failed:', error);
        }
      }
    );
  }, [importSheetFromFile, handleUnifiedFileImport, waitForAppInit, importFromServerFile]);

  // Electron: File → Open メニュー
  useEffect(() => {
    const electron = (window as unknown as { electron?: { onOpenDialogRequest: (cb: () => void) => () => void; showOpenDialog: () => Promise<{ canceled: boolean; filePaths: string[] }>; readFile: (p: string) => Promise<{ success: boolean; content?: string; fileName?: string }> } }).electron;
    if (!electron?.onOpenDialogRequest || !electron?.showOpenDialog || !electron?.readFile) return;
    const cleanup = electron.onOpenDialogRequest(async () => {
      try {
        const { canceled, filePaths } = await electron.showOpenDialog();
        if (!canceled && filePaths?.[0]) {
          const result = await electron.readFile(filePaths[0]);
          if (result.success && result.content) {
            const file = new File([result.content], result.fileName || 'imported.pouches', { type: 'application/pouches' });
            await handleUnifiedFileImport(file, true, filePaths[0]);
          } else {
            alert(t.error.readFileFailed);
          }
        }
      } catch (err) {
        console.error('Open failed:', err);
        alert(t.error.openFailed);
      }
    });
    return cleanup;
  }, [handleUnifiedFileImport, t.error.readFileFailed, t.error.openFailed]);

  // Electron: File → Import from URL メニュー
  useEffect(() => {
    const electron = (window as unknown as { electron?: { onImportUrlPromptRequest: (cb: () => void) => () => void } }).electron;
    if (!electron?.onImportUrlPromptRequest) return;
    return electron.onImportUrlPromptRequest(() => setShowImportPrompt(true));
  }, []);

  // Electron: File menu handlers
  useEffect(() => {
    const electron = (window as unknown as {
      electron?: {
        onMenuNewRequest: (cb: () => void) => () => void;
        onMenuSaveRequest: (cb: () => void) => () => void;
        onMenuSaveAsRequest: (cb: () => void) => () => void;
        onMenuShareRequest: (cb: (t: 'private' | 'public') => void) => () => void;
      };
    }).electron;
    if (!electron?.onMenuSaveRequest) return;
    const cleanups: (() => void)[] = [];
    if (electron.onMenuNewRequest) {
      cleanups.push(electron.onMenuNewRequest(() => handleCreateNewSheet()));
    }
    cleanups.push(electron.onMenuSaveRequest(() => {
      const id = stateRef.current.activeSheetId;
      if (!id) return;
      const path = activeSheetFilePathRef.current.get(id);
      executeExportAction(id, 'download', path);
    }));
    cleanups.push(electron.onMenuSaveAsRequest(() => {
      const id = stateRef.current.activeSheetId;
      if (!id) return;
      executeExportAction(id, 'download');
    }));
    cleanups.push(electron.onMenuShareRequest((shareType) => {
      const id = stateRef.current.activeSheetId;
      if (!id) return;
      handleExportSheet(id, undefined, shareType);
    }));
    return () => cleanups.forEach((c) => c());
  }, [handleCreateNewSheet, executeExportAction, handleExportSheet]);

  // ウィンドウチェックのレスポンダーを設定
  useEffect(() => {
    // 共通のウィンドウIDを使用
    const myWindowId = windowId.current!;
    
    const globalChannel = new BroadcastChannel('pouches-window-exists');
    const fileChannel = new BroadcastChannel('pouches-file-import');
    
    const handleWindowCheck = (event: MessageEvent) => {
      if (event.data.type === 'WINDOW_CHECK') {
        
        
        
        // 送信者と自分が異なるウィンドウの場合のみ応答
        if (event.data.senderWindowId !== myWindowId) {
          
          globalChannel.postMessage({
            type: 'WINDOW_RESPONSE',
            checkId: event.data.checkId,
            windowId: myWindowId
          });
          
        } else {
          
        }
      }
    };
    
    const handleFileImport = async (event: MessageEvent) => {
      if (event.data.type === 'IMPORT_POUCHES_FILE' && event.data.fileData) {
        // 送信者自身は処理をスキップ
        if (event.data.senderWindowId === myWindowId) {
          
          return;
        }
        
        
        
        try {
          
          // Base64データからファイルを復元
          const response = await fetch(event.data.fileData);
          const blob = await response.blob();
          const fileName = event.data.fileName || 'imported.pouches';
          const originalLastModified = event.data.lastModified || Date.now();
          const file = new File([blob], fileName, { 
            type: 'application/pouches',
            lastModified: originalLastModified
          });
          
          // リーダー選出: 最初に応答したウィンドウがファイルをインポート
          const leaderKey = `import_leader_${event.data.importId}`;
          const existingLeader = sessionStorage.getItem(leaderKey);
          
          if (!existingLeader) {
            // リーダーになる
            sessionStorage.setItem(leaderKey, myWindowId);
            
            await importSheetFromFile(file);
            
            // 他のウィンドウにデータ更新を通知
            const refreshChannel = new BroadcastChannel('pouches-refresh-data');
            refreshChannel.postMessage({
              type: 'REFRESH_REQUIRED',
              reason: 'leader_import_completed',
              leaderId: myWindowId
            });
            refreshChannel.close();
          } else {
            // フォロワーとしてDBから読み込み
            
            // REFRESH_REQUIREDイベントを待つ（handleRefreshRequestで処理される）
          }
          
          // ウィンドウにフォーカスを当てる
          window.focus();
          
          // 成功レスポンスを送信（リーダーのみ）
          if (event.data.importId && !existingLeader) {
            const responseChannel = new BroadcastChannel('pouches-import-response');
            responseChannel.postMessage({
              type: 'IMPORT_SUCCESS',
              importId: event.data.importId
            });
            responseChannel.close();
          }
        } catch (error) {
          console.error('BroadcastChannelでのファイルインポートに失敗しました:', error);
          
          // エラーレスポンスを送信
          if (event.data.importId) {
            const responseChannel = new BroadcastChannel('pouches-import-response');
            responseChannel.postMessage({
              type: 'IMPORT_ERROR',
              importId: event.data.importId,
              error: error instanceof Error ? error.message : 'Unknown error'
            });
            responseChannel.close();
          }
        }
      }
    };
    
    // データリフレッシュチャンネルの設定
    const refreshChannel = new BroadcastChannel('pouches-refresh-data');
    
    const handleRefreshRequest = async (event: MessageEvent) => {
      if (event.data.type === 'SHEET_MUTATED' && typeof event.data.sheetId === 'string') {
        if (
          typeof event.data.skipMutationId === 'string' &&
          event.data.skipMutationId === bridgeSelfMutationIdRef.current
        ) {
          bridgeSelfMutationIdRef.current = null;
          return;
        }
        try {
          const sheet = await SheetStorage.getSheet(event.data.sheetId);
          if (!sheet) {
            return;
          }
          dispatch({ type: 'UPDATE_SHEET', payload: sheet });
          if (
            stateRef.current.activeSheetId === event.data.sheetId &&
            activeSheetRef.current
          ) {
            activeSheetRef.current.setPouches(sheet.pouches);
            activeSheetRef.current.setGroups(sheet.groups);
          }
        } catch (error) {
          console.error('[Refresh] Failed to merge sheet mutation:', error);
        }
        return;
      }

      if (event.data.type === 'REFRESH_REQUIRED') {
        
        
        // リーダーインポート完了の場合、自分がリーダーなら処理をスキップ
        if (event.data.reason === 'leader_import_completed' && event.data.leaderId === myWindowId) {
          
          return;
        }
        
        try {
          const storageSheets = await SheetStorage.getAllSheets();
          const mergedSheets = mergeSheetsForState(stateRef.current.sheets, storageSheets);

          const activeSheetId = SheetStorage.getActiveSheetId() ||
            (mergedSheets.length > 0 ? mergedSheets[0].id : '');

          dispatch({
            type: 'INIT_APP',
            payload: {
              sheets: mergedSheets,
              settings: stateRef.current.settings,
            },
          });
          
          // アクティブシートも更新
          if (activeSheetId) {
            dispatch({ type: 'SET_ACTIVE_SHEET', payload: activeSheetId });
          }
          
          
          
          // 代替案：ページリロードが必要な場合は以下をコメントアウト
          // window.location.reload();
        } catch (error) {
          console.error('[Refresh] Failed to refresh data:', error);
        }
      }
    };
    
    globalChannel.addEventListener('message', handleWindowCheck);
    fileChannel.addEventListener('message', handleFileImport);
    refreshChannel.addEventListener('message', handleRefreshRequest);
    
    return () => {
      globalChannel.removeEventListener('message', handleWindowCheck);
      fileChannel.removeEventListener('message', handleFileImport);
      refreshChannel.removeEventListener('message', handleRefreshRequest);
      globalChannel.close();
      fileChannel.close();
      refreshChannel.close();
    };
  }, [importSheetFromFile, state.settings]);

  // ドラッグ&ドロップハンドラー
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

    const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    
    
    const files = Array.from(e.dataTransfer.files);
    
    
    // .pouchesファイルのみを処理
    for (const file of files) {
      if (file.name.endsWith('.pouches')) {
        
        await handleUnifiedFileImport(file, true); // 統合インポート関数を使用
        break; // 最初の.pouchesファイルのみ処理
      }
    }
  }, [handleUnifiedFileImport]);

  // グループを書き出すハンドラ
  const handleExportGroup = async (groupId: string) => {
    if (!activeSheet) return;
    
    // 書き出し機能を呼び出す
    try {
      const result = await shareGroupContent(
        groupId,
        activeSheet.pouches,
        activeSheet.groups,
        state.sheets,
        state.activeSheetId
      );
      
      if (result) {
      }
    } catch (error) {
      console.error('グループの書き出しに失敗しました:', error);
    }
  };

  // グループ内のパウチをすべて正負転換するハンドラ
  const handleToggleGroupSign = (groupId: string, currentPouches?: Pouch[], currentGroups?: Group[]) => {
    if (!activeSheet) return;
    
    // 最新のグループデータを使用（currentGroupsが渡されない場合は activeSheet.groups をフォールバック）
    const groupsToUse = currentGroups || activeSheet.groups;
    const group = groupsToUse.find(g => g.id === groupId);
    if (!group || !group.pouches) return;
    
    // 最新のパウチデータを使用（currentPouchesが渡されない場合は activeSheet.pouches をフォールバック）
    const pouchesToUpdate = currentPouches || activeSheet.pouches;
    
    // グループに所属するパウチをすべて正負転換
    let updatedPouches = pouchesToUpdate.map(pouch => {
      if (!group.pouches?.includes(pouch.id) || pouch.isGroupTotalPouch) {
        return pouch;
      }
      const lid = resolveActiveLayerId(activeSheet);
      const current =
        activeSheet.layers?.length ? getLayerValue(pouch, lid) : pouch.value;
      return applyPouchScalarForSheet(pouch, -current, activeSheet, {
        multiplier: pouch.multiplier,
      });
    });

    // 帳尻パウチが有効なときは onUpdateActiveSheet を経由しないため、ここで BP を再計算（グループ外の BP 含む）
    const balancingPouchId = activeSheet.balancingPouchId ?? null;
    const bpLayerId = resolveActiveLayerId(activeSheet);
    const balancingTargetTotal = getBalancingTargetForLayer(activeSheet, bpLayerId);
    const balancingPreferredSign = activeSheet.balancingPreferredSign;
    if (balancingPouchId && balancingTargetTotal !== undefined) {
      const bpPouch = updatedPouches.find(p => p.id === balancingPouchId);
      if (bpPouch && !bpPouch._isDeleting) {
        updatedPouches = syncBalancingPouchTimeMode(
          updatedPouches,
          groupsToUse,
          balancingPouchId,
          activeSheet
        );
        updatedPouches = applyBalancingPouch(
          updatedPouches,
          groupsToUse,
          balancingPouchId,
          balancingTargetTotal,
          balancingPreferredSign
        );
        updatedPouches = syncBalancingPouchToActiveLayer(updatedPouches, balancingPouchId, {
          ...activeSheet,
          pouches: updatedPouches,
        });
        updatedPouches = updatedPouches.map(p => {
          if (p.id !== balancingPouchId) return p;
          if (p.isTime) {
            return { ...p, isNegative: getTimePouchEffectiveForTotal(p) < 0 };
          }
          return { ...p, isNegative: p.value < 0 };
        });
      }
    }
    
    // パウチとグループを更新
    dispatch({
      type: 'UPDATE_SHEET',
      payload: {
        ...activeSheet,
        pouches: updatedPouches,
        groups: groupsToUse
      }
    });
  };


  // 別シートのオブジェクトを直接削除する専用関数
  const deleteObjectsFromSheet = useCallback(async (
    targetSheetId: string,
    groupId: string,
    pouchIds: string[],
    containedGroupIds: string[]
  ) => {
    
    // 対象シートを取得
    const targetSheet = state.sheets.find(sheet => sheet.id === targetSheetId);
    if (!targetSheet) {
      return;
    }
    
    // パウチを削除
    const updatedPouches = targetSheet.pouches.filter(pouch => !pouchIds.includes(pouch.id));
    
    // グループを削除
    const updatedGroups = targetSheet.groups.filter(group => 
      group.id !== groupId && !containedGroupIds.includes(group.id)
    );
    
    // 更新されたシートを作成
    const updatedSheet = {
      ...targetSheet,
      pouches: updatedPouches,
      groups: updatedGroups
    };
    
    // シートを更新
    dispatch({ type: 'UPDATE_SHEET', payload: updatedSheet });
    
    // stateRefも同期的に更新
    stateRef.current = {
      ...stateRef.current,
      sheets: stateRef.current.sheets.map(s => 
        s.id === updatedSheet.id ? updatedSheet : s
      )
    };
    
    // 複数シートの保存処理を実行
    await saveMultipleSheets([updatedSheet]);
  }, [state.sheets, dispatch]);

  // 複数シートを同時に保存する関数
  const saveMultipleSheets = useCallback(async (sheetsToSave: Sheet[]) => {
    try {
      // すべてのシートを並行して保存
      await Promise.all(sheetsToSave.map(async (sheet) => {
        const result = await SheetStorage.saveSheet(sheet);
        return result;
      }));
    } catch (error) {
      // 個別に保存を試行
      for (const sheet of sheetsToSave) {
        try {
          await SheetStorage.saveSheet(sheet);
        } catch (individualError) {
          console.error('Failed to save individual sheet:', sheet.id, individualError);
        }
      }
    }
  }, []);

  // シート間ドラッグ開始処理（グループ用）
  const handleStartSheetDrag = useCallback((groupData: {
    group: Group;
    pouches: Pouch[];
    containedGroups: Group[];
    finalPosition: { x: number, y: number };
    sourceSheetId: string;
  }) => {
    draggedGroupDataRef.current = groupData;
    sourceSheetGroupRef.current = groupData.group; // 元シートのグループ情報を保存
    setIsDraggingToSheet(true);
  }, []);

  // シート間ドラッグ開始処理（パウチ用）
  const handleStartSheetPouchDrag = useCallback((pouchData: {
    pouch: Pouch;
    finalPosition: { x: number, y: number };
    sourceSheetId: string;
  }) => {
    draggedPouchDataRef.current = pouchData;
    sourceSheetPouchRef.current = pouchData.pouch; // 元シートのパウチ情報を保存
    setIsDraggingToSheet(true);    
  }, []);

  // シート一覧でのマウスホバー処理
  const handleSheetHover = useCallback((sheetId: string, isEntering: boolean) => {
    
    // ドラッグ中でない場合は処理しない（グループまたはパウチ）
    if (!isDraggingToSheet || (!draggedGroupDataRef.current && !draggedPouchDataRef.current)) return;
    
    if (isEntering) {
      setHoveredSheetId(sheetId);
      
      // 1.5秒後にシート切り替え
      if (sheetHoverTimerRef.current) {
        clearTimeout(sheetHoverTimerRef.current);
      }
      
      sheetHoverTimerRef.current = window.setTimeout(() => {
        if (sheetId !== state.activeSheetId) {
          // シート切り替え前にドラッグ状態を保存
          
          if (activeSheetRef.current && globalInteractionRef.current) {
            const currentComponent = activeSheetRef.current as any;
            
            // 現在のマウス/タッチ位置を取得
            const currentMousePosition = globalInteractionRef.current.currentPosition || { x: 0, y: 0 };
            
            // 現在のドラッグ状態を保存
            preservedDragStateRef.current = {
              interaction: { 
                ...globalInteractionRef.current,
                currentPosition: currentMousePosition
              },
              draggedFromOtherSheet: globalInteractionRef.current.draggedFromOtherSheet || false,
              draggedGroupId: globalInteractionRef.current.draggedGroupId,
              tempPouchPosition: currentComponent.getTempPouchPosition ? currentComponent.getTempPouchPosition() : null,
              draggedGroupData: draggedGroupDataRef.current,
              draggedPouchData: draggedPouchDataRef.current
            };
            
          }
          
          // シート切り替え前に**古い**ActiveSheetComponentのみを削除
          
          // 現在のアクティブシートのActiveSheetComponentは保護
          const currentActiveSheetId = state.activeSheetId;
          const targetSheetId = sheetId; // 移動先のシートID
          
          // 削除対象: 現在のアクティブシートでも移動先シートでもないActiveSheetComponent
          const allCalculatorAreas = document.querySelectorAll('.calculator-area');
          
          allCalculatorAreas.forEach((area, index) => {
            const parent = area.parentElement;
            if (parent) {
              // data-sheet-id属性でシートを識別
              const areaSheetId = area.getAttribute('data-sheet-id') || 'unknown';
              
              
              // 現在のアクティブシートと移動先シート以外を削除
              if (areaSheetId !== currentActiveSheetId && areaSheetId !== targetSheetId && areaSheetId !== 'unknown') {
                
                // 削除フラグを設定
                (window as any)[`destroying_${areaSheetId}`] = true;
                
                parent.style.display = 'none';
                parent.remove();
              } else {
              }
            }
          });
          
          
          // シート切り替え前に、現在のシートからシート間ドラッグで作成された要素を削除
          // ただし、ソースシート（ドラッグ開始元のシート）では実行しない
          const isSourceSheet = (draggedGroupDataRef.current?.sourceSheetId === state.activeSheetId) ||
                               (draggedPouchDataRef.current?.sourceSheetId === state.activeSheetId);
          
          // より厳密な判定：現在のシートがソースシートかつ、まだ一度もシート移動していない場合
          const isFirstMoveFromSource = isSourceSheet && !globalInteractionRef.current;
          
          
          if (activeSheetRef.current && !isFirstMoveFromSource) {
            const currentComponent = activeSheetRef.current as any;
            if (currentComponent.removeCrossSheetElements) {
              currentComponent.removeCrossSheetElements();
              
              // 1秒後にシート切り替え
              setTimeout(() => {
                handleSelectSheet(sheetId, new Event('click') as any);
                
                // シート切り替え後に内容を作成（描画完了を確実に待つ）
                const waitForRender = () => {
                  // requestAnimationFrameを複数回実行して描画完了を確実に待つ
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      // さらに少し遅延を入れて確実に描画完了を待つ
                      setTimeout(() => {
                        if (activeSheetRef.current) {
                          // まずドラッグ状態を復元（要素データも含む）
                          if (preservedDragStateRef.current) {
                            const newComponent = activeSheetRef.current as any;
                            if (newComponent.restoreDragState) {
                              newComponent.restoreDragState(preservedDragStateRef.current);
                            }
                            // グローバルドラッグ状態も復元
                            globalInteractionRef.current = preservedDragStateRef.current.interaction;
                            
                            // ドラッグ中の要素データも復元
                            if (preservedDragStateRef.current.draggedGroupData) {
                              draggedGroupDataRef.current = preservedDragStateRef.current.draggedGroupData;
                            }
                            if (preservedDragStateRef.current.draggedPouchData) {
                              draggedPouchDataRef.current = preservedDragStateRef.current.draggedPouchData;
                            }
                          }
                          
                          // グループドラッグの場合
                          if (draggedGroupDataRef.current) {
                            (activeSheetRef.current as any).createDraggedContent(draggedGroupDataRef.current);
                          }
                          // パウチドラッグの場合
                          if (draggedPouchDataRef.current) {
                            (activeSheetRef.current as any).createDraggedPouch(draggedPouchDataRef.current);
                          }
                          
                          // 保存した状態をクリア
                          if (preservedDragStateRef.current) {
                            preservedDragStateRef.current = null;
                          }
                        }
                      }, 50);
                    });
                  });
                };
                
                waitForRender();
              }, 50);
            } else {
              // removeCrossSheetElementsが実行されない場合は即座にシート切り替え
              handleSelectSheet(sheetId, new Event('click') as any);
              
              // シート切り替え後に内容を作成（描画完了を確実に待つ）
              const waitForRender = () => {
                // requestAnimationFrameを複数回実行して描画完了を確実に待つ
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    // さらに少し遅延を入れて確実に描画完了を待つ
                    setTimeout(() => {
                      if (activeSheetRef.current) {
                        // まずドラッグ状態を復元（要素データも含む）
                        if (preservedDragStateRef.current) {
                          const newComponent = activeSheetRef.current as any;
                          if (newComponent.restoreDragState) {
                            newComponent.restoreDragState(preservedDragStateRef.current);
                          }
                          // グローバルドラッグ状態も復元
                          globalInteractionRef.current = preservedDragStateRef.current.interaction;
                          
                          // ドラッグ中の要素データも復元
                          if (preservedDragStateRef.current.draggedGroupData) {
                            draggedGroupDataRef.current = preservedDragStateRef.current.draggedGroupData;
                          }
                          if (preservedDragStateRef.current.draggedPouchData) {
                            draggedPouchDataRef.current = preservedDragStateRef.current.draggedPouchData;
                          }
                        }
                        
                        // グループドラッグの場合
                        if (draggedGroupDataRef.current) {
                          (activeSheetRef.current as any).createDraggedContent(draggedGroupDataRef.current);
                        }
                        // パウチドラッグの場合
                        if (draggedPouchDataRef.current) {
                          (activeSheetRef.current as any).createDraggedPouch(draggedPouchDataRef.current);
                        }
                        
                        // 保存した状態をクリア
                        if (preservedDragStateRef.current) {
                          preservedDragStateRef.current = null;
                        }
                      }
                    }, 50);
                  });
                });
              };
              
              waitForRender();
            }
          } else {
            // 最初のシートから移動する場合は即座にシート切り替え
            handleSelectSheet(sheetId, new Event('click') as any);
            
            // シート切り替え後に内容を作成（描画完了を確実に待つ）
            const waitForRender = () => {
              // requestAnimationFrameを複数回実行して描画完了を確実に待つ
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  // さらに少し遅延を入れて確実に描画完了を待つ
                  setTimeout(() => {
                    if (activeSheetRef.current) {
                      // グループドラッグの場合
                      if (draggedGroupDataRef.current) {
                        (activeSheetRef.current as any).createDraggedContent(draggedGroupDataRef.current);
                      }
                      // パウチドラッグの場合
                      else if (draggedPouchDataRef.current) {
                        (activeSheetRef.current as any).createDraggedPouch(draggedPouchDataRef.current);
                      }
                    }
                  }, 50);
                });
              });
            };
            
            waitForRender();
          }
        }
      }, 1500);
    } else {
      if (hoveredSheetId === sheetId) {
        setHoveredSheetId(null);
        if (sheetHoverTimerRef.current) {
          clearTimeout(sheetHoverTimerRef.current);
          sheetHoverTimerRef.current = null;
        }
      }
    }
  }, [isDraggingToSheet, hoveredSheetId, state.activeSheetId, handleSelectSheet]);

  // グローバルなマウスアップイベントリスナー
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      // シート間ドラッグ状態をクリア
      if (isDraggingToSheet) {
        setTimeout(() => {
          setIsDraggingToSheet(false);
          draggedGroupDataRef.current = null;
          draggedPouchDataRef.current = null;
          setHoveredSheetId(null);
          
          if (sheetHoverTimerRef.current) {
            clearTimeout(sheetHoverTimerRef.current);
            sheetHoverTimerRef.current = null;
          }
        }, 50);
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDraggingToSheet]);

  // グローバルなタッチ移動イベントリスナー（シート間移動用）
  useEffect(() => {
    const handleGlobalTouchMove = (e: TouchEvent) => {
      // ドラッグ中でない場合は処理しない
      if (!isDraggingToSheet || (!draggedGroupDataRef.current && !draggedPouchDataRef.current)) {
        return;
      }

      const touch = e.touches[0];
      if (!touch) return;

      // タッチ位置にある要素を取得
      const elementUnderTouch = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!elementUnderTouch) return;

      // シートタブの要素かどうかを確認
      const sheetItem = elementUnderTouch.closest('.sheet-item');
      
      if (sheetItem) {
        const sheetId = sheetItem.getAttribute('data-sheet-id');
        if (sheetId && sheetId !== hoveredSheetId) {
          // 前回のホバーをクリア
          if (hoveredSheetId) {
            handleSheetHover(hoveredSheetId, false);
          }
          // 新しいシートにホバー
          handleSheetHover(sheetId, true);
        }
      } else {
        // シートタブの外にある場合、ホバーをクリア
        if (hoveredSheetId) {
          handleSheetHover(hoveredSheetId, false);
        }
      }
    };

    const handleGlobalTouchEnd = () => {
      // シート間ドラッグ状態をクリア
      if (isDraggingToSheet) {
        setTimeout(() => {
          setIsDraggingToSheet(false);
          draggedGroupDataRef.current = null;
          draggedPouchDataRef.current = null;
          setHoveredSheetId(null);
          
          if (sheetHoverTimerRef.current) {
            clearTimeout(sheetHoverTimerRef.current);
            sheetHoverTimerRef.current = null;
          }
        }, 50);
      }
    };

    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    document.addEventListener('touchend', handleGlobalTouchEnd);
    return () => {
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [isDraggingToSheet, hoveredSheetId, handleSheetHover]);

  const layerSwitchContextValue = useMemo(
    () => ({
      activeLayerId,
      layerSwitchDirection,
      layerSwitchNonce,
      layerSwitchFromLayerId,
    }),
    [activeLayerId, layerSwitchDirection, layerSwitchNonce, layerSwitchFromLayerId]
  );

  return (
    <GuideContext.Provider value={isGuideEnabled}>
    <PouchValueKeyboardProvider isTouchDevice={state.isTouchDevice}>
    <LayerSwitchProvider value={layerSwitchContextValue}>
    <>
    <div className="app-container"
      data-active-sheet-id={state.activeSheetId}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* サイドバー */}
      <div className={`left-sidebar ${state.isLeftSidebarOpen ? 'open' : 'closed'}`}>
        <div className="left-sidebar-handle" 
          onClick={toggleMenu}
          onTouchEnd={(e) => e.stopPropagation()}
          data-tooltip-id="tt"
          data-tooltip-html={formatTooltip(state.isLeftSidebarOpen ? `Click: ${tt.closeMenuAndSheetList}` : `Click: ${tt.openMenuAndSheetList}`)}
        >
          <svg 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ 
              width: 'var(--icon-size)', 
              height: 'var(--icon-size)' 
            }}
          >
            {state.isLeftSidebarOpen ? (
              <path d="M15 18L9 12L15 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            ) : (
              <path d="M9 18L15 12L9 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            )}
          </svg>
        </div>
        
        <div className="left-sidebar-header">
          <div className="app-title">
            <h1 className="main-title" data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Click: ${tt.reload}`)}>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/logo.svg`} 
                alt="POUCHES" 
                style={{ cursor: 'pointer' }}
                onClick={() => window.location.reload()}
                onTouchEnd={() => window.location.reload()}
              />
            </h1>
            <div className="sidebar-meta">
              Ver. {appDisplayVersion}
              {billingPaywallEnabled && entitlementStatus.source !== 'debug' ? (
                <>
                  {' - '}
                  {(() => {
                    const billing = getBillingCopy(locale);
                    const statusText = isProEntitlement(entitlementStatus)
                      ? billing.sidebarPro
                      : (() => {
                          const remaining = freeSheetsRemaining(
                            state.sheets.length,
                            entitlementStatus
                          );
                          return billing.sidebarFreeRemaining(remaining);
                        })();
                    const isWebRedeem =
                      detectBillingPlatform() === 'web' &&
                      !(window as { electron?: unknown }).electron;
                    const isStorePurchase = isNativeStorePurchasePlatform();
                    const isStorePro = isStoreProEntitlement(entitlementStatus);
                    const isClickable =
                      (isStorePro && isStorePurchase) ||
                      (!isProEntitlement(entitlementStatus) &&
                        (isWebRedeem || isNativeBillingPaywallPlatform()));
                    const tooltip = isStorePro && isStorePurchase
                      ? billing.sidebarStatusClickManage
                      : !isProEntitlement(entitlementStatus) && isWebRedeem
                        ? billing.sidebarStatusClickRedeem
                        : billing.sidebarStatusClickSubscribe;

                    if (!isClickable) {
                      return <span className="sidebar-meta-status">{statusText}</span>;
                    }

                    return (
                      <button
                        type="button"
                        className={`sidebar-meta-status${
                          isProEntitlement(entitlementStatus) ? ' sidebar-meta-status--pro' : ''
                        }`}
                        tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
                        data-tooltip-id="tt"
                        data-tooltip-html={formatTooltip(`Click: ${tooltip}`)}
                        onClick={() => void handleSidebarBillingStatusClick()}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          void handleSidebarBillingStatusClick();
                        }}
                      >
                        {statusText}
                      </button>
                    );
                  })()}
                </>
              ) : null}
            </div>
          </div>
          <button className="new-sheet-button" 
            tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
            data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Click: ${tt.createNewSheet}`)}
            onClick={handleCreateNewSheet} 
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M20 12H4" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 4V20" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>{t.menu.new}</span>
          </button>
          <button className="import-sheet-button" 
            tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
            data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Click: ${tt.importSheet}`)}
            onClick={handleImportSheet} 
            onTouchEnd={handleImportSheet}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              stroke="var(--icon-color)"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M4 15v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 12l4 4 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 2v14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{t.menu.import}</span>
          </button>
        </div>

        <div className="sheet-list">
          {state.sheets.map(sheet => {
            const sheetLayerId = resolveActiveLayerId(sheet);
            const sheetGroups = sheet.groups ?? [];
            const mirroredPouches = sheet.layers?.length
              ? mirrorPouchesForLayer(sheet.pouches, sheetLayerId, sheet, sheetGroups)
              : sheet.pouches;
            const sheetTotal = computeSheetTotalForSidebar(sheet, sheetLayerId);
            const allPouchesAreTime = sheetContributingPouchesAreAllTime(
              mirroredPouches,
              sheetGroups,
              sheet.balancingPouchId
            );
            const isActiveRow = sheet.id === state.activeSheetId;
            const suppressSheetListAnim =
              (isSheetSwitching || isSheetSwitchingRef.current) && !isDraggingToSheet;
            const counterDuration = isInitialLoading
              ? 600
              : suppressSheetListAnim
                ? 0
                : 200;
            const plainVisible = allPouchesAreTime
              ? `${formatDetailedNumber(sheetTotal)} (${formatSheetMinutesAsTime(sheetTotal)})`
              : formatDetailedNumber(sheetTotal);
            const layerLines = sheet.layers?.length
              ? buildSheetTotalLayerTooltipLines(sheet, sheet.pouches, sheetGroups)
              : [plainVisible];
            const sheetTotalTooltipFormatted =
              sheetTotal !== 0
                ? resolveLayerNumericTooltipHtml(sheet, plainVisible, layerLines) ??
                  (allPouchesAreTime
                    ? formatDetailedWithTimeTooltipHtml(sheetTotal)
                    : `<b>${(sheetTotal < 0 ? '-' : '') + Math.abs(sheetTotal).toLocaleString(undefined, { maximumFractionDigits: 8 })}</b>`)
                : '';
            const titleHtml = sheet.title ? `<b>${sheet.title}</b>` : '';

            return (
            <div 
              key={sheet.id}
              className={`sheet-item ${sheet.id === state.activeSheetId ? 'active' : ''} ${hoveredSheetId === sheet.id ? 'hovered-for-drag' : ''} ${(draggedGroupDataRef.current?.sourceSheetId === sheet.id || draggedPouchDataRef.current?.sourceSheetId === sheet.id) ? 'source-for-drag' : ''}`}
              tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
              onClick={(e) => handleSelectSheet(sheet.id, e)}
              onTouchEnd={(e) => handleSelectSheet(sheet.id, e)}
              onMouseEnter={() => handleSheetHover(sheet.id, true)}
              onMouseLeave={() => handleSheetHover(sheet.id, false)}
              onTouchStart={(e) => {
                // タッチ開始時の処理（ドラッグ中の場合のみ）
                if (isDraggingToSheet && (draggedGroupDataRef.current || draggedPouchDataRef.current)) {
                  handleSheetHover(sheet.id, true);
                }
              }}
              data-sheet-id={sheet.id}
            >
              <div className="sheet-item-content">
                <div className="sheet-title" data-tooltip-id="tt" data-tooltip-html={(() => {
                  const guideLines = isActiveRow
                    ? formatTooltip(`Click: ${tt.editSheetTitle}`)
                    : [formatTooltip(`Click: ${tt.selectSheet}`), formatTooltip(`Double Click: ${tt.editSheetTitle}`)].filter(Boolean).join('<br/>');
                  return [titleHtml, sheetTotalTooltipFormatted, guideLines].filter(Boolean).join('<br/>');
                })()}>
                  <input
                    type="text"
                    placeholder={t.placeholder.noTitle}
                    tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
                    value={sheet.title || ''}
                    onChange={(e) => handleSheetTitleChange(sheet.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => {
                      const target = e.currentTarget;
                      const wasFocused = document.activeElement === target;
                      if (!wasFocused) {
                        // 初回クリックの場合、フォーカス後に全選択
                        requestAnimationFrame(() => {
                          target.select();
                        });
                      }
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // モバイル環境では明示的にフォーカスを設定
                      const target = e.target as HTMLInputElement;
                      target.focus();
                      target.select();
                    }}
                    onCompositionStart={() => {
                      // IME入力開始時のフラグ設定
                      (window as any).isIMEComposing = true;
                    }}
                    onCompositionEnd={() => {
                      // IME入力終了時のフラグクリア
                      (window as any).isIMEComposing = false;
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      // IME入力中は全てのキー入力をスキップ
                      if ((window as any).isIMEComposing) {
                        return;
                      }
                      
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                  />
                </div>
                <div className="sheet-total-date" data-tooltip-id="tt" data-tooltip-html={(() => {
                  const guideLines = isActiveRow
                    ? ''
                    : formatTooltip(`Click: ${tt.selectSheet}`);
                  return [titleHtml, sheetTotalTooltipFormatted, guideLines].filter(Boolean).join('<br/>');
                })()}>
                  <span className='sheet-total'>
                    <LayerAwareCounter
                      key={`sheet-total-${sheet.id}`}
                      sheet={isActiveRow ? sheet : undefined}
                      value={Math.abs(sheetTotal)}
                      isNegative={sheetTotal < 0}
                      animateFromZero={isInitialLoading}
                      suppressTooltip
                      duration={counterDuration}
                      displayAsTime={allPouchesAreTime}
                      timeDisplayTag="strong"
                    />
                  </span>
                  <br/>
                  {new Date(sheet.createdAt).toLocaleString()}
                </div>
              </div>
              <button 
                className="duplicate-sheet-button"
                data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Click: ${tt.cloneSheet}`)}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDuplicateSheet(sheet.id, e);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  handleDuplicateSheet(sheet.id, e);
                }}
              >
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ 
                    width: 'var(--icon-size)', 
                    height: 'var(--icon-size)' 
                  }}
                >
                  <path d="M20 9h-9a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2z" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className="export-sheet-button"
                data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Click: ${tt.exportAndShareSheet}`)}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  handleExportSheet(sheet.id, e);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  handleExportSheet(sheet.id, e);
                }}
              >
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  stroke="var(--icon-color)"
                  style={{ 
                    width: 'var(--icon-size)', 
                    height: 'var(--icon-size)' 
                  }}
                >
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 6l-4-4-4 4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 2v13" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className="delete-sheet-button"
                data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Double Click: ${tt.deleteSheet}\n${getModifierKeys().shift}+Click: ${tt.deleteAllGroupsAndPouches}`)}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSheet(sheet.id, e);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  handleDeleteSheet(sheet.id, e);
                }}
              >
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ 
                    width: 'var(--icon-size)', 
                    height: 'var(--icon-size)' 
                  }}
                >
                  <path d="M3 6h18" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                  <line x1="10" y1="11" x2="10" y2="17" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></line>
                  <line x1="14" y1="11" x2="14" y2="17" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></line>
                </svg>
              </button>
            </div>
            );
          })}
        </div>
        
        {/* フッター */}
        <div className="left-sidebar-footer">
          <div className="snap-toggle-row">
            {/* グリッド切り替えスイッチ */}
            <div className="snap-toggle"
              data-tooltip-id="tt"
              data-tooltip-html={formatTooltip(isSnapEnabled ? `Click: ${tt.hideGrid}` : `Click: ${tt.showGrid}`)}
              onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setIsSnapEnabled(!isSnapEnabled); }}
            >
              <span className="snap-label">{ENGLISH_UI_LABELS.GRID}</span>
              <label className="snap-switch">
                <input
                  type="checkbox"
                  checked={isSnapEnabled}
                  onChange={(e) => setIsSnapEnabled(e.target.checked)}
                  tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
                />
                <span className="snap-slider"></span>
              </label>
            </div>

            {/* ガイド切り替えスイッチ */}
            <div className="snap-toggle guide-toggle"
              data-tooltip-id="tt"
              data-tooltip-html={
                isGuideEnabled
                  ? `${formatTooltip('Click')} <span class="tt-desc">${tt.hideTooltipGuide}</span>`
                  : `<span class="tt-desc">${tt.showTooltipGuide}</span>`
              }
              onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); (window as any).__isGuideEnabled = !isGuideEnabled; setIsGuideEnabled(!isGuideEnabled); }}
            >
              <span className="snap-label">{ENGLISH_UI_LABELS.GUIDE}</span>
              <label className="snap-switch">
                <input
                  type="checkbox"
                  checked={isGuideEnabled}
                  onChange={(e) => {
                    (window as any).__isGuideEnabled = e.target.checked;
                    setIsGuideEnabled(e.target.checked);
                  }}
                  tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
                />
                <span className="snap-slider"></span>
              </label>
            </div>
          </div>
          
          <p className="help-link">
            <button 
              tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
              onClick={() => openExternal('https://apps.tomippe.jp/pouches/guide-mini.pdf')} 
              onTouchEnd={(e) => { e.stopPropagation(); openExternal('https://apps.tomippe.jp/pouches/guide-mini.pdf'); }}
              data-tooltip-id="tt"
              data-tooltip-html={formatTooltip(`Click: ${tt.openQuickHelp}`)}
            >
              <svg 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t.help.quickHelp}
            </button>
            <button 
              tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
              onClick={() => openExternal('https://apps.tomippe.jp/pouches/guide.pdf')} 
              onTouchEnd={(e) => { e.stopPropagation(); openExternal('https://apps.tomippe.jp/pouches/guide.pdf'); }}
              data-tooltip-id="tt"
              data-tooltip-html={formatTooltip(`Click: ${tt.openInstruction}`)}
            >
              <svg 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t.help.instruction}
            </button></p>
          <p className="apps-logo"><a href="https://apps.tomippe.jp/" target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); openExternal('https://apps.tomippe.jp/'); }} onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); openExternal('https://apps.tomippe.jp/'); }} data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Click: ${tt.openAppsSite}`)}>
            <img src={`${process.env.PUBLIC_URL || ''}/assets/apps-logo.svg`} alt="StudioTomippe Apps" />
          </a></p>
          <p className="sidebar-feedback-link">
            <button
              type="button"
              tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
              onClick={() => void requestReviewFromMenu(locale)}
              onTouchEnd={(e) => {
                e.stopPropagation();
                void requestReviewFromMenu(locale);
              }}
            >
              {locale === 'ja' ? 'フィードバックを送る...' : locale === 'zh' ? '发送反馈...' : 'Send Feedback...'}
            </button>
          </p>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div 
        className="main-content"
      >
        {/* アクティブシートのタイトル入力欄 */}
        {activeSheet && (
          <div className="active-sheet-header-block">
            <div className="active-sheet-title">
              <input
                ref={activeSheetTitleInputRef}
                type="text"
                placeholder={t.placeholder.noTitle}
                tabIndex={1000}
                data-tooltip-id="tt"
                data-tooltip-html={isActiveSheetTitleFocused ? (activeSheetTitleEditTooltipHtml || undefined) : formatTooltip(`Click: ${tt.editSheetTitle}`)}
                value={activeSheet.title || ''}
                onFocus={() => setIsActiveSheetTitleFocused(true)}
                onBlur={() => setIsActiveSheetTitleFocused(false)}
                onChange={(e) => handleSheetTitleChange(activeSheet.id, e.target.value)}
                onMouseDown={(e) => {
                  const target = e.currentTarget;
                  const wasFocused = document.activeElement === target;
                  if (!wasFocused) {
                    requestAnimationFrame(() => {
                      target.select();
                    });
                  }
                }}
                onTouchEnd={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.focus();
                  target.select();
                }}
                onCompositionStart={() => {
                  (window as any).isIMEComposing = true;
                }}
                onCompositionEnd={() => {
                  (window as any).isIMEComposing = false;
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if ((window as any).isIMEComposing) {
                    return;
                  }
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
              />
            </div>
            <SheetLayerCalcTitleList
              sheet={activeSheet}
              onSelectLayer={handleSetActiveLayer}
              onAddLayer={handleAddSheetLayer}
              onRenameLayer={handleRenameSheetLayer}
              onRemoveLayer={handleRemoveSheetLayer}
              pendingFocusEditRef={pendingLayerNameEditRef}
            />
          </div>
        )}
        {activeSheet ? (
          <ActiveSheetComponent
            key={state.activeSheetId} // シート切り替え時にコンポーネントを再マウント
            ref={activeSheetRef}
            activeSheetId={state.activeSheetId}
            lastPouchValue={activeSheet.lastPouchValue || 0}
            lastPouchColor={activeSheet.lastPouchColor || POUCH_COLORS[0].id}
            lastGroupColor={activeSheet.lastGroupColor || GROUP_COLORS[0].id}
            initialPouches={pouchesForActiveSheet}
            initialGroups={activeSheet.groups}
            onUpdateActiveSheet={handleUpdateActiveSheet}
            defaultValue={state.settings.defaultValue}
            globalInteractionRef={globalInteractionRef}
            onRemoveGroupFromSourceSheet={removeGroupFromSourceSheet}
            sourceSheetGroupRef={sourceSheetGroupRef}
            draggedGroupDataRef={draggedGroupDataRef}
            onStartSheetPouchDrag={handleStartSheetPouchDrag}
            onRemovePouchFromSourceSheet={removePouchFromSourceSheet}
            sourceSheetPouchRef={sourceSheetPouchRef}
            draggedPouchDataRef={draggedPouchDataRef}
            onResetSheetDragState={resetSheetDragState}
            isInitialLoading={isInitialLoading}
            colorLegends={activeSheet.colorLegends || []}
            isTouchDevice={state.isTouchDevice}
            onUpdateDeviceType={handleUpdateDeviceType}
            onExportGroup={handleExportGroup}
            onToggleGroupSign={handleToggleGroupSign}
            currentSheet={activeSheet}
            onToggleBalancingPouch={handleToggleBalancingPouch}
            isDraggingToSheet={isDraggingToSheet}
            onStartSheetDrag={handleStartSheetDrag}
            onRequestFocusSheetTitle={() => {
              const el = activeSheetTitleInputRef.current;
              if (el) {
                el.focus();
                el.select();
              }
            }}
            isSheetTitleEditing={isActiveSheetTitleFocused}
            isMobile={state.isMobile}
            isLeftSidebarOpen={state.isLeftSidebarOpen}
            onTableHoverChange={(groupId, pouchId) => {
              // 前回の状態と同じ場合は何もしない
              const lastState = lastHoverStateRef.current;
              if (lastState.groupId === groupId && lastState.pouchId === pouchId) {
                return;
              }
              
              // 前回の状態を更新
              lastHoverStateRef.current = {groupId, pouchId};
              
              // 計算エリアからのhover状態をテーブルに反映
              // まず既存のhoverクラスをクリア
              document.querySelectorAll('.hover').forEach(el => {
                el.classList.remove('hover');
              });
              
              // 新しいhover状態を適用
              if (groupId) {
                // グループの場合、グループヘッダー行のみにhoverクラスを付与
                const groupRows = document.querySelectorAll(`tr[data-group-id="${groupId}"]:not([data-pouch-id])`);
                groupRows.forEach(el => {
                  el.classList.add('hover');
                });
              } else if (pouchId) {
                // パウチの場合、パウチ行にhoverクラスを付与
                const pouchRows = document.querySelectorAll(`tr[data-pouch-id="${pouchId}"]`);
                pouchRows.forEach(el => {
                  el.classList.add('hover');
                });
              }
            }}
          />
        ) : (
          <div className="empty-state">
            <p>{t.empty.selectOrCreate}</p>
          </div>
        )}
        
        <div className="footer">
          {/* 凡例コンポーネント */}
          <LegendsComponent
            pouches={activeSheet?.pouches || []}
            groups={activeSheet?.groups || []}
            colorLegends={activeSheet?.colorLegends || []}
            activeSheetId={state.activeSheetId}
            isSheetSwitching={isSheetSwitchingRef.current}
            currentSheet={activeSheet}
            onUpdateColorLegend={handleUpdateColorLegend}
            calculateColorTotal={handleCalculateColorTotal}
            calculateColorCount={handleCalculateColorCount}
            onEditConfirm={handleEditConfirm}
            onLegendHoverChange={(colorId) => {
              // 凡例ホバー状態の変更をActiveSheetComponentに伝える
              if (activeSheetRef.current) {
                // まず既存のlegend-hoveredクラスをクリア
                document.querySelectorAll('.legend-hovered').forEach(el => {
                  el.classList.remove('legend-hovered');
                });
                
                // 色IDが指定されている場合、該当する色のパウチとテーブル行にクラスを追加
                if (colorId) {
                  // パウチコンテナにクラスを追加
                  document.querySelectorAll(`.pouch-container[data-pouch-id]`).forEach(element => {
                    const pouchId = element.getAttribute('data-pouch-id');
                    if (pouchId) {
                      const pouch = activeSheet?.pouches.find(p => p.id === pouchId);
                      if (pouch && pouch.color === colorId) {
                        element.classList.add('legend-hovered');
                      }
                    }
                  });
                  
                  // テーブル行（tr）にクラスを追加
                  document.querySelectorAll(`tr[data-pouch-id]`).forEach(element => {
                    const pouchId = element.getAttribute('data-pouch-id');
                    if (pouchId) {
                      const pouch = activeSheet?.pouches.find(p => p.id === pouchId);
                      if (pouch && pouch.color === colorId) {
                        element.classList.add('legend-hovered');
                      }
                    }
                  });
                }
              }
            }}
          />
          
          <div 
            className={`total-container ${isTotalExpanded ? 'expanded' : ''}`}
            onClick={() => setIsTotalExpanded(!isTotalExpanded)}
            onTouchEnd={(e) => {
              // e.stopPropagation();
              setIsTotalExpanded(!isTotalExpanded);
            }}
          >
            <span className="total-toggle-icon">
              <svg viewBox="0 0 24 24" width="20" height="20">
                  <path d="M10 7l5 5-5 5z" />
              </svg>
            </span>
            <div className="total-item">
              <div className={`total-display${activeSheet?.balancingPouchId ? ' balancing-active' : ''}`}
                data-tooltip-id="tt"
                data-tooltip-html={(() => {
                  const isBp = !!(activeSheet?.balancingPouchId && balancingTargetForDisplay !== undefined);
                  const allTime = footerStatsShowsTime;
                  if (isBp) {
                    const target = balancingTargetForDisplay!;
                    const diff = snapBalancingTotalDiff(stats.total, target);
                    return formatBalancingEquationTooltip(target, diff, stats.total, allTime);
                  }
                  const plainVisible = allTime
                    ? `${formatDetailedNumber(stats.total)} (${formatSheetMinutesAsTime(stats.total)})`
                    : formatDetailedNumber(stats.total);
                  const layerLines =
                    activeSheet?.layers?.length
                      ? buildStatsTotalLayerTooltipLines(
                          activeSheet,
                          activeSheet.pouches,
                          activeSheet.groups ?? []
                        )
                      : [plainVisible];
                  return (
                    resolveLayerNumericTooltipHtml(activeSheet, plainVisible, layerLines) ??
                    `<b>${plainVisible}</b>`
                  );
                })()}
                onMouseEnter={() => {
                  if (activeSheet?.balancingPouchId) {
                    document.querySelectorAll(`[data-pouch-id="${activeSheet.balancingPouchId}"]`).forEach(el => el.classList.add('balancing-pouch-hovered'));
                  }
                }}
                onMouseLeave={() => {
                  document.querySelectorAll('.balancing-pouch-hovered').forEach(el => el.classList.remove('balancing-pouch-hovered'));
                }}
              >
                <span>{t.stats.total}<svg className={`balancing-lock-icon${activeSheet?.balancingPouchId ? ' visible' : ''}`} viewBox="0 0 24 24" width="20" height="20" fill="currentColor"
                  data-tooltip-id="tt"
                  data-tooltip-html={activeSheet?.balancingPouchId && balancingTargetForDisplay !== undefined ? (() => {
                    const target = balancingTargetForDisplay!;
                    const diff = snapBalancingTotalDiff(stats.total, target);
                    const allTime = footerStatsShowsTime;
                    const info = formatBalancingEquationTooltip(target, diff, stats.total, allTime);
                    const guide = formatTooltip(`Click: ${tt.unlockTotalAndDisableBalancing}`);
                    return guide ? `${info}<br/>${guide}` : info;
                  })() : undefined}
                  onClick={(e) => { e.stopPropagation(); if (activeSheet?.balancingPouchId) handleToggleBalancingPouch(activeSheet.balancingPouchId); }}><path d="M16 11V7A4 4 0 0 0 8 7V11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/><rect x="5" y="11" width="14" height="10" rx="1.5"/></svg></span> 
                {(() => {
                  const isBpActive = !!(activeSheet?.balancingPouchId && balancingTargetForDisplay !== undefined);
                  const bpDiff = isBpActive ? snapBalancingTotalDiff(stats.total, balancingTargetForDisplay!) : 0;
                  const displayValue = isBpActive ? balancingTargetForDisplay! : stats.total;

                  const allPouchesAreTime = footerStatsShowsTime;

                  const bpTooltipHtml = isBpActive
                    ? formatBalancingEquationTooltip(
                        balancingTargetForDisplay!,
                        bpDiff,
                        stats.total,
                        allPouchesAreTime
                      )
                    : undefined;

                  const totalCounter = (
                    <LayerAwareCounter
                      key={`total-${state.activeSheetId}`}
                      sheet={activeSheet}
                      value={Math.abs(displayValue)}
                      isNegative={displayValue < 0}
                      animateFromZero={isInitialLoading || isSheetSwitchingRef.current}
                      duration={isInitialLoading || isSheetSwitchingRef.current ? 600 : 200}
                      suppressTooltip
                      displayAsTime={allPouchesAreTime}
                      timeDisplayTag="strong"
                      showTimeConversion={false}
                    />
                  );
                  const diffSmall =
                    bpDiff !== 0 ? (
                      <small className={`balancing-diff${displayValue < 0 ? ' negative' : ''}`}>
                        {allPouchesAreTime
                          ? `${bpDiff >= 0 ? '+' : '-'}${formatSheetMinutesAsTime(Math.abs(bpDiff))}`
                          : `${bpDiff > 0 ? '+' : ''}${formatBodyNumber2dp(bpDiff)}`}
                      </small>
                    ) : null;
                  const timeDiffMain =
                    bpDiff !== 0 ? (
                      <span className={`balancing-diff${displayValue < 0 ? ' negative' : ''}`}>
                        {bpDiff >= 0 ? '+' : '-'}
                        {formatSheetMinutesAsTime(Math.abs(bpDiff))}
                      </span>
                    ) : null;
                  const numericDiffSmall =
                    bpDiff !== 0 ? (
                      <span className={`balancing-diff compact${displayValue < 0 ? ' negative' : ''}`}>
                        {bpDiff > 0 ? '+' : ''}
                        {formatBodyNumber2dp(bpDiff)}
                      </span>
                    ) : null;
                  const unifiedTotalTooltipHtml =
                    allPouchesAreTime
                      ? (bpTooltipHtml ?? formatDetailedWithTimeTooltipHtml(displayValue))
                      : bpTooltipHtml;

                  if (allPouchesAreTime) {
                    if (isBpActive) {
                      return (
                        <span {...(unifiedTotalTooltipHtml ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': unifiedTotalTooltipHtml } : {})}>
                          {totalCounter}
                          {timeDiffMain}
                          <small className="balancing-time-numeric-hint">
                            <LayerAwareCounter
                              key={`total-hint-${state.activeSheetId}`}
                              sheet={activeSheet}
                              value={Math.abs(displayValue)}
                              isNegative={displayValue < 0}
                              className="balancing-numeric-total"
                              animateFromZero={isInitialLoading || isSheetSwitchingRef.current}
                              duration={isInitialLoading || isSheetSwitchingRef.current ? 600 : 200}
                              suppressTooltip={!!bpTooltipHtml}
                            />
                            {numericDiffSmall}
                          </small>
                        </span>
                      );
                    }
                    return (
                      <span {...(unifiedTotalTooltipHtml ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': unifiedTotalTooltipHtml } : {})}>
                        {totalCounter}
                        {diffSmall}
                      </span>
                    );
                  } else {
                    return (
                      <span {...(unifiedTotalTooltipHtml ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': unifiedTotalTooltipHtml } : {})}>
                        {totalCounter}{diffSmall}
                      </span>
                    );
                  }
                })()}
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-display">
                <span>{t.stats.count}</span>
                <LayerAwareCounter
                  key={`count-${state.activeSheetId}`}
                  sheet={activeSheet}
                  value={stats.count}
                  animateFromZero={isInitialLoading || isSheetSwitchingRef.current}
                  duration={isInitialLoading || isSheetSwitchingRef.current ? 600 : 200}
                />
              </div>
            </div>
            <div className="stat-item">
              <div className='stat-display'>
                <span>{t.stats.average}</span>
                {renderFooterStatCounter(
                  'average',
                  stats.average,
                  footerStatsShowsTime ? formatDetailedWithTimeTooltipHtml(stats.average) : undefined
                )}
              </div>
            </div>
            <div 
              className="stat-item"
              onMouseEnter={() => {
                // MAX値と同じ値を持つパウチとテーブル行にクラスを追加
                if (activeSheet?.pouches && activeSheet.pouches.length > 0) {
                  // まず既存のmax-hoveredクラスをクリア
                  document.querySelectorAll('.max-hovered').forEach(el => {
                    el.classList.remove('max-hovered');
                  });
                  
                  // シートの全パウチが時間の場合の判定（GTP は参照先が全て時間なら含む）
                  const allPouchesAreTime = footerStatsShowsTime;
                  
                  // MAX値と同じ値を持つパウチを探す
                  pouchesForActiveLayer.forEach(pouch => {
                    let pouchValue;
                    if (allPouchesAreTime) {
                      // 時間パウチの場合はactualValue（分数）で比較
                      pouchValue = getPouchActualValue(pouch, activeSheet.groups || []);
                    } else {
                      // 通常の場合は掛け算モードのbaseValue、割り算モードのeffectiveValueで比較
                      if (pouch.multiplier < 0) {
                        pouchValue = getPouchActualValue(pouch, activeSheet.groups || []);
                      } else {
                        pouchValue = pouch.value;
                      }
                    }
                    
                    // 値が一致する場合（浮動小数点の誤差を考慮）
                    if (Math.abs(pouchValue - stats.max) < 0.0000001) {
                      // パウチコンテナにクラスを追加
                      const pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouch.id}"]`);
                      if (pouchElement) {
                        pouchElement.classList.add('max-hovered');
                      }
                      
                      // テーブル行にクラスを追加
                      const rowElement = document.querySelector(`tr[data-pouch-id="${pouch.id}"]`);
                      if (rowElement) {
                        rowElement.classList.add('max-hovered');
                      }
                    }
                  });
                }
              }}
              onMouseLeave={() => {
                // max-hoveredクラスをクリア
                document.querySelectorAll('.max-hovered').forEach(el => {
                  el.classList.remove('max-hovered');
                });
              }}
            >
              <div className='stat-display'>
                <span>{t.stats.max}</span>
                {renderFooterStatCounter(
                  'max',
                  stats.max,
                  footerStatsShowsTime ? formatDetailedWithTimeTooltipHtml(stats.max) : undefined
                )}
              </div>
            </div>
            <div 
              className="stat-item"
              onMouseEnter={() => {
                // MIN値と同じ値を持つパウチとテーブル行にクラスを追加
                if (activeSheet?.pouches && activeSheet.pouches.length > 0) {
                  // まず既存のmin-hoveredクラスをクリア
                  document.querySelectorAll('.min-hovered').forEach(el => {
                    el.classList.remove('min-hovered');
                  });
                  
                  // シートの全パウチが時間の場合の判定（GTP は参照先が全て時間なら含む）
                  const allPouchesAreTime = footerStatsShowsTime;
                  
                  // MIN値と同じ値を持つパウチを探す
                  pouchesForActiveLayer.forEach(pouch => {
                    let pouchValue;
                    if (allPouchesAreTime) {
                      // 時間パウチの場合はactualValue（分数）で比較
                      pouchValue = getPouchActualValue(pouch, activeSheet.groups || []);
                    } else {
                      // 通常の場合は掛け算モードのbaseValue、割り算モードのeffectiveValueで比較
                      if (pouch.multiplier < 0) {
                        pouchValue = getPouchActualValue(pouch, activeSheet.groups || []);
                      } else {
                        pouchValue = pouch.value;
                      }
                    }
                    
                    // 値が一致する場合（浮動小数点の誤差を考慮）
                    if (Math.abs(pouchValue - stats.min) < 0.0000001) {
                      // パウチコンテナにクラスを追加
                      const pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouch.id}"]`);
                      if (pouchElement) {
                        pouchElement.classList.add('min-hovered');
                      }
                      
                      // テーブル行にクラスを追加
                      const rowElement = document.querySelector(`tr[data-pouch-id="${pouch.id}"]`);
                      if (rowElement) {
                        rowElement.classList.add('min-hovered');
                      }
                    }
                  });
                }
              }}
              onMouseLeave={() => {
                // min-hoveredクラスをクリア
                document.querySelectorAll('.min-hovered').forEach(el => {
                  el.classList.remove('min-hovered');
                });
              }}
            >
              <div className='stat-display'>
                <span>{t.stats.min}</span>
                {renderFooterStatCounter(
                  'min',
                  stats.min,
                  footerStatsShowsTime ? formatDetailedWithTimeTooltipHtml(stats.min) : undefined
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* 右サイドバー */}
      <div className={`right-sidebar ${state.isRightSidebarOpen ? 'open' : 'closed'}`}>
        <div 
          className="right-sidebar-handle"
          onClick={toggleSidebar}
          onTouchEnd={toggleSidebar}
          data-tooltip-id="tt"
          data-tooltip-html={formatTooltip(state.isRightSidebarOpen ? `Click: ${tt.closeHistoryAndTable}` : `Click: ${tt.openHistoryAndTable}`)}
        >
          <svg 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ 
              width: 'var(--icon-size)', 
              height: 'var(--icon-size)' 
            }}
          >
            {state.isRightSidebarOpen ? (
              <path d="M9 18L15 12L9 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            ) : (
              <path d="M15 18L9 12L15 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            )}
          </svg>
        </div>
        
        {/* 履歴パネル - 常にレンダリング */}
        {activeSheet && (
            <div className="history">
              <HistoryComponent
                sheet={activeSheet}
                pouches={activeSheet.pouches}
                groups={activeSheet.groups}
                onStateUpdate={handleHistoryStateUpdate}
                isOpen={state.isRightSidebarOpen}
              />
            </div>
        )}
        
        {/* テーブル表示 - 開いているときまたは完全に閉じていないときのみレンダリング */}
        <div className="table">
          {activeSheet && (state.isRightSidebarOpen || !state.isRightSidebarFullyClosed) && (
              <TableComponent 
              isOpen={state.isRightSidebarOpen}
              onToggle={() => dispatch({ type: 'TOGGLE_SIDEBAR' })}
              groups={activeSheet.groups}
              pouches={activeSheet.pouches}
              colorLegends={activeSheet.colorLegends || []}
              currentSheet={activeSheet}
              onClearCanvasHover={() => {
                activeSheetRef.current?.clearCanvasHover();
              }}
              onHoverGroupChange={(groupId, pouchId) => {
                // ホバー状態の変更をActiveSheetComponentに伝える
                if (activeSheetRef.current) {
                  // まず既存のホバー状態をクリア
                  document.querySelectorAll('.table-hovered').forEach(el => {
                    el.classList.remove('table-hovered');
                  });
                  
                  // グループの場合
                  if (groupId) {
                    // data-group-id属性を持つすべての要素を検索
                    document.querySelectorAll(`[data-group-id="${groupId}"]`).forEach(el => {
                      el.classList.add('table-hovered');
                    });
                  }
                  
                  // パウチの場合
                  if (pouchId) {
                    // data-pouch-id属性を持つすべての要素を検索
                    document.querySelectorAll(`[data-pouch-id="${pouchId}"]`).forEach(el => {
                      el.classList.add('table-hovered');
                    });
                  }
                }
              }}
              onSelectionChange={(groupIds, pouchIds) => {
                // テーブル選択状態の変更をActiveSheetComponentに伝える
                if (activeSheetRef.current) {
                  // まず既存の選択状態をクリア
                  document.querySelectorAll('.table-selected').forEach(el => {
                    el.classList.remove('table-selected');
                  });
                  
                  // グループの選択状態を更新
                  for (const groupId of groupIds) {
                    // グループIDが一致する要素を選択
                    document.querySelectorAll(`.group-wrapper[data-group-id="${groupId}"]`).forEach(el => {
                      el.classList.add('table-selected');
                    });
                  }
                  
                  // パウチの選択状態を更新
                  for (const pouchId of pouchIds) {
                    // パウチIDが一致する要素を選択
                    document.querySelectorAll(`.pouch-container[data-pouch-id="${pouchId}"]`).forEach(el => {
                      el.classList.add('table-selected');
                    });
                  }
                }
              }}
              onUpdateGroupTitle={async (groupId, title) => {
                // グループタイトルを更新
                if (activeSheet) {
                  const updatedGroups = activeSheet.groups.map(group => 
                    group.id === groupId ? { ...group, title } : group
                  );
                  
                  // handleUpdateActiveSheetを呼び出して保存
                  await handleUpdateActiveSheet(null, updatedGroups, false, true, undefined, HistoryActionType.EDIT_GROUP_TITLE);
                }
              }}
              onUpdatePouchTitle={async (pouchId, title) => {
                // パウチタイトルを更新
                if (activeSheet) {
                  const updatedPouches = activeSheet.pouches.map(pouch => 
                    pouch.id === pouchId ? { ...pouch, title } : pouch
                  );
                  
                  // handleUpdateActiveSheetを呼び出して保存
                  await handleUpdateActiveSheet(updatedPouches, null, false, true, undefined, HistoryActionType.EDIT_POUCH_TITLE);
                }
              }}
              onSetActiveLayer={handleSetActiveLayer}
              onAddSheetLayer={handleAddSheetLayer}
              onRenameSheetLayer={handleRenameSheetLayer}
              onRemoveSheetLayer={handleRemoveSheetLayer}
              tableScrollRef={tableScrollRef}
              onUpdatePouchValue={async (pouchId, value, multiplier?, historyActionType?, forceNewEntry?, lastExpression?, valueWhenLastExpression?, multiplyWhenLastExpression?, isTime?: boolean, roundUnit?: number | null, roundUnitWhenLastExpression?: number, layerId?: string) => {
                // パウチ値を更新
                if (activeSheet) {
                  const lid = layerId ?? resolveActiveLayerId(activeSheet);
                  const updatedPouches = activeSheet.pouches.map(pouch => {
                    if (pouch.id === pouchId) {
                      const useLayerValues = pouchUsesLayerValues(activeSheet, pouch);
                      const isTimeLayer = getPouchLayerIsTime(pouch, lid);
                      const gtpMult = multiplier ?? (isTimeLayer
                        ? getPouchLayerMultiplier(pouch, lid)
                        : pouch.multiplier);
                      const timeFlag = isTime !== undefined ? isTime : (isTimeLayer ? true : !!pouch.isTime);
                      const storeMultInTimeLayer = !!timeFlag;
                      const currentRu = useLayerValues
                        ? getPouchLayerRoundUnit(pouch, lid)
                        : pouch.roundUnit;
                      const nextRoundUnit = clampRoundUnitToScaledCap(
                        roundUnit === null
                          ? undefined
                          : roundUnit !== undefined
                            ? roundUnit
                            : currentRu,
                        pouch,
                        value,
                        gtpMult,
                      );
                      const nextRuWhenLast =
                        roundUnitWhenLastExpression !== undefined
                          ? roundUnitWhenLastExpression
                          : roundUnit !== undefined
                            ? undefined
                            : useLayerValues
                              ? getPouchLayerRoundUnitWhenLastExpression(pouch, lid)
                              : pouch.roundUnitWhenLastExpression;

                      if (pouch.isGroupTotalPouch && historyActionType === HistoryActionType.EDIT_POUCH_VALUE) {
                        return {
                          ...pouch,
                          value,
                          multiplier: gtpMult,
                          isNegative: value < 0,
                          isTime: isTime !== undefined ? isTime : pouch.isTime,
                          isGroupTotalPouch: false,
                          referencedGroupId: undefined,
                          oppositeToTotal: undefined,
                          lastExpression: lastExpression ?? pouch.lastExpression,
                          valueWhenLastExpression: valueWhenLastExpression ?? pouch.valueWhenLastExpression,
                          multiplyWhenLastExpression: multiplyWhenLastExpression ?? pouch.multiplyWhenLastExpression,
                          roundUnit: nextRoundUnit,
                          roundUnitWhenLastExpression: nextRuWhenLast,
                        };
                      }

                      const base = {
                        ...pouch,
                        value,
                        ...(storeMultInTimeLayer ? {} : { multiplier: gtpMult }),
                        isNegative: value < 0,
                        isTime: timeFlag,
                        lastExpression: lastExpression ?? pouch.lastExpression,
                        valueWhenLastExpression: valueWhenLastExpression ?? pouch.valueWhenLastExpression,
                        multiplyWhenLastExpression: multiplyWhenLastExpression ?? pouch.multiplyWhenLastExpression,
                        roundUnit: nextRoundUnit,
                        roundUnitWhenLastExpression: nextRuWhenLast,
                      };
                      return setPouchLayerValue(
                        base,
                        lid,
                        value,
                        {
                          lastExpression: base.lastExpression,
                          valueWhenLastExpression: base.valueWhenLastExpression,
                          roundUnit: nextRoundUnit,
                          roundUnitWhenLastExpression: nextRuWhenLast,
                          ...(isTime !== undefined ? { isTime } : {}),
                          ...(multiplier !== undefined && storeMultInTimeLayer ? { multiplier: gtpMult } : {}),
                        },
                        activeSheet
                      );
                    }
                    return pouch;
                  });

                  logSheetPouchValues(
                    'pouch-value:input',
                    { ...activeSheet, activeLayerId: lid },
                    updatedPouches
                  );
                  
                  await handleUpdateActiveSheet(
                    updatedPouches,
                    null,
                    false,
                    true,
                    undefined,
                    historyActionType ?? HistoryActionType.EDIT_POUCH_VALUE,
                    forceNewEntry
                  );
                }
              }}
              onUpdatePouchMultiplier={async (pouchId, multiplier) => {
                if (activeSheet) {
                  const lid = resolveActiveLayerId(activeSheet);
                  const updatedPouches = activeSheet.pouches.map(pouch => {
                    if (pouch.id !== pouchId) return pouch;
                    if (pouchUsesLayerValues(activeSheet, pouch) && getPouchLayerIsTime(pouch, lid)) {
                      return setPouchLayerValue(
                        pouch,
                        lid,
                        getLayerValue(pouch, lid),
                        { multiplier },
                        activeSheet,
                      );
                    }
                    return syncPouchValueMirror({ ...pouch, multiplier }, lid, activeSheet);
                  });

                  await handleUpdateActiveSheet(updatedPouches, null, false, true, undefined, HistoryActionType.EDIT_POUCH_VALUE);
                }
              }}
              onSetPouchError={(pouchId, hasError) => {
                // ActiveSheetComponentを通じてエラー状態を管理
                if (activeSheetRef.current) {
                  activeSheetRef.current.setError(pouchId, hasError);
                }
              }}
              onBulkUpdateTitles={async (updates) => {
                // 一括でタイトルをクリア
                if (activeSheet) {
                  let updatedGroups = activeSheet.groups;
                  let updatedPouches = activeSheet.pouches;
                  
                  // グループタイトルをクリア
                  if (updates.groupIds.length > 0) {
                    updatedGroups = activeSheet.groups.map(group => 
                      updates.groupIds.includes(group.id) ? { ...group, title: '' } : group
                    );
                  }
                  
                  // パウチタイトルをクリア
                  if (updates.pouchIds.length > 0) {
                    updatedPouches = activeSheet.pouches.map(pouch => 
                      updates.pouchIds.includes(pouch.id) ? { ...pouch, title: '' } : pouch
                    );
                  }
                  
                  // 一度に保存（グループとパウチの両方に変更がある場合も対応）
                  await handleUpdateActiveSheet(
                    updates.pouchIds.length > 0 ? updatedPouches : null,
                    updates.groupIds.length > 0 ? updatedGroups : null,
                    false,
                    true,
                    undefined,
                    updates.groupIds.length > 0 ? HistoryActionType.EDIT_GROUP_TITLE : HistoryActionType.EDIT_POUCH_TITLE
                  );
                }
              }}
              onBulkUpdate={async (updates) => {
                // 一括で複数のプロパティを更新
                if (activeSheet) {
                  let updatedGroups = activeSheet.groups;
                  let updatedPouches = activeSheet.pouches;
                  
                  // 完全なシートデータが渡された場合は直接使用
                  if ((updates as any).completeGroups) {
                    updatedGroups = (updates as any).completeGroups;
                  }
                  if ((updates as any).completePouches) {
                    updatedPouches = (updates as any).completePouches;
                    
                    // ペースト履歴タイプを取得（デフォルトはEDIT_POUCH_VALUE）
                    const pasteHistoryType = (updates as any).pasteHistoryType || HistoryActionType.EDIT_POUCH_VALUE;
                    
                    // 一度に保存（ペースト時は常に追加モード）
                    await handleUpdateActiveSheet(
                      updatedPouches,
                      updatedGroups,
                      false,
                      true,
                      undefined,
                      pasteHistoryType,
                      true // forceNewEntry: ペースト時は常に新しい履歴エントリを作成
                    );
                    return;
                  }
                  
                  // 削除処理を最初に実行
                  if (updates.deletedGroups && updates.deletedGroups.length > 0) {
                    updatedGroups = activeSheet.groups.filter(group => !updates.deletedGroups!.includes(group.id));
                  }
                  
                  if (updates.deletedPouches && updates.deletedPouches.length > 0) {
                    updatedPouches = activeSheet.pouches.filter(pouch => !updates.deletedPouches!.includes(pouch.id));
                  }
                  
                  // 削除処理がある場合はアニメーションフラグを適用
                  if ((updates.deletedGroups && updates.deletedGroups.length > 0) || 
                      (updates.deletedPouches && updates.deletedPouches.length > 0)) {
                    const animationResult = HistoryLogic.applyAnimationFlags(
                      activeSheet.pouches,
                      updatedPouches,
                      activeSheet.groups,
                      updatedGroups
                    );
                    
                    // アニメーション用の状態を即座に更新（削除フラグ付き）
                    // ActiveSheetComponentに直接アニメーション状態を渡す
                    if (activeSheetRef.current) {
                      activeSheetRef.current.setPouches(animationResult.pouches);
                      activeSheetRef.current.setGroups(animationResult.groups);
                    }
                    
                    // 300ms後に最終状態に更新（削除フラグを除去）
                    setTimeout(() => {
                      const cleanFinalPouches = updatedPouches.map(p => {
                        const cleanPouch = { ...p };
                        const pouchAny = cleanPouch as any;
                        delete pouchAny._isDeleting;
                        delete pouchAny._animateFromZero;
                        return cleanPouch;
                      });
                      
                      const cleanFinalGroups = updatedGroups.map(g => {
                        const cleanGroup = { ...g };
                        const groupAny = cleanGroup as any;
                        delete groupAny._isDeleting;
                        delete groupAny._isAdding;
                        return cleanGroup;
                      });
                      
                      // 最終状態でhandleUpdateActiveSheetを呼び出し
                      handleUpdateActiveSheet(
                        cleanFinalPouches,
                        cleanFinalGroups,
                        false,
                        true,
                        undefined,
                        HistoryActionType.DELETE_GROUP // 削除の履歴アクションタイプ
                      );
                    }, 300);
                    
                    return; // ここで処理を終了
                  }
                  
                  // グループタイトルを更新
                  if (updates.groupTitles && updates.groupTitles.length > 0) {
                    updatedGroups = updatedGroups.map(group => {
                      const groupUpdate = updates.groupTitles!.find(gt => gt.groupId === group.id);
                      return groupUpdate ? { ...group, title: groupUpdate.title } : group;
                    });
                  }
                  
                  // パウチ関連の更新を統合処理
                  const pouchUpdates = new Map<string, Partial<Pouch>>();
                  
                  // パウチタイトルを更新
                  if (updates.pouchTitles && updates.pouchTitles.length > 0) {
                    updates.pouchTitles.forEach(update => {
                      pouchUpdates.set(update.pouchId, { ...pouchUpdates.get(update.pouchId), title: update.title });
                    });
                  }
                  
                  // パウチ値を更新
                  if (updates.pouchValues && updates.pouchValues.length > 0) {
                    updates.pouchValues.forEach(update => {
                      const currentPouch = updatedPouches.find(p => p.id === update.pouchId);
                      if (!currentPouch) return;
                      const layerId =
                        (update as { layerId?: string }).layerId ??
                        resolveActiveLayerId(activeSheet);
                      const merged = applyPouchScalarForSheet(
                        { ...currentPouch, ...pouchUpdates.get(update.pouchId) },
                        update.value,
                        activeSheet,
                        {
                          multiplier: update.multiplier,
                          clearExpression: update.lastExpression === '',
                        }
                      );
                      const updateData = {
                        ...merged,
                        multiplyWhenLastExpression: update.multiplyWhenLastExpression,
                      };
                      if ((update as { _animateSignChange?: boolean })._animateSignChange) {
                        (updateData as Pouch & { _animateSignChange?: boolean })._animateSignChange =
                          true;
                      }
                      pouchUpdates.set(update.pouchId, updateData);
                    });
                  }
                  
                  // マルチプライヤーを更新
                  if (updates.pouchMultipliers && updates.pouchMultipliers.length > 0) {
                    updates.pouchMultipliers.forEach(update => {
                      pouchUpdates.set(update.pouchId, { ...pouchUpdates.get(update.pouchId), multiplier: update.multiplier });
                    });
                  }
                  
                  // パウチ更新を適用
                  if (pouchUpdates.size > 0) {
                    updatedPouches = updatedPouches.map(pouch => {
                      const pouchUpdate = pouchUpdates.get(pouch.id);
                      if (pouchUpdate) {
                        const result = { ...pouch, ...pouchUpdate };
                        return result;
                      }
                      return pouch;
                    });
                  }
                  
                  // 一度に保存
                  await handleUpdateActiveSheet(
                    updatedPouches,
                    updatedGroups,
                    false,
                    true,
                    undefined,
                    HistoryActionType.EDIT_POUCH_VALUE // デフォルトの履歴アクションタイプ
                  );
                }
              }}
              onChangeColor={async (pouchId, isShiftPressed = false) => {
                // テーブルからの色変更処理
                if (activeSheet) {
                  // 色を変更するパウチを見つける
                  const targetPouch = activeSheet.pouches.find(p => p.id === pouchId);
                  if (!targetPouch) return;
                  
                  // 現在の色のインデックスを取得
                  const currentColorIndex = POUCH_COLORS.findIndex(color => color.id === targetPouch.color);
                  
                  let nextColorId: string;
                  
                  if (isShiftPressed) {
                    // Shift+クリック: 逆ローテーション
                    if (currentColorIndex === 0) {
                      nextColorId = POUCH_COLORS[POUCH_COLORS.length - 1].id;
                    } else {
                      const prevColorIndex = (currentColorIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
                      nextColorId = POUCH_COLORS[prevColorIndex].id;
                    }
                  } else {
                    // 通常クリック: 通常のローテーション
                    const nextColorIndex = (currentColorIndex + 1) % POUCH_COLORS.length;
                    nextColorId = currentColorIndex === -1 
                      ? POUCH_COLORS[0].id 
                      : POUCH_COLORS[nextColorIndex].id;
                  }
                  
                  // パウチの色を更新
                  const updatedPouches = activeSheet.pouches.map(pouch => {
                    if (pouch.id === pouchId) {
                      return {
                        ...pouch,
                        color: nextColorId
                      };
                    }
                    return pouch;
                  });
                  
                  // handleUpdateActiveSheetを呼び出して保存
                  await handleUpdateActiveSheet(updatedPouches, null, false, true, undefined, HistoryActionType.CHANGE_POUCH_COLOR);
                }
              }}
              onChangeGroupColor={async (groupId, isShiftPressed = false, isCtrlOption = false, skipMute = false) => {
                if (activeSheet) {
                  let updatedGroups: Group[];
                  if (isCtrlOption && !skipMute) {
                    const targetGroup = activeSheet.groups.find(g => g.id === groupId);
                    if (targetGroup) {
                      if (targetGroup.color === 'mute') {
                        updatedGroups = activeSheet.groups.map(g =>
                          g.id === groupId ? { ...g, color: g.originalColor || 'yellow' } : g
                        );
                      } else {
                        updatedGroups = activeSheet.groups.map(g =>
                          g.id === groupId ? { ...g, color: 'mute', originalColor: g.color } : g
                        );
                      }
                    } else {
                      return;
                    }
                  } else if (isShiftPressed) {
                    updatedGroups = changeGroupColor(groupId, activeSheet.groups, true, skipMute);
                  } else {
                    updatedGroups = changeGroupColor(groupId, activeSheet.groups, false, skipMute);
                  }
                  await handleUpdateActiveSheet(null, updatedGroups, false, true, undefined, HistoryActionType.CHANGE_GROUP_COLOR);
                }
              }}
              isTouchDevice={state.isTouchDevice}
              isInitialLoading={isInitialLoading}
              isSheetSwitching={isSheetSwitchingRef.current}
            />
          )}
        </div>
      </div>

      {billingPaywallEnabled ? (
        <PaywallModal
          open={paywallOpen}
          busy={paywallBusy}
          sheetCount={state.sheets.length}
          entitlement={entitlementStatus}
          errorMessage={paywallError}
          redeemCodeInput={paywallRedeemCodeInput}
          onRedeemCodeInputChange={setPaywallRedeemCodeInput}
          onClose={() => {
            setPaywallOpen(false);
            setPaywallRedeemCodeInput('');
          }}
          onPurchase={handlePaywallPurchase}
          onRestore={handlePaywallRestore}
          onManage={handlePaywallManage}
          onRedeem={() => void handlePaywallRedeem()}
          onOpenWebRedeemIssue={() => {
            setPaywallOpen(false);
            setWebRedeemIssueOpen(true);
          }}
          onOpenStoreProduct={() => void handlePaywallOpenStore()}
          showStoreFallbackLink={isWindowsStorePurchasePlatform()}
        />
      ) : null}

      {isNativeBillingPaywallPlatform() ? (
        <WebRedeemIssueModal
          open={webRedeemIssueOpen}
          onClose={() => setWebRedeemIssueOpen(false)}
        />
      ) : null}

      {/* カスタム確認ダイアログ（PWA対応） */}
      {showImportConfirm && (
        <div className="custom-prompt-overlay">
          <div className="custom-prompt-dialog">
            <div className="custom-prompt-header">
              {t.importConfirm.title}
            </div>
            <div className="custom-prompt-body">
              <p className="custom-prompt-message">
                {t.importConfirm.message(confirmSheetTitle)}
              </p>
            </div>
            <div className="custom-prompt-footer">
              <button 
                className="custom-prompt-button cancel"
                onClick={handleConfirmCancel}
                onTouchEnd={handleConfirmCancel}
              >
                {t.common.cancel}
              </button>
              <button 
                className="custom-prompt-button ok"
                onClick={handleConfirmOK}
                onTouchEnd={handleConfirmOK}
              >
                {t.common.import}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* カスタムエクスポート確認ダイアログ（PWA対応） */}
      {showExportConfirm && (
        <div className="custom-prompt-overlay">
          <div className="custom-prompt-dialog">
            <div className="custom-prompt-header">
              {t.export.title}
            </div>
            <div className="custom-prompt-body">
              <p className="custom-prompt-message">
                {t.export.message(exportSheetTitle)}
              </p>
            </div>
            <div className="custom-prompt-footer export-buttons">
              <button 
                className="custom-prompt-button private"
                onClick={(e) => handleExportAction('private', e)}
                onTouchEnd={(e) => handleExportAction('private', e)}
              >
                {t.export.privateShare}
              </button>
              <button 
                className="custom-prompt-button public"
                onClick={(e) => handleExportAction('public', e)}
                onTouchEnd={(e) => handleExportAction('public', e)}
              >
                {t.export.publicShare}
              </button>
              <button 
                className="custom-prompt-button download"
                onClick={(e) => handleExportAction('download', e)}
                onTouchEnd={(e) => handleExportAction('download', e)}
              >
                {t.export.download}
              </button>
              <button 
                className="custom-prompt-button cancel"
                onClick={(e) => { if ('touches' in e) e.preventDefault(); handleExportCancel(); }}
                onTouchEnd={(e) => { e.preventDefault(); handleExportCancel(); }}
              >
                {t.common.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* カスタムインポートプロンプト（PWA対応） */}
      {showImportPrompt && (
        <div className="custom-prompt-overlay">
          <div className="custom-prompt-dialog">
            <div className="custom-prompt-header">
              {t.importPrompt.title}
            </div>
            <div className="custom-prompt-body">
              <p className="custom-prompt-message">
                {t.importPrompt.message}
              </p>
              <input
                type="text"
                className="custom-prompt-input"
                value={importInputValue}
                onChange={(e) => setImportInputValue(e.target.value)}
                placeholder={t.importPrompt.placeholder}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleImportPromptOK();
                  } else if (e.key === 'Escape') {
                    handleImportPromptCancel();
                  }
                }}
                onTouchEnd={(e) => e.stopPropagation()}
              />
            </div>
            <div className="custom-prompt-footer">
              <button 
                className="custom-prompt-button cancel"
                onClick={handleImportPromptCancel}
                onTouchEnd={handleImportPromptCancel}
              >
                {t.common.cancel}
              </button>
              <button 
                className="custom-prompt-button ok"
                onClick={handleImportPromptOK}
                onTouchEnd={handleImportPromptOK}
              >
                {importInputValue.trim() === '' ? t.importPrompt.selectFile : t.importPrompt.fromUrl}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    <Tooltip id="tt" delayShow={0} delayHide={80} offset={8} className="app-tooltip" noArrow={true} />
    </>
    </LayerSwitchProvider>
    </PouchValueKeyboardProvider>
    </GuideContext.Provider>);
}

export default App; 
